'use client';

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Link from 'next/link';
import {
  Share2,
  UserPlus,
  UserCheck,
  Shield,
  Loader2,
  Copy,
  Check,
  Settings,
  Eye,
  MapPin,
  ChevronLeft,
  ChevronRight,
  Map as MapIcon,
  GitFork,
  Lock,
  Calendar,
  Clock,
  Globe,
  Star,
  Zap,
  BookOpen,
  Award,
  X as XIcon,
  Image as ImageIcon,
  Video,
  Mic,
  Play,
  Pause,
  ShieldCheck,
  ExternalLink,
  Music,
  Users,
  FileText,
  MessageCircle,
  Home,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useTranslation } from '@/i18n/useTranslation';
import { openNextdoorShare } from '@/lib/share/nextdoor';
import type {
  PublicProfilePayload,
  PublicProfileStats,
  PublicJourneyMoment,
  PublicFamilyItem,
  PublicFamilySummary,
  PublicStory,
  PublicPulsePost,
  PublicProofCard,
  PublicCircle,
  PublicSoundtrackItem,
  ProfilePlanTier,
  VerificationLevel,
} from '@/lib/public-profile/types';
import {
  isSoundtrackInlineExpandable,
  opensSoundtrackExternally,
  resolveSoundtrack,
  SOUNDTRACK_SOURCE_BADGE as ST_SOURCE_BADGE,
} from '@/lib/public-profile/soundtrack';
import PublicContentActions, { type PublicComment } from '@/components/public/PublicContentActions';
import {
  RAINBOW_GRADIENT,
  rainbowBorderBackground,
  SOFT_RAINBOW_GRADIENT,
  softRainbowBorderBackground,
} from '@/app/public-landing-page/components/ledger-rainbow';
import { consumePendingAction, loginUrlWithReturn } from '@/lib/auth/pending-action';
import ReachOutDialog from '@/components/messages/ReachOutDialog';
import { trackEvent } from '@/lib/analyticsEvents';
import QRShareModal from './QRShareModal';
import LivingRecordProfile from './LivingRecordProfile';
import { projectPublicPreviewPayload } from '@/lib/public-profile/public-preview';
import {
  mergePublicProfileSectionPayload,
  type PublicProfileSection,
} from '@/lib/public-profile/progressive-loading';

// ── Design tokens ──────────────────────────────────────────────────────────────

const T = {
  pageBg: 'linear-gradient(165deg, #f8fafc 0%, #eef6ff 38%, #fdf4ff 68%, #f0fdf9 100%)',
  cardBg: '#ffffff',
  cardBorder: 'rgba(0,0,0,0.07)',
  cardShadow: '0 1px 8px rgba(0,0,0,0.06), 0 4px 20px rgba(0,0,0,0.04)',
  cardHoverShadow: '0 4px 24px rgba(0,0,0,0.11), 0 1px 8px rgba(0,0,0,0.06)',
  text: '#0f1923',
  textMuted: '#5c6b7a',
  accent: '#1b6b5c',
  accentLight: 'rgba(27,107,92,0.1)',
  accentBorder: 'rgba(27,107,92,0.22)',
  profileCardBg: 'rgba(255,255,255,0.78)',
  tabActive: '#1b6b5c',
  tabInactive: '#8a9bb0',
};

function PrivateProfilePreview({ onExit }: { onExit: () => void }) {
  const { t } = useTranslation();

  return (
    <main
      style={{
        minHeight: '100vh',
        color: '#172028',
        background: 'linear-gradient(180deg, #f6f8f5 0, #fbfaf6 52%, #f4f7f4 100%)',
      }}
    >
      <header
        style={{
          minHeight: 48,
          padding: '8px max(16px, calc((100vw - 1120px) / 2))',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 16,
          borderBottom: '1px solid #dfe5e2',
          background: 'rgba(251, 252, 249, 0.9)',
        }}
      >
        <strong style={{ fontSize: 13 }}>{t('public_profile.view_as_public')}</strong>
        <button
          type="button"
          onClick={onExit}
          style={{
            minHeight: 36,
            padding: '0 14px',
            border: '1px solid #cdd8d3',
            borderRadius: 10,
            color: '#174f45',
            background: '#fff',
            fontWeight: 700,
            cursor: 'pointer',
          }}
        >
          {t('public_profile.owner_view')}
        </button>
      </header>
      <section
        style={{
          width: 'min(520px, calc(100% - 32px))',
          margin: '18vh auto 0',
          textAlign: 'center',
        }}
      >
        <Lock size={32} aria-hidden style={{ margin: '0 auto 18px', color: '#45645b' }} />
        <h1 style={{ margin: '0 0 10px', font: '400 30px/1.2 Georgia, serif' }}>
          {t('public_profile.private_profile_title')}
        </h1>
        <p style={{ margin: 0, color: '#65737f', lineHeight: 1.65 }}>
          {t('public_profile.private_profile_body')}
        </p>
      </section>
    </main>
  );
}

// Keep the previous presentation available as a fast rollback while the canonical
// public-profile loader, visibility boundary, and social actions remain unchanged.
const LIVING_RECORD_PROFILE_ENABLED = process.env.NEXT_PUBLIC_LIVING_RECORD_PROFILE_V2 !== 'false';

// ── Tier-aware visual constants ───────────────────────────────────────────────

const LEGACY_HERO_BORDER =
  'linear-gradient(135deg, rgba(233,168,76,0.6) 0%, rgba(217,119,6,0.5) 50%, rgba(233,168,76,0.55) 100%)';

const VERIFIED_HERO_BORDER =
  'linear-gradient(135deg, rgba(13,148,136,0.4) 0%, rgba(96,165,250,0.35) 50%, rgba(13,148,136,0.3) 100%)';

const FREE_HERO_BORDER = '#e2e8f0';

function heroBorderForTier(tier: ProfilePlanTier): string {
  if (tier === 'legacy') return LEGACY_HERO_BORDER;
  if (tier === 'verified') return VERIFIED_HERO_BORDER;
  return FREE_HERO_BORDER;
}

// For Legacy only: premium box shadow on the hero card
function heroShadowForTier(tier: ProfilePlanTier): string {
  if (tier === 'legacy')
    return '0 28px 70px rgba(233,168,76,0.12), 0 10px 36px rgba(217,119,6,0.07), 0 2px 8px rgba(15,23,42,0.04)';
  if (tier === 'verified') return '0 8px 32px rgba(13,148,136,0.1), 0 2px 8px rgba(15,23,42,0.04)';
  return '0 2px 16px rgba(15,23,42,0.06)';
}

const HERO_STAT_ACCENTS = [
  '#22d3ee',
  '#a78bfa',
  '#fb7185',
  '#34d399',
  '#8b5cf6',
  '#fcd34d',
] as const;

const HERO_AURORA_BORDER =
  'conic-gradient(from 135deg, rgba(45,212,191,.78), rgba(132,204,22,.55), rgba(250,204,21,.62), rgba(244,114,182,.58), rgba(139,92,246,.66), rgba(56,189,248,.70), rgba(45,212,191,.78))';

const HERO_GLASS = {
  shell:
    'linear-gradient(165deg, rgba(246,250,255,0.93) 0%, rgba(244,251,250,0.9) 55%, rgba(246,244,255,0.88) 100%)',
  panel: 'rgba(255,255,255,0.68)',
  border: 'rgba(255,255,255,0.92)',
  borderSoft: 'rgba(148,163,184,0.22)',
  blur: 'blur(18px) saturate(1.55)',
} as const;

const PILLAR_TOKENS: Record<string, { bg: string; text: string; icon: string }> = {
  History: { bg: '#faeeda', text: '#854f0b', icon: '#ef9f27' },
  Achievement: { bg: '#eaf3de', text: '#3b6d11', icon: '#97c459' },
  Performance: { bg: '#e6f1fb', text: '#185fa5', icon: '#85b7eb' },
  Hero: { bg: '#fcebeb', text: '#a32d2d', icon: '#f09595' },
  Grit: { bg: '#eeedfe', text: '#534ab7', icon: '#afa9ec' },
  Mischief: { bg: '#eeedfe', text: '#534ab7', icon: '#afa9ec' },
  Favorites: { bg: '#fbeaf0', text: '#993556', icon: '#ed93b1' },
  Products: { bg: '#fbeaf0', text: '#993556', icon: '#ed93b1' },
  News: { bg: '#e1f5ee', text: '#0f6e56', icon: '#5dcaa5' },
};

function pillarTokens(tag?: string | null) {
  if (!tag) return { bg: '#f1f5f9', text: '#475569', icon: '#94a3b8' };
  for (const [k, v] of Object.entries(PILLAR_TOKENS)) {
    if (tag.toLowerCase().includes(k.toLowerCase())) return v;
  }
  return { bg: '#f1f5f9', text: '#475569', icon: '#94a3b8' };
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function initials(name: string): string {
  return name
    .split(/\s+/)
    .map((w) => w[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: 'short', year: 'numeric' });
  } catch {
    return '';
  }
}

function shortDate(iso: string | null | undefined): string {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return '';
  }
}

function isPhotoMedia(s: PublicStory): boolean {
  const url = (s.media_url || '').toLowerCase();
  if (!url) return false;
  return (
    /\.(jpe?g|png|gif|webp|avif|heic)($|\?)/.test(url) ||
    s.content_type === 'photo' ||
    url.includes('/photo') ||
    url.includes('/image')
  );
}

function isVideoMedia(s: PublicStory): boolean {
  const url = (s.media_url || '').toLowerCase();
  if (!url) return false;
  return (
    /\.(mp4|mov|webm|avi|mkv)($|\?)/.test(url) ||
    s.content_type === 'video' ||
    url.includes('/video')
  );
}

function isAudioMedia(s: PublicStory): boolean {
  const url = (s.media_url || '').toLowerCase();
  if (!url) return false;
  return (
    /\.(mp3|wav|ogg|m4a|aac|flac)($|\?)/.test(url) ||
    s.content_type === 'audio' ||
    url.includes('/audio')
  );
}

type TabId =
  | 'overview'
  | 'stories'
  | 'photos'
  | 'videos'
  | 'oral_stories'
  | 'milestones'
  | 'journey'
  | 'heritage'
  | 'circles'
  | 'life_cards'
  | 'verified'
  | 'pulse'
  | 'about';
interface TabDef {
  id: TabId;
  label: string;
  count?: number;
}
interface Props {
  handle: string;
  initialData?: PublicProfilePayload | null;
}

const FALLBACK_VISIBILITY: PublicProfilePayload['visibility'] = {
  profile_enabled: true,
  show_erv_id: false,
  show_followers: false,
  show_following: false,
  show_location: false,
  show_member_since: false,
  show_stories: false,
  show_records: false,
  show_proof_cards: false,
  show_journey_preview: false,
  journey_map_visibility: 'private',
  show_family_preview: false,
  show_public_circles: false,
  allow_comments: false,
  allow_witness_requests: false,
  allow_circle_requests: false,
  featured_story_id: null,
  featured_record_id: null,
  allow_search_indexing: false,
  allow_vault_on_profile: false,
};

const FALLBACK_STATS: PublicProfileStats = {
  publicRecords: 0,
  publicStories: 0,
  publicPulsePosts: 0,
  followers: 0,
  following: 0,
  verifiedRecords: 0,
  publicProofCards: 0,
  publicCircles: 0,
};

const FALLBACK_FAMILY_SUMMARY: PublicFamilySummary = {
  public_family_members_count: 0,
  public_family_records_count: 0,
  public_heritage_items_count: 0,
  public_generations_count: null,
  preview_items: [],
};

// Per-section error boundary — catches synchronous render crashes in optional
// profile sections and hides that section without collapsing the whole profile.
class SectionErrorBoundary extends React.Component<
  { children: React.ReactNode; section: string },
  { failed: boolean }
> {
  state = { failed: false };
  static getDerivedStateFromError(): { failed: boolean } {
    return { failed: true };
  }
  componentDidCatch(error: Error) {
    console.warn('[PublicProfile] public-profile:section-render-failed', {
      section: this.props.section,
      message: error.message,
    });
  }
  render() {
    if (this.state.failed) return null;
    return this.props.children;
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value && typeof value === 'object' && !Array.isArray(value));
}

function recordKeys(value: unknown): string[] {
  return isRecord(value) ? Object.keys(value) : [];
}

function asArray<T>(value: unknown): T[] {
  return Array.isArray(value) ? (value as T[]) : [];
}

function nullableString(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value : null;
}

function safeString(value: unknown, fallback = ''): string {
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return fallback;
}

function safeNumber(value: unknown, fallback = 0): number {
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function safeBool(value: unknown, fallback = false): boolean {
  return typeof value === 'boolean' ? value : fallback;
}

function normalizeStats(source: Record<string, unknown>): PublicProfileStats {
  return {
    publicRecords: safeNumber(source.publicRecords),
    publicStories: safeNumber(source.publicStories),
    publicPulsePosts: safeNumber(source.publicPulsePosts),
    followers: safeNumber(source.followers),
    following: safeNumber(source.following),
    verifiedRecords: safeNumber(source.verifiedRecords),
    publicProofCards: safeNumber(source.publicProofCards),
    publicCircles: safeNumber(source.publicCircles),
    publicJourneyMoments:
      source.publicJourneyMoments == null ? undefined : safeNumber(source.publicJourneyMoments),
  };
}

function normalizeStory(value: unknown): PublicStory | null {
  if (!isRecord(value) || !value.id) return null;
  return {
    id: safeString(value.id),
    title: nullableString(value.title),
    body: nullableString(value.body) || '',
    pillar_tag: nullableString(value.pillar_tag),
    media_url: nullableString(value.media_url),
    milestone_date: nullableString(value.milestone_date),
    created_at: nullableString(value.created_at) || new Date(0).toISOString(),
    witness_count: safeNumber(value.witness_count),
    content_type: nullableString(value.content_type) || 'written',
  };
}

function normalizePulse(value: unknown): PublicPulsePost | null {
  if (!isRecord(value) || !value.id) return null;
  return {
    id: safeString(value.id),
    body: nullableString(value.body) || '',
    pillar_tag: nullableString(value.pillar_tag),
    media_url: nullableString(value.media_url),
    created_at: nullableString(value.created_at) || new Date(0).toISOString(),
    witness_count: safeNumber(value.witness_count),
    pulse_type: nullableString(value.pulse_type),
  };
}

function normalizeRecord(value: unknown): PublicProfilePayload['records'][number] | null {
  if (!isRecord(value) || !value.id) return null;
  return {
    id: safeString(value.id),
    title: safeString(value.title, 'Untitled record'),
    pillar: nullableString(value.pillar),
    institution: nullableString(value.institution),
    status: nullableString(value.status),
    achievement_date: nullableString(value.achievement_date),
    description: nullableString(value.description),
    is_public: safeBool(value.is_public, true),
    source_achievement_id: safeString(value.source_achievement_id || value.id),
    evidence_state: value.evidence_state === 'added' ? 'added' : 'none',
    witness_state:
      value.witness_state === 'witnessed'
        ? 'witnessed'
        : value.witness_state === 'requested'
          ? 'requested'
          : 'none',
    verification_state:
      value.verification_state === 'institution_issued'
        ? 'institution_issued'
        : value.verification_state === 'institution_reviewed'
          ? 'institution_reviewed'
          : 'personal_record',
    media: normalizedArray(value.media, (media) => {
      if (!isRecord(media) || !media.id || !media.file_url) return null;
      return {
        id: safeString(media.id),
        media_type: safeString(media.media_type, 'document'),
        file_url: safeString(media.file_url),
        file_name: nullableString(media.file_name),
        caption: nullableString(media.caption),
      };
    }),
    location: isRecord(value.location)
      ? {
          name: nullableString(value.location.name),
          latitude: typeof value.location.latitude === 'number' ? value.location.latitude : null,
          longitude: typeof value.location.longitude === 'number' ? value.location.longitude : null,
        }
      : null,
  };
}

function normalizeProofCard(value: unknown): PublicProofCard | null {
  if (!isRecord(value) || !value.id) return null;
  return {
    id: safeString(value.id),
    title: safeString(value.title, 'Verified record'),
    issuer_name: safeString(value.issuer_name, 'Ertzyx'),
    verification_ref: nullableString(value.verification_ref),
    trust_level: safeString(value.trust_level, 'self_documented'),
    thumbnail_url: nullableString(value.thumbnail_url),
    issue_date: nullableString(value.issue_date),
  };
}

function normalizeJourneyMoment(value: unknown): PublicJourneyMoment | null {
  if (!isRecord(value) || !value.id) return null;
  return {
    id: safeString(value.id),
    title: safeString(value.title, 'Journey moment'),
    date: nullableString(value.date) || nullableString(value.created_at) || '',
    location_name: nullableString(value.location_name),
    latitude: typeof value.latitude === 'number' ? value.latitude : null,
    longitude: typeof value.longitude === 'number' ? value.longitude : null,
    summary: nullableString(value.summary),
    thumbnail_url: nullableString(value.thumbnail_url),
    source_type: safeString(value.source_type, 'manual_journey'),
    source_id: nullableString(value.source_id),
  };
}

function normalizeFamilyItem(value: unknown): PublicFamilyItem | null {
  if (!isRecord(value) || !value.id) return null;
  const kind = ['member', 'record', 'heritage'].includes(String(value.kind))
    ? (value.kind as PublicFamilyItem['kind'])
    : 'heritage';
  return {
    id: safeString(value.id),
    kind,
    title: nullableString(value.title) || 'Family highlight',
    description: nullableString(value.description),
    collection_title: nullableString(value.collection_title),
    created_at: nullableString(value.created_at) || '',
    timeline_date: nullableString(value.timeline_date),
    label_key: nullableString(value.label_key),
    relationship_label: nullableString(value.relationship_label),
    subtitle: nullableString(value.subtitle),
    is_living: typeof value.is_living === 'boolean' ? value.is_living : undefined,
    thumbnail_url: nullableString(value.thumbnail_url),
  };
}

function normalizeCircle(value: unknown): PublicCircle | null {
  if (!isRecord(value) || !value.id) return null;
  const atelier = isRecord(value.atelier_object) ? value.atelier_object : null;
  return {
    id: safeString(value.id),
    name: safeString(value.name, 'Circle'),
    description: nullableString(value.description),
    privacy: safeString(value.privacy, 'Public Proof'),
    circle_type: nullableString(value.circle_type),
    visibility_label: safeString(value.visibility_label, 'Profile Visible'),
    trust_status: safeString(value.trust_status, 'Trusted Circle'),
    member_count: safeNumber(value.member_count),
    archive_count: safeNumber(value.archive_count),
    witness_count: safeNumber(value.witness_count),
    featured_public_item_title: nullableString(value.featured_public_item_title),
    public_media_url: nullableString(value.public_media_url),
    public_theme: nullableString(value.public_theme),
    public_icon: nullableString(value.public_icon),
    atelier_object: atelier
      ? {
          id: safeString(atelier.id),
          name: safeString(atelier.name, 'Atelier object'),
          category: safeString(atelier.category, 'Table Objects'),
          preview: safeString(atelier.preview, ''),
          accent: safeString(atelier.accent, '#14b8a6'),
        }
      : null,
    owner_relationship: value.owner_relationship === 'joined' ? 'joined' : 'created',
    allow_join_request: safeBool(value.allow_join_request),
    is_discoverable: safeBool(value.is_discoverable),
    is_featured: safeBool(value.is_featured),
  };
}

function normalizedArray<T>(value: unknown, normalize: (item: unknown) => T | null): T[] {
  return asArray<unknown>(value)
    .map(normalize)
    .filter((item): item is T => Boolean(item));
}

function normalizePublicProfilePayload(
  raw: unknown,
  fallbackHandle: string
): PublicProfilePayload | null {
  if (!isRecord(raw)) return null;

  const nested = isRecord(raw.data) ? raw.data : null;
  const source = nested && (nested.profile || nested.user || nested.publicProfile) ? nested : raw;
  const profileSource =
    (isRecord(source.profile) && source.profile) ||
    (isRecord(source.publicProfile) && source.publicProfile) ||
    (isRecord(source.user) && source.user) ||
    null;

  if (!profileSource?.id) return null;

  const statsSource = isRecord(source.stats) ? source.stats : {};
  const visibilitySource = isRecord(source.visibility) ? source.visibility : {};
  const familySummarySource = isRecord(source.familySummary) ? source.familySummary : {};
  const familySource = isRecord(source.family) ? source.family : {};
  const payloadHandle =
    nullableString(source.handle) ||
    nullableString(profileSource.public_handle) ||
    nullableString(profileSource.vault_id) ||
    fallbackHandle;

  const planTier = ['starter', 'verified', 'legacy'].includes(String(source.planTier))
    ? (source.planTier as ProfilePlanTier)
    : 'starter';
  const verificationLevel = [
    'unverified',
    'self_verified',
    'witnessed',
    'institution_verified',
    'legacy_verified',
  ].includes(String(source.verificationLevel))
    ? (source.verificationLevel as VerificationLevel)
    : 'unverified';

  return {
    profile: {
      id: String(profileSource.id),
      full_name:
        nullableString(profileSource.full_name) ||
        nullableString(profileSource.institution_name) ||
        payloadHandle,
      vault_id: nullableString(profileSource.vault_id) || '',
      public_handle: nullableString(profileSource.public_handle),
      bio: nullableString(profileSource.bio),
      avatar_url: nullableString(profileSource.avatar_url),
      banner_url: nullableString(profileSource.banner_url),
      location: nullableString(profileSource.location),
      created_at: nullableString(profileSource.created_at) || '',
      role: nullableString(profileSource.role) || 'individual',
      erv_id: nullableString(profileSource.erv_id),
    },
    handle: payloadHandle,
    visibility: {
      ...FALLBACK_VISIBILITY,
      ...visibilitySource,
    } as PublicProfilePayload['visibility'],
    stats: normalizeStats({ ...FALLBACK_STATS, ...statsSource }),
    stories: normalizedArray<PublicStory>(source.stories, normalizeStory),
    pulseHighlights: normalizedArray<PublicPulsePost>(source.pulseHighlights, normalizePulse),
    records: normalizedArray<PublicProfilePayload['records'][number]>(
      source.records,
      normalizeRecord
    ),
    proofCards: normalizedArray<PublicProofCard>(source.proofCards, normalizeProofCard),
    journeyMoments: normalizedArray<PublicJourneyMoment>(
      source.journeyMoments,
      normalizeJourneyMoment
    ),
    familyItems: normalizedArray<PublicFamilyItem>(
      source.familyItems || familySource.items,
      normalizeFamilyItem
    ),
    familySummary: {
      ...FALLBACK_FAMILY_SUMMARY,
      ...familySummarySource,
      public_family_members_count: safeNumber(
        familySummarySource.public_family_members_count,
        FALLBACK_FAMILY_SUMMARY.public_family_members_count
      ),
      public_family_records_count: safeNumber(
        familySummarySource.public_family_records_count,
        FALLBACK_FAMILY_SUMMARY.public_family_records_count
      ),
      public_heritage_items_count: safeNumber(
        familySummarySource.public_heritage_items_count,
        FALLBACK_FAMILY_SUMMARY.public_heritage_items_count ?? 0
      ),
      public_generations_count:
        familySummarySource.public_generations_count == null
          ? null
          : safeNumber(familySummarySource.public_generations_count),
      preview_items: normalizedArray<PublicFamilyItem>(
        familySummarySource.preview_items || source.familyItems || familySource.items,
        normalizeFamilyItem
      ),
    },
    circles: normalizedArray<PublicCircle>(source.circles, normalizeCircle),
    featuredStory: normalizeStory(source.featuredStory),
    featuredRecord: normalizeRecord(source.featuredRecord),
    isOwner: source.isOwner === true,
    isFollowing: source.isFollowing === true,
    viewerId: nullableString(source.viewerId),
    soundtrack: asArray(source.soundtrack),
    soundtrackState: ['public', 'private', 'empty'].includes(String(source.soundtrackState))
      ? (source.soundtrackState as PublicProfilePayload['soundtrackState'])
      : asArray(source.soundtrack).length > 0
        ? 'public'
        : 'empty',
    planTier,
    verificationLevel,
  };
}

// ── Main component ────────────────────────────────────────────────────────────

export default function PublicProfileExperience({ handle, initialData = null }: Props) {
  const { user, loading: authLoading } = useAuth();
  const [data, setData] = useState<PublicProfilePayload | null>(initialData);
  const [loading, setLoading] = useState(!initialData?.profile?.id);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<TabId>('overview');
  const [isFollowing, setIsFollowing] = useState(false);
  const [followLoading, setFollowLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [viewAsPublic, setViewAsPublic] = useState(false);
  const [reachOutOpen, setReachOutOpen] = useState(false);
  const [circleRequestStatus, setCircleRequestStatus] = useState<
    Record<string, 'idle' | 'loading' | 'pending' | 'member' | 'error'>
  >({});
  const [soundtrackItems, setSoundtrackItems] = useState<PublicSoundtrackItem[]>(
    initialData?.soundtrack || []
  );
  const [loadingSection, setLoadingSection] = useState<PublicProfileSection | null>(null);
  // Only one inline soundtrack player can be active at a time.
  const [activeTrackId, setActiveTrackId] = useState<string | null>(null);
  const tabBarRef = useRef<HTMLDivElement>(null);
  const tabShellRef = useRef<HTMLDivElement>(null);
  const [tabFade, setTabFade] = useState({ left: false, right: false });
  const hasRenderableProfileRef = useRef(Boolean(initialData?.profile?.id));
  const loadedSectionsRef = useRef<Set<PublicProfileSection>>(new Set());
  const inFlightSectionsRef = useRef<Set<PublicProfileSection>>(new Set());

  const scrollTabsBy = useCallback((delta: number) => {
    tabBarRef.current?.scrollBy({ left: delta, behavior: 'smooth' });
  }, []);

  const profileUrl =
    typeof window !== 'undefined'
      ? `${window.location.origin}/u/${data?.handle || handle}`
      : `/u/${handle}`;

  // Inline expand: direct audio, compact streaming embeds, and compact YouTube preview.
  const handleSoundtrackAction = useCallback((item: PublicSoundtrackItem) => {
    const { type, url } = resolveSoundtrack(item);
    if (opensSoundtrackExternally(type)) {
      if (url) window.open(url, '_blank', 'noopener,noreferrer');
      return;
    }
    if (isSoundtrackInlineExpandable(type)) {
      const trackId = item.id || url;
      if (trackId) setActiveTrackId((prev) => (prev === trackId ? null : trackId));
    }
  }, []);

  const fetchProfile = useCallback(async () => {
    setLoading(!hasRenderableProfileRef.current);
    setError(null);
    try {
      const res = await fetch(
        `/api/public/profile?handle=${encodeURIComponent(handle)}&section=overview`,
        { cache: 'no-store' }
      );
      console.info('[PublicProfile] public-profile:api-status', {
        routeHandle: handle,
        status: res.status,
        ok: res.ok,
      });
      const json = await res.json().catch(() => ({}));
      if (json.redirectTo && typeof json.redirectTo === 'string') {
        window.location.replace(json.redirectTo);
        return;
      }
      if (!res.ok) {
        console.warn('[PublicProfile] public-profile:frontend-missing-base-profile', {
          routeHandle: handle,
          apiStatus: res.status,
          resolvedProfileExists: false,
          renderFallbackReason: json.error || 'api_not_ok',
          responseKeys: recordKeys(json),
        });
        if (!hasRenderableProfileRef.current) {
          setError(json.error || 'Profile not found');
          setData(null);
        }
        return;
      }
      const payload = normalizePublicProfilePayload(json, handle);
      if (!payload) {
        console.warn('[PublicProfile] public-profile:frontend-missing-base-profile', {
          routeHandle: handle,
          apiStatus: res.status,
          resolvedProfileExists: false,
          renderFallbackReason: 'missing_normalized_base_profile',
          responseKeys: recordKeys(json),
        });
        if (!hasRenderableProfileRef.current) {
          setError('Profile not found');
          setData(null);
        }
        return;
      }
      console.info('[PublicProfile] public-profile:frontend-payload-valid', {
        routeHandle: handle,
        apiStatus: res.status,
        resolvedProfileExists: Boolean(payload.profile?.id),
        responseKeys: recordKeys(json),
        optionalCounts: {
          stories: payload.stories.length,
          pulseHighlights: payload.pulseHighlights.length,
          records: payload.records.length,
          proofCards: payload.proofCards.length,
          journeyMoments: payload.journeyMoments.length,
          familyItems: payload.familyItems.length,
          circles: payload.circles.length,
          soundtrack: payload.soundtrack?.length ?? 0,
        },
      });
      setData((current) =>
        current
          ? mergePublicProfileSectionPayload(
              current,
              payload,
              'overview',
              loadedSectionsRef.current
            )
          : payload
      );
      loadedSectionsRef.current.add('overview');
      hasRenderableProfileRef.current = true;
      setIsFollowing(Boolean(payload.isFollowing));
      setSoundtrackItems(payload.soundtrack || []);
    } catch (err) {
      console.warn('[PublicProfile] public-profile:frontend-missing-base-profile', {
        routeHandle: handle,
        resolvedProfileExists: false,
        renderFallbackReason: 'fetch_or_render_preparation_exception',
        message: err instanceof Error ? err.message : 'Unable to load profile',
      });
      if (!hasRenderableProfileRef.current) setError('Unable to load profile');
    } finally {
      setLoading(false);
    }
  }, [handle]);

  const requestProfileSection = useCallback(
    (section: PublicProfileSection) => {
      if (section === 'overview' || loadedSectionsRef.current.has(section)) {
        return;
      }
      if (inFlightSectionsRef.current.has(section)) {
        setLoadingSection(section);
        return;
      }

      inFlightSectionsRef.current.add(section);
      setLoadingSection(section);

      void (async () => {
        try {
          const response = await fetch(
            `/api/public/profile?handle=${encodeURIComponent(handle)}&section=${section}`,
            { cache: 'no-store' }
          );
          const json = await response.json().catch(() => ({}));
          if (json.redirectTo && typeof json.redirectTo === 'string') {
            window.location.replace(json.redirectTo);
            return;
          }
          if (!response.ok) {
            throw new Error(json.error || `Unable to load ${section}`);
          }

          const payload = normalizePublicProfilePayload(json, handle);
          if (!payload) throw new Error(`Invalid ${section} payload`);

          setData((current) =>
            current
              ? mergePublicProfileSectionPayload(
                  current,
                  payload,
                  section,
                  loadedSectionsRef.current
                )
              : payload
          );
          loadedSectionsRef.current.add(section);
        } catch (sectionError) {
          console.warn('[PublicProfile] public-profile:section-load-failed', {
            routeHandle: handle,
            section,
            message:
              sectionError instanceof Error ? sectionError.message : 'Unable to load section',
          });
        } finally {
          inFlightSectionsRef.current.delete(section);
          setLoadingSection((current) => (current === section ? null : current));
        }
      })();
    },
    [handle]
  );

  useEffect(() => {
    loadedSectionsRef.current.clear();
    inFlightSectionsRef.current.clear();
    setLoadingSection(null);
  }, [handle]);

  useEffect(() => {
    if (!authLoading) fetchProfile();
  }, [authLoading, fetchProfile]);

  const runPendingAction = useCallback(async () => {
    const pending = consumePendingAction();
    if (!pending || !user || !data) return;
    if (pending.type === 'follow' && pending.targetHandle === data.handle) {
      setFollowLoading(true);
      try {
        const response = await fetch('/api/community/follow', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ target_handle: data.handle, action: 'follow' }),
        });
        if (response.ok) {
          setIsFollowing(true);
          fetchProfile();
        }
      } finally {
        setFollowLoading(false);
      }
    }
    if (pending.type === 'circle_request' && pending.circleId) {
      await fetch(`/api/circles/${encodeURIComponent(pending.circleId)}/join-request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      setCircleRequestStatus((prev) => ({ ...prev, [pending.circleId!]: 'pending' }));
    }
    if (pending.type === 'reach_out' && pending.targetHandle === data.handle) {
      setReachOutOpen(true);
    }
  }, [user, data, fetchProfile]);

  useEffect(() => {
    if (user && data) runPendingAction();
  }, [user, data, runPendingAction]);

  const isOwner = data?.isOwner && !viewAsPublic;

  const tabs = useMemo<TabDef[]>(() => {
    if (!data) return [];
    const stories = data.stories || [];
    const journeyMoments = data.journeyMoments || [];
    const familyItems = data.familyItems || [];
    const circles = data.circles || [];
    const records = data.records || [];
    const proofCards = data.proofCards || [];
    const pulseHighlights = data.pulseHighlights || [];
    const photos = stories.filter(isPhotoMedia);
    const videos = stories.filter(isVideoMedia);
    const oral = stories.filter(isAudioMedia);
    const journeyVisible =
      isOwner ||
      data.visibility.show_journey_preview ||
      ['preview', 'full'].includes(data.visibility.journey_map_visibility);
    const storiesVisible = isOwner || data.visibility.show_stories;
    const recordsVisible = isOwner || data.visibility.show_records;
    const familyVisible = isOwner || data.visibility.show_family_preview;
    const circlesVisible = isOwner || data.visibility.show_public_circles;
    const proofVisible = isOwner || data.visibility.show_proof_cards;

    const orderedTabs: Array<TabDef | null> = [
      { id: 'overview', label: 'Life at a glance' },
      isOwner ? { id: 'life_cards' as const, label: 'Chapters' } : null,
      storiesVisible && stories.length > 0
        ? { id: 'stories' as const, label: 'Stories', count: stories.length }
        : null,
      familyVisible && familyItems.length > 0
        ? { id: 'heritage' as const, label: 'People & belonging', count: familyItems.length }
        : null,
      circlesVisible && circles.length > 0
        ? { id: 'circles' as const, label: 'Shared circles', count: circles.length }
        : null,
      journeyVisible && journeyMoments.length > 0
        ? {
            id: 'journey' as const,
            label: 'Places that shaped me',
            count: journeyMoments.length,
          }
        : null,
      (journeyVisible || recordsVisible) && (journeyMoments.length > 0 || records.length > 0)
        ? {
            id: 'milestones' as const,
            label: 'Milestones & recognition',
            count: journeyMoments.length || records.length,
          }
        : null,
      storiesVisible && photos.length > 0
        ? { id: 'photos' as const, label: 'Preserved photos', count: photos.length }
        : null,
      storiesVisible && videos.length > 0
        ? { id: 'videos' as const, label: 'Preserved video', count: videos.length }
        : null,
      storiesVisible && oral.length > 0
        ? { id: 'oral_stories' as const, label: 'Preserved voices', count: oral.length }
        : null,
      proofVisible && proofCards.length > 0
        ? { id: 'verified' as const, label: 'Public trust records', count: proofCards.length }
        : null,
      storiesVisible && pulseHighlights.length > 0
        ? { id: 'pulse' as const, label: 'Public Pulse', count: pulseHighlights.length }
        : null,
      { id: 'about', label: 'About' },
    ];
    return orderedTabs.filter((tab): tab is TabDef => tab !== null);
  }, [data, isOwner]);

  useEffect(() => {
    if (tabs.length > 0 && !tabs.some((tab) => tab.id === activeTab)) {
      setActiveTab('overview');
    }
  }, [activeTab, tabs]);

  const switchTab = useCallback((id: TabId) => {
    setActiveTab(id);
    setTimeout(
      () => tabShellRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }),
      50
    );
  }, []);

  // Scroll the active tab button into view within the horizontal rail (not the page).
  useEffect(() => {
    const rail = tabBarRef.current;
    if (!rail) return;
    const btn = rail.querySelector<HTMLButtonElement>(`[data-public-tab="${activeTab}"]`);
    if (!btn) return;
    const railLeft = rail.scrollLeft;
    const railWidth = rail.offsetWidth;
    const btnLeft = btn.offsetLeft;
    const btnWidth = btn.offsetWidth;
    if (btnLeft < railLeft + 24) {
      rail.scrollTo({ left: Math.max(0, btnLeft - 24), behavior: 'smooth' });
    } else if (btnLeft + btnWidth > railLeft + railWidth - 24) {
      rail.scrollTo({ left: btnLeft + btnWidth - railWidth + 24, behavior: 'smooth' });
    }
  }, [activeTab]);

  // Track rail scroll position so fade indicators and arrow visibility update.
  useEffect(() => {
    const rail = tabBarRef.current;
    if (!rail) return;
    const checkFade = () => {
      const scrollLeft = rail.scrollLeft;
      const maxScroll = rail.scrollWidth - rail.clientWidth;
      setTabFade({
        left: scrollLeft > 4,
        right: maxScroll > 4 && scrollLeft < maxScroll - 4,
      });
    };
    checkFade();
    rail.addEventListener('scroll', checkFade, { passive: true });
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(checkFade) : null;
    if (ro) ro.observe(rail);
    return () => {
      rail.removeEventListener('scroll', checkFade);
      if (ro) ro.disconnect();
    };
  }, [tabs]);

  const handleFollow = async () => {
    if (!data) return;
    if (!user) {
      const returnTo = `/u/${handle}`;
      window.location.href = loginUrlWithReturn(returnTo, {
        type: 'follow',
        targetHandle: data.handle,
        returnTo,
      });
      return;
    }
    if (user.id === data.profile.id) return;
    setFollowLoading(true);
    const prev = isFollowing;
    setIsFollowing(!prev);
    try {
      const response = await fetch('/api/community/follow', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          target_handle: data.handle,
          action: prev ? 'unfollow' : 'follow',
        }),
      });
      if (!response.ok) throw new Error('follow_failed');
      fetchProfile();
    } catch {
      setIsFollowing(prev);
    } finally {
      setFollowLoading(false);
    }
  };

  const handleReachOut = () => {
    if (!data || data.isOwner) return;
    trackEvent('profile_reach_out_started');
    if (!user) {
      const returnTo = `/u/${handle}`;
      window.location.href = loginUrlWithReturn(returnTo, {
        type: 'reach_out',
        targetHandle: data.handle,
        returnTo,
      });
      return;
    }
    setReachOutOpen(true);
  };

  const handleCircleRequest = async (circle: PublicCircle) => {
    if (!circle.allow_join_request) return;
    const returnTo = `/u/${handle}`;
    if (!user) {
      window.location.href = loginUrlWithReturn(returnTo, {
        type: 'circle_request',
        circleId: circle.id,
        returnTo,
      });
      return;
    }
    setCircleRequestStatus((prev) => ({ ...prev, [circle.id]: 'loading' }));
    try {
      const res = await fetch(`/api/circles/${encodeURIComponent(circle.id)}/join-request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      const json = (await res.json().catch(() => ({}))) as {
        status?: 'pending' | 'member';
        error?: string;
      };
      if (!res.ok) throw new Error(json.error || 'Could not send request');
      setCircleRequestStatus((prev) => ({ ...prev, [circle.id]: json.status || 'pending' }));
      fetchProfile();
    } catch {
      setCircleRequestStatus((prev) => ({ ...prev, [circle.id]: 'error' }));
    }
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: `${data?.profile.full_name} on Ertzyx`,
          text: 'View my public Living Ledger on Ertzyx',
          url: profileUrl,
        });
        trackEvent('profile_link_copied');
        return;
      } catch {
        /* fall through */
      }
    }
    await navigator.clipboard.writeText(profileUrl);
    trackEvent('profile_link_copied');
    setCopied(true);
    setTimeout(() => setCopied(false), 2200);
  };

  const handleNextdoorShare = () => {
    openNextdoorShare(
      { entityType: 'profile', title: data?.profile.full_name || 'Ertzyx', publicUrl: profileUrl },
      (result) => {
        if (result === 'opened') return;
        setCopied(true);
        setTimeout(() => setCopied(false), 2200);
      }
    );
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: T.pageBg }}>
        <ProfileSkeleton />
      </div>
    );
  }

  if (error || !data?.profile?.id) {
    const isPrivate = /not public|private/i.test(error || '');
    console.warn('[PublicProfile] public-profile:render-fallback', {
      routeHandle: handle,
      resolvedProfileExists: Boolean(data?.profile?.id),
      renderFallbackReason: error || 'missing_profile_id',
    });
    const title = isPrivate ? 'This profile is private' : 'Profile not found';
    const message = isPrivate
      ? `The owner of @${handle} hasn't made this profile public yet.`
      : `We couldn't find a public profile for @${handle}. The link may be incorrect, or this person hasn't published their profile.`;
    const Icon = isPrivate ? Lock : Globe;
    return (
      <div
        style={{
          minHeight: '100vh',
          background: T.pageBg,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 24,
        }}
      >
        <div
          style={{
            textAlign: 'center',
            maxWidth: 420,
            width: '100%',
            background: T.cardBg,
            border: `1px solid ${T.cardBorder}`,
            boxShadow: T.cardShadow,
            borderRadius: 20,
            padding: '40px 32px',
          }}
        >
          <div
            style={{
              width: 60,
              height: 60,
              borderRadius: 16,
              background: isPrivate ? 'rgba(245,158,11,0.12)' : 'rgba(15,23,42,0.05)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 18px',
            }}
          >
            <Icon size={26} color={isPrivate ? '#d97706' : T.textMuted} />
          </div>
          <h1
            style={{
              color: T.text,
              fontSize: 22,
              fontWeight: 700,
              margin: '0 0 10px',
              fontFamily: 'DM Serif Display, serif',
            }}
          >
            {title}
          </h1>
          <p style={{ color: T.textMuted, marginBottom: 24, fontSize: 14, lineHeight: 1.6 }}>
            {message}
          </p>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Link
              href="/public-timeline"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                background: T.accent,
                color: '#fff',
                fontWeight: 700,
                textDecoration: 'none',
                fontSize: 14,
                padding: '10px 18px',
                borderRadius: 10,
              }}
            >
              <Globe size={15} /> Explore public records
            </Link>
            <Link
              href="/"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                border: `1px solid ${T.cardBorder}`,
                color: T.text,
                fontWeight: 600,
                textDecoration: 'none',
                fontSize: 14,
                padding: '10px 18px',
                borderRadius: 10,
              }}
            >
              Go to Ertzyx
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const p = data.profile;
  const displayHandle = data.handle || handle;
  const stats = data.stats;
  const planTier: ProfilePlanTier = data.planTier || 'starter';
  const verificationLevel: VerificationLevel = data.verificationLevel || 'unverified';
  const isVerified =
    verificationLevel === 'institution_verified' ||
    verificationLevel === 'legacy_verified' ||
    verificationLevel === 'witnessed';
  const isLegacy = planTier === 'legacy';
  const ownerSoundtrackPrompt =
    !isOwner || soundtrackItems.length > 0
      ? null
      : planTier === 'starter'
        ? {
            title: 'Upgrade to add Life Soundtrack',
            body: 'Life Soundtrack is available on Verified and Legacy plans.',
            href: '/individual-dashboard/billing',
            cta: 'Upgrade plan',
            locked: true,
          }
        : data.soundtrackState === 'private'
          ? {
              title: 'Your soundtrack is private',
              body: 'Manage track visibility to publish your soundtrack on this profile.',
              href: '/individual-dashboard/public-identity?tab=soundtrack',
              cta: 'Manage soundtrack',
              locked: true,
            }
          : {
              title: 'Add your Life Soundtrack',
              body: 'Choose the songs and audio that belong on your public profile.',
              href: '/individual-dashboard/public-identity?tab=soundtrack',
              cta: 'Add soundtrack',
              locked: false,
            };
  const exploreHref = isOwner ? '/individual-dashboard' : '/register';
  const rawVaultId = data.visibility.show_erv_id ? p.erv_id || p.vault_id || '' : '';
  const publicVaultId = rawVaultId
    ? rawVaultId.startsWith('ERV-') || rawVaultId.startsWith('INST-')
      ? rawVaultId
      : `ERV-${rawVaultId}`
    : '';
  const publicVaultHref = publicVaultId
    ? `/vault/${encodeURIComponent(publicVaultId)}`
    : '/public-timeline';

  if (LIVING_RECORD_PROFILE_ENABLED) {
    const livingRecordData = viewAsPublic ? projectPublicPreviewPayload(data) : data;
    if (!livingRecordData) {
      return <PrivateProfilePreview onExit={() => setViewAsPublic(false)} />;
    }
    const livingRecordRawVaultId = livingRecordData.visibility.show_erv_id
      ? livingRecordData.profile.erv_id || livingRecordData.profile.vault_id || ''
      : '';
    const livingRecordVaultId = livingRecordRawVaultId
      ? livingRecordRawVaultId.startsWith('ERV-') || livingRecordRawVaultId.startsWith('INST-')
        ? livingRecordRawVaultId
        : `ERV-${livingRecordRawVaultId}`
      : '';
    return (
      <>
        <LivingRecordProfile
          data={livingRecordData}
          routeHandle={handle}
          isOwnerView={data.isOwner}
          viewAsPublic={viewAsPublic}
          viewerSignedIn={Boolean(user)}
          authLoading={authLoading}
          isFollowing={isFollowing}
          followLoading={followLoading}
          copied={copied}
          publicVaultId={livingRecordVaultId}
          publicVaultHref={
            livingRecordVaultId
              ? `/vault/${encodeURIComponent(livingRecordVaultId)}`
              : '/public-timeline'
          }
          onFollow={handleFollow}
          onMessage={handleReachOut}
          onShare={handleShare}
          onTogglePublicView={() => setViewAsPublic((current) => !current)}
          onRequestSection={requestProfileSection}
          loadingSection={loadingSection}
        />
        {!authLoading && !data.isOwner && (
          <ReachOutDialog
            open={reachOutOpen}
            targetName={p.full_name || 'this person'}
            targetHandle={data.handle}
            onClose={() => setReachOutOpen(false)}
          />
        )}
      </>
    );
  }

  return (
    <div
      className="public-profile-shell"
      style={{
        minHeight: '100vh',
        background: T.pageBg,
        color: T.text,
        paddingBottom: 80,
        fontFamily: 'DM Sans, sans-serif',
        overflowX: 'hidden',
      }}
    >
      <style jsx global>{`
        .public-profile-shell,
        .public-profile-shell * {
          box-sizing: border-box;
        }
        .public-profile-shell img,
        .public-profile-shell iframe,
        .public-profile-shell video {
          max-width: 100%;
        }
        .pp-story-card-grid {
          display: grid;
          grid-template-columns: 156px minmax(0, 1fr);
        }
        .pp-story-visual {
          min-height: 148px;
          overflow: hidden;
          position: relative;
        }
        @media (max-width: 767px) {
          .pp-profile-container {
            padding-left: 12px !important;
            padding-right: 12px !important;
            padding-top: 12px !important;
          }
          .pp-hero-tier-frame {
            background: transparent !important;
            box-shadow: none !important;
            padding: 0 !important;
          }
          .pp-hero-aurora-wash {
            display: none !important;
          }
          .pp-hero-cover {
            height: 144px !important;
          }
          .pp-hero-cover::after {
            content: none;
          }
          .pp-hero-content {
            padding-left: 16px !important;
            padding-right: 16px !important;
          }
          .pp-avatar-wrap {
            margin-top: -44px !important;
            width: 102px;
            align-self: flex-start;
          }
          .pp-hero-actions {
            display: grid !important;
            grid-template-columns: repeat(2, minmax(0, 1fr));
            width: 100%;
          }
          .pp-hero-action {
            width: 100% !important;
            min-width: 0;
            justify-content: center !important;
            padding-left: 10px !important;
            padding-right: 10px !important;
          }
          .pp-featured-visual {
            min-height: 148px !important;
            height: 148px;
            overflow: hidden;
          }
          .pp-featured-visual > img {
            min-height: 100% !important;
          }
          .pp-story-card-grid {
            grid-template-columns: minmax(0, 1fr);
          }
          .pp-story-visual {
            height: 146px;
            min-height: 146px;
          }
          .pp-avatar-ring {
            box-shadow:
              0 6px 16px rgba(15, 23, 42, 0.12),
              0 0 14px rgba(45, 212, 191, 0.13),
              0 0 18px rgba(139, 92, 246, 0.09) !important;
            padding: 2px !important;
          }
          .pp-banner-overlay {
            background: linear-gradient(
              to bottom,
              rgba(15, 23, 42, 0.06) 0%,
              rgba(15, 23, 42, 0) 50%,
              rgba(255, 255, 255, 0.2) 85%,
              rgba(255, 255, 255, 0.45) 100%
            ) !important;
          }
          .pp-tabs-shell {
            max-width: 100%;
            overflow: hidden;
          }
          .pp-tabs-scroll {
            max-width: 100%;
            overflow-x: auto;
            overflow-y: hidden;
            white-space: nowrap;
            overscroll-behavior-inline: contain;
            touch-action: pan-x;
          }
          .pp-tabs-scroll::-webkit-scrollbar {
            display: none;
          }
        }
        .pp-tabs-shell {
          max-width: 100%;
          overflow: hidden;
        }
        .pp-tabs-scroll {
          max-width: 100%;
          overflow-x: auto;
          overflow-y: hidden;
          scrollbar-width: none;
          -webkit-overflow-scrolling: touch;
        }
        .pp-tabs-rail {
          display: inline-flex;
          min-width: max-content;
          border-bottom: none;
          position: relative;
          padding-bottom: 1px;
        }
        .pp-tabs-rail::after {
          content: '';
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          height: 1px;
          background: linear-gradient(
            90deg,
            rgba(45, 212, 191, 0.5),
            rgba(132, 204, 22, 0.36),
            rgba(250, 204, 21, 0.36),
            rgba(244, 114, 182, 0.38),
            rgba(139, 92, 246, 0.44),
            rgba(56, 189, 248, 0.44)
          );
          border-radius: 1px;
        }
        .pp-tabs-scroll::-webkit-scrollbar {
          display: none;
        }
        .pp-tab-arrow {
          display: none;
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
          z-index: 4;
          width: 28px;
          height: 28px;
          border-radius: 8px;
          border: 1px solid rgba(148, 163, 184, 0.22);
          background: rgba(255, 255, 255, 0.95);
          cursor: pointer;
          align-items: center;
          justify-content: center;
          transition: opacity 0.15s;
        }
        @media (min-width: 640px) {
          .pp-tab-arrow {
            display: flex;
          }
        }
      `}</style>
      {/* Owner bar */}
      {data.isOwner && (
        <div
          style={{
            background: '#0d1117',
            color: '#e8edf3',
            padding: '10px 16px',
            display: 'flex',
            flexWrap: 'wrap',
            gap: 8,
            alignItems: 'center',
            justifyContent: 'space-between',
            fontSize: 12,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Link
              href="/individual-dashboard"
              style={{ color: '#4a5d72', textDecoration: 'none', fontSize: 12, fontWeight: 600 }}
            >
              Back to Dashboard
            </Link>
            <span style={{ color: '#2a3d52', fontSize: 11 }}>|</span>
            <span style={{ fontWeight: 600, color: '#4a5d72' }}>Your public profile</span>
          </div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            <Link
              href="/individual-dashboard/public-identity"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 5,
                padding: '6px 10px',
                borderRadius: 7,
                background: 'rgba(255,255,255,0.08)',
                color: '#e8edf3',
                textDecoration: 'none',
                fontWeight: 600,
                fontSize: 12,
                transition: 'background 0.15s',
              }}
            >
              <Settings size={12} /> Edit profile
            </Link>
            <button
              type="button"
              onClick={() => setViewAsPublic((v) => !v)}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 5,
                padding: '6px 10px',
                borderRadius: 7,
                border: 'none',
                background: viewAsPublic ? 'rgba(27,107,92,0.35)' : 'rgba(255,255,255,0.08)',
                color: '#e8edf3',
                fontWeight: 600,
                fontSize: 12,
                cursor: 'pointer',
              }}
            >
              <Eye size={12} /> {viewAsPublic ? 'Back to owner view' : 'View as public'}
            </button>
            <button
              type="button"
              onClick={handleShare}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 5,
                padding: '6px 10px',
                borderRadius: 7,
                border: 'none',
                background: T.accent,
                color: '#fff',
                fontWeight: 600,
                fontSize: 12,
                cursor: 'pointer',
              }}
            >
              {copied ? <Check size={12} /> : <Copy size={12} />}
              {copied ? 'Copied!' : 'Share profile'}
            </button>
          </div>
        </div>
      )}

      {/* Logged-in visitor bar — shown to authenticated users who are NOT the profile owner */}
      {!!user && !data.isOwner && (
        <div
          style={{
            background: '#0d1117',
            color: '#e8edf3',
            padding: '9px 16px',
            display: 'flex',
            flexWrap: 'wrap',
            gap: 8,
            alignItems: 'center',
            justifyContent: 'space-between',
            fontSize: 12,
          }}
        >
          <Link
            href="/individual-dashboard"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              color: '#00b4a0',
              textDecoration: 'none',
              fontWeight: 700,
              fontSize: 12,
            }}
          >
            <ChevronLeft size={14} /> My Ledger
          </Link>
          <Link
            href={`/u/${data.handle || handle}`}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 5,
              padding: '5px 10px',
              borderRadius: 7,
              background: 'rgba(255,255,255,0.06)',
              color: '#8ba3bc',
              textDecoration: 'none',
              fontWeight: 600,
              fontSize: 11,
            }}
          >
            Public view
          </Link>
        </div>
      )}

      {/* Guest bar — shown to logged-out visitors */}
      {!user && !authLoading && (
        <div
          style={{
            background: 'rgba(255,255,255,0.97)',
            borderBottom: '1px solid rgba(0,0,0,0.07)',
            padding: '9px 16px',
            display: 'flex',
            flexWrap: 'wrap',
            gap: 8,
            alignItems: 'center',
            justifyContent: 'flex-end',
            fontSize: 12,
          }}
        >
          <Link
            href="/login"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 5,
              padding: '5px 12px',
              borderRadius: 8,
              background: 'transparent',
              color: '#334155',
              textDecoration: 'none',
              fontWeight: 600,
              fontSize: 12,
            }}
          >
            Sign in
          </Link>
          <Link
            href="/register"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 5,
              padding: '5px 12px',
              borderRadius: 8,
              background: '#00b4a0',
              color: '#fff',
              textDecoration: 'none',
              fontWeight: 700,
              fontSize: 12,
            }}
          >
            Create account
          </Link>
        </div>
      )}

      <div className="pp-profile-container mx-auto max-w-6xl px-4 pt-5 sm:px-6 lg:px-8">
        {/* ── Profile Hero — opal glass identity card ── */}
        <div
          className="pp-hero-tier-frame relative mb-3"
          style={{
            padding: planTier === 'starter' ? 1 : 2,
            borderRadius: 22,
            background: heroBorderForTier(planTier),
            boxShadow: heroShadowForTier(planTier),
          }}
        >
          <div
            className="relative overflow-hidden rounded-[20px]"
            style={{
              background: HERO_GLASS.shell,
              backdropFilter: HERO_GLASS.blur,
              WebkitBackdropFilter: HERO_GLASS.blur,
            }}
          >
            {/* Soft aurora wash behind banner — legacy only */}
            {isLegacy && (
              <div
                className="pp-hero-aurora-wash"
                aria-hidden
                style={{
                  position: 'absolute',
                  inset: 0,
                  pointerEvents: 'none',
                  background:
                    'radial-gradient(ellipse 80% 50% at 12% 0%, rgba(34,211,238,0.14), transparent 55%), radial-gradient(ellipse 60% 45% at 88% 8%, rgba(167,139,250,0.12), transparent 50%), radial-gradient(ellipse 70% 40% at 50% 100%, rgba(251,113,133,0.08), transparent 55%)',
                }}
              />
            )}

            {/* Cover — prominent, lightly framed */}
            <div
              className="pp-hero-cover relative h-44 overflow-hidden sm:h-52 lg:h-60"
              style={{
                background:
                  'linear-gradient(135deg, rgba(224,242,254,0.9) 0%, rgba(237,233,254,0.85) 45%, rgba(255,241,242,0.8) 100%)',
              }}
            >
              {p.banner_url ? (
                <>
                  <img
                    src={p.banner_url}
                    alt=""
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      display: 'block',
                    }}
                  />
                  <div
                    className="pp-banner-overlay"
                    style={{
                      position: 'absolute',
                      inset: 0,
                      background:
                        'linear-gradient(to bottom, rgba(15,23,42,0.08) 0%, rgba(15,23,42,0) 38%, rgba(255,255,255,0.55) 78%, rgba(255,255,255,0.92) 100%)',
                    }}
                  />
                  <div
                    style={{
                      position: 'absolute',
                      inset: 0,
                      background:
                        'linear-gradient(120deg, rgba(34,211,238,0.08) 0%, transparent 35%, rgba(167,139,250,0.06) 65%, transparent 100%)',
                    }}
                  />
                </>
              ) : (
                <div
                  className="h-full w-full"
                  style={{
                    background:
                      'radial-gradient(ellipse at 18% 40%, rgba(34,211,238,0.28) 0%, transparent 52%), radial-gradient(ellipse at 82% 18%, rgba(167,139,250,0.24) 0%, transparent 48%), radial-gradient(ellipse at 62% 88%, rgba(251,113,133,0.18) 0%, transparent 50%), linear-gradient(145deg,#eef6ff 0%,#f5f0ff 45%,#fff7ed 100%)',
                  }}
                >
                  <svg
                    viewBox="0 0 800 240"
                    style={{
                      position: 'absolute',
                      inset: 0,
                      width: '100%',
                      height: '100%',
                      opacity: 0.05,
                    }}
                    aria-hidden
                  >
                    <defs>
                      <pattern id="ppGrid" width="40" height="40" patternUnits="userSpaceOnUse">
                        <path
                          d="M 40 0 L 0 0 0 40"
                          fill="none"
                          stroke="#334155"
                          strokeWidth="0.6"
                        />
                      </pattern>
                    </defs>
                    <rect width="800" height="240" fill="url(#ppGrid)" />
                  </svg>
                </div>
              )}
            </div>

            <div className="pp-hero-content relative z-[1] px-4 pb-5 pt-0 sm:px-6 sm:pb-6 lg:px-7 lg:pb-7">
              <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start">
                  {/* Avatar — dimensional opal ring */}
                  <div
                    className="pp-avatar-wrap"
                    style={{ position: 'relative', flexShrink: 0, marginTop: -50 }}
                  >
                    <div
                      className="pp-avatar-ring"
                      style={{
                        padding: 2,
                        borderRadius: '50%',
                        background: HERO_AURORA_BORDER,
                        boxShadow:
                          '0 8px 22px rgba(15,23,42,.12), 0 0 18px rgba(45,212,191,.14), 0 0 22px rgba(139,92,246,.10)',
                        display: 'inline-flex',
                      }}
                    >
                      <div
                        style={{
                          width: 96,
                          height: 96,
                          borderRadius: '50%',
                          overflow: 'hidden',
                          background: 'linear-gradient(135deg, #dbeafe, #ede9fe, #fce7f3)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: 30,
                          fontWeight: 800,
                          color: '#334155',
                          letterSpacing: '-0.02em',
                          border: '2px solid rgba(255,255,255,0.96)',
                          boxSizing: 'border-box',
                        }}
                      >
                        {p.avatar_url ? (
                          <img
                            src={p.avatar_url}
                            alt=""
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                          />
                        ) : (
                          initials(p.full_name || '?')
                        )}
                      </div>
                    </div>
                    {isVerified && (
                      <div
                        style={{
                          position: 'absolute',
                          bottom: 6,
                          right: 2,
                          width: 24,
                          height: 24,
                          borderRadius: '50%',
                          background: 'linear-gradient(135deg, #34d399, #22c55e)',
                          border: '2.5px solid #fff',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          boxShadow: '0 2px 8px rgba(34,197,94,0.35)',
                        }}
                      >
                        <Check size={11} color="#fff" strokeWidth={3} />
                      </div>
                    )}
                  </div>

                  {/* Identity copy */}
                  <div style={{ flex: 1, minWidth: 0, paddingTop: 4 }}>
                    <div
                      style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}
                    >
                      <h1
                        className="text-2xl sm:text-3xl lg:text-4xl"
                        style={{
                          margin: 0,
                          fontWeight: 800,
                          color: '#0f172a',
                          letterSpacing: '-0.025em',
                          fontFamily: 'DM Serif Display, serif',
                          lineHeight: 1.05,
                          textShadow: '0 1px 0 rgba(255,255,255,0.8)',
                        }}
                      >
                        {p.full_name || displayHandle}
                      </h1>

                      {/* Legacy plan badge */}
                      {isLegacy && (
                        <span
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 4,
                            padding: '4px 10px',
                            borderRadius: 999,
                            background:
                              'linear-gradient(135deg, rgba(252,211,77,0.18) 0%, rgba(167,139,250,0.14) 100%)',
                            border: '1px solid rgba(252,211,77,0.45)',
                            fontSize: 10,
                            fontWeight: 800,
                            color: '#92400e',
                            letterSpacing: '0.08em',
                            textTransform: 'uppercase',
                          }}
                        >
                          <Star size={8} fill="#d97706" color="#d97706" /> Legacy
                        </span>
                      )}

                      {/* Verified plan badge (only when not Legacy) */}
                      {planTier === 'verified' && (
                        <span
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 4,
                            padding: '4px 9px',
                            borderRadius: 999,
                            background: 'rgba(13,148,136,0.09)',
                            border: '1px solid rgba(13,148,136,0.25)',
                            fontSize: 10,
                            fontWeight: 700,
                            color: '#0f766e',
                            letterSpacing: '0.06em',
                            textTransform: 'uppercase',
                          }}
                        >
                          <Zap size={8} color="#0d9488" /> Verified
                        </span>
                      )}

                      {/* Verification badge — only when actually verified */}
                      {isVerified && (
                        <span
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 4,
                            padding: '4px 9px',
                            borderRadius: 999,
                            background:
                              verificationLevel === 'legacy_verified'
                                ? 'linear-gradient(135deg, rgba(34,211,238,0.12), rgba(52,211,153,0.1))'
                                : 'rgba(52,211,153,0.12)',
                            border:
                              verificationLevel === 'legacy_verified'
                                ? '1px solid rgba(34,211,238,0.35)'
                                : '1px solid rgba(52,211,153,0.28)',
                            fontSize: 10,
                            fontWeight: 700,
                            color: verificationLevel === 'legacy_verified' ? '#0369a1' : '#059669',
                            letterSpacing: '0.06em',
                            textTransform: 'uppercase',
                          }}
                        >
                          <ShieldCheck size={9} strokeWidth={2.5} />
                          {verificationLevel === 'witnessed'
                            ? 'Witnessed'
                            : verificationLevel === 'legacy_verified'
                              ? 'Legacy Verified'
                              : 'Verified'}
                        </span>
                      )}
                    </div>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        marginTop: 6,
                        flexWrap: 'wrap',
                      }}
                    >
                      <span
                        style={{
                          fontSize: 13,
                          color: '#64748b',
                          fontWeight: 600,
                          background: 'rgba(255,255,255,0.55)',
                          border: `1px solid ${HERO_GLASS.borderSoft}`,
                          padding: '3px 10px',
                          borderRadius: 999,
                          backdropFilter: 'blur(8px)',
                        }}
                      >
                        @{displayHandle}
                      </span>
                      {publicVaultId && (
                        <span
                          style={{
                            fontSize: 11,
                            color: '#475569',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 4,
                            background: 'rgba(255,255,255,0.55)',
                            border: `1px solid ${isVerified ? 'rgba(52,211,153,0.3)' : HERO_GLASS.borderSoft}`,
                            padding: '3px 9px',
                            borderRadius: 999,
                            fontFamily: 'ui-monospace, monospace',
                            backdropFilter: 'blur(8px)',
                          }}
                        >
                          <Shield size={10} color={isVerified ? '#059669' : '#0d9488'} />{' '}
                          {publicVaultId}
                          {isVerified && <Check size={9} color="#059669" strokeWidth={3} />}
                        </span>
                      )}
                      {p.location && (
                        <span
                          style={{
                            fontSize: 12,
                            color: '#64748b',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 4,
                          }}
                        >
                          <MapPin size={11} color="#94a3b8" /> {p.location}
                        </span>
                      )}
                    </div>
                    {p.bio && (
                      <p
                        style={{
                          margin: '12px 0 0',
                          fontSize: 14.5,
                          color: '#475569',
                          lineHeight: 1.65,
                          maxWidth: 580,
                        }}
                      >
                        {p.bio}
                      </p>
                    )}
                    {p.created_at && (
                      <p
                        style={{
                          margin: '8px 0 0',
                          fontSize: 11.5,
                          color: '#94a3b8',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 4,
                        }}
                      >
                        <Calendar size={10} /> Member since {formatDate(p.created_at)}
                      </p>
                    )}
                  </div>
                </div>

                {/* Actions — polished glass buttons */}
                <div
                  className="pp-hero-actions flex flex-wrap items-center gap-2 lg:justify-end"
                  style={{ flexShrink: 0, marginTop: 4 }}
                >
                  {!authLoading && !data.isOwner && (
                    <button
                      type="button"
                      onClick={handleFollow}
                      disabled={followLoading}
                      className="pp-hero-action active:scale-[0.97]"
                      style={{
                        height: 40,
                        padding: '0 18px',
                        borderRadius: 12,
                        border: isFollowing ? `1px solid ${HERO_GLASS.borderSoft}` : 'none',
                        background: isFollowing
                          ? 'rgba(255,255,255,0.72)'
                          : 'linear-gradient(135deg, #0d9488 0%, #1b6b5c 100%)',
                        color: isFollowing ? '#64748b' : '#fff',
                        fontSize: 13,
                        fontWeight: 700,
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 6,
                        cursor: 'pointer',
                        transition:
                          'transform 150ms cubic-bezier(0.23,1,0.32,1), box-shadow 150ms ease, background-color 150ms ease',
                        boxShadow: isFollowing
                          ? '0 1px 4px rgba(15,23,42,0.06)'
                          : '0 4px 18px rgba(13,148,136,0.28)',
                        backdropFilter: 'blur(10px)',
                      }}
                    >
                      {followLoading ? (
                        <Loader2 size={13} className="animate-spin" />
                      ) : isFollowing ? (
                        <>
                          <UserCheck size={13} /> Following
                        </>
                      ) : (
                        <>
                          <UserPlus size={13} /> Follow
                        </>
                      )}
                    </button>
                  )}
                  {!authLoading && !data.isOwner && (
                    <button
                      type="button"
                      onClick={handleReachOut}
                      className="pp-hero-action active:scale-[0.97]"
                      style={{
                        height: 40,
                        padding: '0 16px',
                        borderRadius: 12,
                        border: `1px solid ${HERO_GLASS.borderSoft}`,
                        background: 'rgba(255,255,255,0.78)',
                        color: '#0f766e',
                        fontSize: 13,
                        fontWeight: 700,
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 6,
                        cursor: 'pointer',
                        boxShadow: '0 2px 10px rgba(15,23,42,0.05)',
                        backdropFilter: 'blur(10px)',
                      }}
                    >
                      <MessageCircle size={14} aria-hidden="true" /> Reach out
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={handleShare}
                    className="pp-hero-action active:scale-[0.97]"
                    style={{
                      height: 40,
                      padding: '0 14px',
                      borderRadius: 12,
                      border: `1px solid ${HERO_GLASS.borderSoft}`,
                      background: 'rgba(255,255,255,0.72)',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6,
                      cursor: 'pointer',
                      color: copied ? '#059669' : '#334155',
                      fontSize: 12.5,
                      fontWeight: 600,
                      boxShadow: '0 2px 10px rgba(15,23,42,0.05)',
                      backdropFilter: 'blur(10px)',
                    }}
                  >
                    {copied ? <Check size={13} /> : <Share2 size={13} />}
                    {copied ? 'Copied!' : 'Share profile'}
                  </button>
                  <button
                    type="button"
                    onClick={handleNextdoorShare}
                    aria-label="Share to Nextdoor"
                    title="Share to Nextdoor"
                    className="pp-hero-action active:scale-[0.97]"
                    style={{
                      height: 40,
                      width: 40,
                      borderRadius: 12,
                      border: `1px solid ${HERO_GLASS.borderSoft}`,
                      background: 'rgba(255,255,255,0.72)',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      color: '#334155',
                      boxShadow: '0 2px 10px rgba(15,23,42,0.05)',
                      backdropFilter: 'blur(10px)',
                    }}
                  >
                    <Home size={14} />
                  </button>
                </div>
              </div>

              {/* Stats — glass tiles with spectral accents */}
              <div
                className="grid grid-cols-3 gap-1.5 sm:grid-cols-6 sm:gap-2"
                style={{
                  marginTop: 18,
                  paddingTop: 16,
                  borderTop: `1px solid ${HERO_GLASS.borderSoft}`,
                }}
              >
                {(
                  [
                    {
                      value: (data.journeyMoments || []).length || stats.publicRecords || 0,
                      label: 'Milestones',
                    },
                    { value: stats.followers ?? 0, label: 'Followers' },
                    { value: stats.following ?? 0, label: 'Following' },
                    { value: stats.publicStories || 0, label: 'Stories' },
                    { value: stats.publicCircles ?? (data.circles || []).length, label: 'Circles' },
                    { value: stats.verifiedRecords ?? 0, label: 'Verified' },
                  ] as const
                ).map((s, i) => (
                  <div
                    key={s.label}
                    style={{
                      textAlign: 'center',
                      padding: '8px 4px',
                      borderRadius: 12,
                      background: HERO_GLASS.panel,
                      border: `1px solid ${HERO_GLASS.borderSoft}`,
                      boxShadow:
                        '0 2px 10px rgba(15,23,42,0.04), inset 0 1px 0 rgba(255,255,255,0.85)',
                      backdropFilter: 'blur(12px)',
                      position: 'relative',
                      overflow: 'hidden',
                    }}
                  >
                    <div
                      aria-hidden
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: '15%',
                        right: '15%',
                        height: 2,
                        borderRadius: 999,
                        background: HERO_STAT_ACCENTS[i % HERO_STAT_ACCENTS.length],
                        opacity: 0.7,
                      }}
                    />
                    <div
                      className="text-lg sm:text-xl"
                      style={{
                        fontWeight: 800,
                        color: '#0f172a',
                        fontFamily: 'DM Serif Display, serif',
                        lineHeight: 1,
                        letterSpacing: '-0.01em',
                      }}
                    >
                      {s.value}
                    </div>
                    <div
                      className="text-[9px] sm:text-[10px]"
                      style={{
                        color: '#64748b',
                        marginTop: 4,
                        fontWeight: 700,
                        letterSpacing: '0.05em',
                        textTransform: 'uppercase',
                      }}
                    >
                      {s.label}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Owner-only Life Soundtrack setup, privacy, or plan state. */}
        {ownerSoundtrackPrompt && (
          <div
            style={{
              margin: '10px 0 4px',
              padding: '16px 18px',
              background: 'linear-gradient(135deg, #fdf4ff 0%, #fff7ed 100%)',
              border: '1px solid rgba(167,139,250,0.25)',
              borderRadius: 14,
              boxShadow: '0 1px 8px rgba(167,139,250,0.07)',
              display: 'flex',
              alignItems: 'center',
              gap: 14,
            }}
          >
            <div
              style={{
                width: 40,
                height: 40,
                borderRadius: 10,
                background: 'rgba(167,139,250,0.12)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <Music size={18} color="#7c3aed" />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 800,
                  color: '#7c3aed',
                  letterSpacing: '0.07em',
                  textTransform: 'uppercase',
                  marginBottom: 2,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 5,
                }}
              >
                {ownerSoundtrackPrompt.locked ? <Lock size={9} /> : <Music size={9} />} Life
                Soundtrack
              </div>
              <div style={{ fontSize: 13, color: '#4c1d95', fontWeight: 600 }}>
                {ownerSoundtrackPrompt.title}
              </div>
              <div style={{ fontSize: 11.5, color: '#6b7280', marginTop: 2 }}>
                {ownerSoundtrackPrompt.body}
              </div>
            </div>
            <Link
              href={ownerSoundtrackPrompt.href}
              className="active:scale-[0.97]"
              style={{
                flexShrink: 0,
                fontSize: 11.5,
                fontWeight: 700,
                color: '#7c3aed',
                textDecoration: 'none',
                border: '1px solid rgba(124,58,237,0.3)',
                borderRadius: 8,
                padding: '6px 12px',
                background: 'rgba(124,58,237,0.06)',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
              }}
            >
              {ownerSoundtrackPrompt.cta} <ChevronRight size={12} />
            </Link>
          </div>
        )}

        {/* ── Life Soundtrack ── */}
        {soundtrackItems.length > 0 && (
          <SectionErrorBoundary section="soundtrack">
            <div
              style={{
                margin: '10px 0 4px',
                borderRadius: 14,
                border: '1px solid rgba(124,58,237,0.13)',
                boxShadow: '0 1px 12px rgba(124,58,237,0.06)',
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  padding: '10px 16px 8px',
                  background: 'linear-gradient(135deg, #fdf4ff 0%, #f0fdf9 100%)',
                  borderBottom: '1px solid rgba(124,58,237,0.09)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 7,
                }}
              >
                <Music size={12} color="#7c3aed" />
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 800,
                    color: '#7c3aed',
                    letterSpacing: '0.07em',
                    textTransform: 'uppercase',
                  }}
                >
                  Life Soundtrack
                </span>
              </div>
              <div style={{ padding: '4px 16px 12px', background: '#fff' }}>
                {soundtrackItems.map((item: PublicSoundtrackItem) => {
                  const { type, embedUrl, url } = resolveSoundtrack(item);
                  const trackId = item.id || url;
                  const isActive = activeTrackId === trackId;
                  const inlineExpandable = isSoundtrackInlineExpandable(type);
                  const externalPlay = opensSoundtrackExternally(type) && !!url;
                  const showPlayControl = inlineExpandable || externalPlay;
                  return (
                    <div key={trackId} style={{ borderTop: '1px solid rgba(0,0,0,0.05)' }}>
                      <div
                        style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 0' }}
                      >
                        {showPlayControl ? (
                          <button
                            type="button"
                            aria-label={
                              externalPlay
                                ? `Open ${item.title} on ${ST_SOURCE_BADGE[type].label}`
                                : isActive
                                  ? `Collapse player for ${item.title}`
                                  : `Play ${item.title} inline`
                            }
                            onClick={() => handleSoundtrackAction(item)}
                            style={{
                              width: 30,
                              height: 30,
                              borderRadius: 9,
                              background:
                                isActive && inlineExpandable
                                  ? 'rgba(27,107,92,0.18)'
                                  : 'rgba(27,107,92,0.1)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flexShrink: 0,
                              border: 'none',
                              cursor: 'pointer',
                              padding: 0,
                              transition: 'background 0.15s',
                            }}
                          >
                            {isActive && inlineExpandable ? (
                              <Pause size={13} color="#1b6b5c" fill="#1b6b5c" />
                            ) : (
                              <Play size={13} color="#1b6b5c" fill="#1b6b5c" />
                            )}
                          </button>
                        ) : (
                          <div
                            style={{
                              width: 30,
                              height: 30,
                              borderRadius: 9,
                              background: 'rgba(27,107,92,0.08)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flexShrink: 0,
                            }}
                          >
                            <Music size={13} color="#1b6b5c" />
                          </div>
                        )}
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div
                            style={{
                              fontSize: 13,
                              fontWeight: 700,
                              color: '#0f1923',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {item.title}
                          </div>
                          <div
                            style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 1 }}
                          >
                            {item.artist && (
                              <span
                                style={{
                                  fontSize: 11.5,
                                  color: '#5c6b7a',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {item.artist}
                              </span>
                            )}
                            <span
                              style={{
                                flexShrink: 0,
                                fontSize: 9.5,
                                fontWeight: 700,
                                letterSpacing: '0.03em',
                                textTransform: 'uppercase',
                                color: ST_SOURCE_BADGE[type].color,
                                background: `${ST_SOURCE_BADGE[type].color}14`,
                                border: `1px solid ${ST_SOURCE_BADGE[type].color}33`,
                                borderRadius: 999,
                                padding: '1px 7px',
                              }}
                            >
                              {ST_SOURCE_BADGE[type].label}
                            </span>
                          </div>
                        </div>
                        {url && (
                          <a
                            href={url}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              fontSize: 11,
                              color: type === 'youtube' ? '#dc2626' : '#1b6b5c',
                              fontWeight: 600,
                              display: 'flex',
                              alignItems: 'center',
                              gap: 3,
                              flexShrink: 0,
                              textDecoration: 'none',
                            }}
                          >
                            {type === 'youtube' ? 'Open on YouTube' : 'Open'}{' '}
                            <ExternalLink size={11} />
                          </a>
                        )}
                      </div>
                      {isActive && inlineExpandable && (
                        <div style={{ padding: '2px 0 10px' }}>
                          {type === 'audio' ? (
                            // eslint-disable-next-line jsx-a11y/media-has-caption
                            <audio controls src={url} style={{ width: '100%', height: 38 }} />
                          ) : type === 'youtube' ? (
                            embedUrl ? (
                              <iframe
                                src={embedUrl}
                                width="100%"
                                height={152}
                                frameBorder="0"
                                allow="clipboard-write; encrypted-media; picture-in-picture"
                                allowFullScreen
                                loading="lazy"
                                style={{
                                  borderRadius: 10,
                                  maxWidth: '100%',
                                  display: 'block',
                                  background: '#f8fafc',
                                }}
                                title={`${item.title} on YouTube`}
                              />
                            ) : (
                              <div
                                style={{
                                  borderRadius: 10,
                                  border: '1px solid rgba(220,38,38,0.16)',
                                  background: 'rgba(220,38,38,0.06)',
                                  color: '#991b1b',
                                  padding: '10px 12px',
                                  fontSize: 12,
                                  fontWeight: 700,
                                }}
                              >
                                Playback opens on YouTube
                              </div>
                            )
                          ) : type === 'spotify' ? (
                            <iframe
                              src={embedUrl!}
                              width="100%"
                              height={/track|episode/.test(url) ? 80 : 152}
                              frameBorder="0"
                              allow="clipboard-write; encrypted-media; picture-in-picture"
                              loading="lazy"
                              style={{ borderRadius: 10, maxWidth: '100%', display: 'block' }}
                              title={`${item.title} on Spotify`}
                            />
                          ) : type === 'apple_music' ? (
                            <iframe
                              src={embedUrl!}
                              width="100%"
                              height={130}
                              frameBorder="0"
                              allow="autoplay *; encrypted-media *; fullscreen *"
                              sandbox="allow-forms allow-popups allow-same-origin allow-scripts allow-storage-access-by-user-activation allow-top-navigation-by-user-activation"
                              loading="lazy"
                              style={{ borderRadius: 10, maxWidth: '100%', display: 'block' }}
                              title={`${item.title} on Apple Music`}
                            />
                          ) : type === 'soundcloud' ? (
                            <iframe
                              src={embedUrl!}
                              width="100%"
                              height={120}
                              frameBorder="0"
                              allow="autoplay"
                              loading="lazy"
                              style={{ borderRadius: 10, maxWidth: '100%', display: 'block' }}
                              title={`${item.title} on SoundCloud`}
                            />
                          ) : null}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </SectionErrorBoundary>
        )}

        {/* ── Tab nav ── */}
        <div
          ref={tabShellRef}
          className="pp-tabs-shell"
          style={{ position: 'relative', overflow: 'hidden' }}
        >
          {/* Left fade — dynamic */}
          <div
            aria-hidden
            style={{
              position: 'absolute',
              left: 0,
              top: 0,
              bottom: 1,
              width: 56,
              background: 'linear-gradient(to right, rgba(248,250,252,0.97) 55%, transparent)',
              zIndex: 3,
              pointerEvents: 'none',
              transition: 'opacity 0.2s',
              opacity: tabFade.left ? 1 : 0,
            }}
          />
          {/* Right fade — dynamic */}
          <div
            aria-hidden
            style={{
              position: 'absolute',
              right: 0,
              top: 0,
              bottom: 1,
              width: 56,
              background: 'linear-gradient(to left, rgba(240,252,249,0.97) 55%, transparent)',
              zIndex: 3,
              pointerEvents: 'none',
              transition: 'opacity 0.2s',
              opacity: tabFade.right ? 1 : 0,
            }}
          />
          {/* Desktop: left arrow */}
          <button
            type="button"
            aria-label="Scroll tabs left"
            onClick={() => scrollTabsBy(-180)}
            className="pp-tab-arrow"
            style={{
              left: 4,
              color: T.tabActive,
              opacity: tabFade.left ? 1 : 0,
              pointerEvents: tabFade.left ? 'auto' : 'none',
            }}
          >
            <ChevronLeft size={14} />
          </button>
          {/* Desktop: right arrow */}
          <button
            type="button"
            aria-label="Scroll tabs right"
            onClick={() => scrollTabsBy(180)}
            className="pp-tab-arrow"
            style={{
              right: 4,
              color: T.tabActive,
              opacity: tabFade.right ? 1 : 0,
              pointerEvents: tabFade.right ? 'auto' : 'none',
            }}
          >
            <ChevronRight size={14} />
          </button>
          <div
            className="pp-tabs-scroll"
            ref={tabBarRef}
            style={{
              overflowX: 'auto',
              overflowY: 'hidden',
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
              scrollSnapType: 'x proximity',
              WebkitOverflowScrolling: 'touch',
              whiteSpace: 'nowrap',
              maxWidth: '100%',
            }}
          >
            <div className="pp-tabs-rail">
              {tabs.map((tab) => {
                const active = activeTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    data-public-tab={tab.id}
                    type="button"
                    onClick={() => switchTab(tab.id)}
                    style={{
                      flexShrink: 0,
                      padding: '12px 14px',
                      border: 'none',
                      borderBottom: '2.5px solid transparent',
                      background: 'none',
                      backgroundImage: active ? SOFT_RAINBOW_GRADIENT : undefined,
                      backgroundRepeat: 'no-repeat',
                      backgroundPosition: 'left bottom',
                      backgroundSize: active ? '100% 2.5px' : undefined,
                      color: active ? T.tabActive : T.tabInactive,
                      fontWeight: active ? 700 : 500,
                      fontSize: 13,
                      cursor: 'pointer',
                      marginBottom: -1,
                      transition: 'color 0.15s, border-color 0.15s',
                      fontFamily: 'DM Sans, sans-serif',
                      whiteSpace: 'nowrap',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 5,
                      scrollSnapAlign: 'start',
                      minHeight: 44,
                    }}
                  >
                    <span>{tab.label}</span>
                    {tab.count !== undefined && tab.count > 0 && (
                      <span
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          minWidth: 18,
                          height: 18,
                          borderRadius: 9,
                          background: active ? T.accentLight : 'rgba(0,0,0,0.06)',
                          color: active ? T.accent : '#8a9bb0',
                          fontSize: 10.5,
                          fontWeight: 700,
                          padding: '0 4px',
                        }}
                      >
                        {tab.count}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* ── Tab panels ── */}
        <div style={{ paddingTop: 20, paddingBottom: 40 }}>
          {activeTab === 'overview' && (
            <SectionErrorBoundary section="overview-tab">
              <OverviewTab
                data={data}
                handle={displayHandle}
                isFollowing={isFollowing}
                profileId={p.id}
                isOwner={!!isOwner}
                onTabSwitch={switchTab}
                exploreHref={exploreHref}
                onRequestJoin={handleCircleRequest}
                circleRequestStatus={circleRequestStatus}
              />
            </SectionErrorBoundary>
          )}
          {activeTab === 'stories' && (
            <SectionErrorBoundary section="stories-tab">
              <StoriesTab
                stories={data.stories}
                featuredStory={data.featuredStory}
                handle={displayHandle}
                profileId={p.id}
                isFollowing={isFollowing}
                visible={data.visibility.show_stories}
              />
            </SectionErrorBoundary>
          )}
          {activeTab === 'milestones' && (
            <SectionErrorBoundary section="milestones-tab">
              <MilestonesTab data={data} />
            </SectionErrorBoundary>
          )}
          {activeTab === 'journey' && (
            <SectionErrorBoundary section="journey-tab">
              <JourneyTab
                moments={data.journeyMoments || []}
                visible={
                  data.visibility.show_journey_preview ||
                  ['preview', 'full'].includes(data.visibility.journey_map_visibility) ||
                  (data.journeyMoments || []).length > 0
                }
                isOwner={!!isOwner}
                planTier={planTier}
              />
            </SectionErrorBoundary>
          )}
          {activeTab === 'heritage' && (
            <SectionErrorBoundary section="heritage-tab">
              <FamilyTab
                items={data.familyItems || []}
                visible={data.visibility.show_family_preview}
                isOwner={!!isOwner}
                planTier={planTier}
              />
            </SectionErrorBoundary>
          )}
          {activeTab === 'circles' && (
            <SectionErrorBoundary section="circles-tab">
              <CirclesPublicTab
                circles={data.circles || []}
                planTier={planTier}
                isOwner={!!isOwner}
                onRequestJoin={handleCircleRequest}
                requestStatus={circleRequestStatus}
              />
            </SectionErrorBoundary>
          )}
          {activeTab === 'photos' && (
            <SectionErrorBoundary section="photos-tab">
              <PhotosTab
                stories={(data.stories || []).filter(isPhotoMedia)}
                visible={data.visibility.show_stories}
              />
            </SectionErrorBoundary>
          )}
          {activeTab === 'videos' && (
            <SectionErrorBoundary section="videos-tab">
              <VideosTab
                stories={(data.stories || []).filter(isVideoMedia)}
                visible={data.visibility.show_stories}
              />
            </SectionErrorBoundary>
          )}
          {activeTab === 'oral_stories' && (
            <SectionErrorBoundary section="oral-stories-tab">
              <OralStoriesTab
                stories={(data.stories || []).filter(isAudioMedia)}
                visible={data.visibility.show_stories}
              />
            </SectionErrorBoundary>
          )}
          {activeTab === 'verified' && (
            <SectionErrorBoundary section="verified-tab">
              <VerifiedTab
                proofCards={data.proofCards}
                visible={data.visibility.show_proof_cards}
                publicVaultHref={publicVaultHref}
              />
            </SectionErrorBoundary>
          )}
          {activeTab === 'pulse' && (
            <SectionErrorBoundary section="pulse-tab">
              <PulseTab posts={data.pulseHighlights} />
            </SectionErrorBoundary>
          )}
          {activeTab === 'life_cards' && (
            <SectionErrorBoundary section="life-cards-tab">
              <LifeCardsPublicTab profileId={p.id} />
            </SectionErrorBoundary>
          )}
          {activeTab === 'about' && (
            <SectionErrorBoundary section="about-tab">
              <AboutTab profile={p} stats={stats} memberSince={p.created_at} />
            </SectionErrorBoundary>
          )}
        </div>
      </div>

      {!user && <VisitorCtaBar />}

      {!authLoading && !data.isOwner && (
        <ReachOutDialog
          open={reachOutOpen}
          targetName={p.full_name || 'this person'}
          targetHandle={data.handle}
          onClose={() => setReachOutOpen(false)}
        />
      )}
    </div>
  );
}

// ── Skeleton ──────────────────────────────────────────────────────────────────

function ProfileSkeleton() {
  return (
    <div style={{ maxWidth: 760, margin: '20px auto', padding: '0 16px' }}>
      <div
        style={{
          borderRadius: 18,
          height: 210,
          background: 'linear-gradient(145deg,#111827,#1a2535)',
        }}
      />
      <div
        style={{ height: 46, margin: '6px 0', borderRadius: 8, background: 'rgba(0,0,0,0.04)' }}
      />
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
        {[1, 2].map((i) => (
          <div key={i} style={{ height: 160, borderRadius: 14, background: 'rgba(0,0,0,0.04)' }} />
        ))}
      </div>
      {[1, 2, 3].map((i) => (
        <div
          key={i}
          style={{
            height: 100,
            margin: '10px 0',
            borderRadius: 14,
            background: 'rgba(0,0,0,0.04)',
          }}
        />
      ))}
    </div>
  );
}

// ── Visitor CTA ───────────────────────────────────────────────────────────────

function VisitorCtaBar() {
  return (
    <div
      style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        padding: '10px 16px max(10px, env(safe-area-inset-bottom))',
        background: 'rgba(243,245,248,0.95)',
        borderTop: '1px solid rgba(0,0,0,0.07)',
        backdropFilter: 'blur(10px)',
      }}
    >
      <Link
        href="/register"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 7,
          minHeight: 48,
          borderRadius: 12,
          background: T.accent,
          color: '#fff',
          fontWeight: 700,
          textDecoration: 'none',
          fontSize: 14,
          boxShadow: '0 4px 16px rgba(27,107,92,0.3)',
        }}
      >
        <Star size={15} fill="#fff" /> Create your Living Ledger
      </Link>
    </div>
  );
}

// ── Overview tab ──────────────────────────────────────────────────────────────

function OverviewTab({
  data,
  handle,
  isFollowing,
  profileId,
  isOwner,
  onTabSwitch,
  exploreHref,
  onRequestJoin,
  circleRequestStatus,
}: {
  data: PublicProfilePayload;
  handle: string;
  isFollowing: boolean;
  profileId: string;
  isOwner: boolean;
  onTabSwitch: (id: TabId) => void;
  exploreHref: string;
  onRequestJoin: (circle: PublicCircle) => void;
  circleRequestStatus: Record<string, 'idle' | 'loading' | 'pending' | 'member' | 'error'>;
}) {
  const allStories: PublicStory[] = [];
  if ((isOwner || data.visibility.show_stories) && data.featuredStory) {
    allStories.push(data.featuredStory);
  }
  (isOwner || data.visibility.show_stories ? data.stories || [] : []).slice(0, 4).forEach((s) => {
    if (!allStories.some((x) => x.id === s.id)) allStories.push(s);
  });

  const jMoments = data.journeyMoments || [];
  const fItems = data.familyItems || [];
  const cItems = data.circles || [];
  const journeyIsPublic =
    data.visibility.show_journey_preview ||
    ['preview', 'full'].includes(data.visibility.journey_map_visibility);

  // For visitors: show Journey if public data exists OR the owner explicitly
  // made the map public, so the public-safe empty preview can render.
  // For owners: always show the module so they see the setup prompt even when
  // visibility is disabled by default. The preview card components themselves
  // handle the isOwner guard and render the correct empty/setup state.
  const showJourneyShowcase = journeyIsPublic || isOwner;
  const showHeritageShowcase =
    isOwner || (data.visibility.show_family_preview && fItems.length > 0);
  const showCircleShowcase = isOwner || (data.visibility.show_public_circles && cItems.length > 0);
  const hasArchiveHighlights = showJourneyShowcase || showHeritageShowcase || showCircleShowcase;

  return (
    <div>
      <section style={{ marginBottom: 22 }}>
        <div
          style={{
            fontSize: 11,
            fontWeight: 900,
            color: T.accent,
            textTransform: 'uppercase',
            marginBottom: 5,
          }}
        >
          Life at a glance
        </div>
        <h2
          style={{
            margin: 0,
            color: T.text,
            fontFamily: 'Georgia, serif',
            fontSize: 24,
            lineHeight: 1.2,
          }}
        >
          A chosen public view of this living record
        </h2>
        <p style={{ margin: '7px 0 0', color: T.textMuted, fontSize: 13, lineHeight: 1.55 }}>
          Only stories, relationships, places, media, and trust records made public by this person
          appear here.
        </p>
      </section>

      {(isOwner || data.visibility.show_stories) && data.featuredStory && (
        <SectionErrorBoundary section="featured-story">
          <div style={{ marginBottom: 22 }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: 12,
              }}
            >
              <span
                style={{ fontSize: 13, fontWeight: 800, color: T.text, letterSpacing: '-0.01em' }}
              >
                Featured Story
              </span>
            </div>
            <FeaturedStoryCard
              story={data.featuredStory}
              profileId={profileId}
              handle={handle}
              isFollowing={isFollowing}
            />
          </div>
        </SectionErrorBoundary>
      )}

      {hasArchiveHighlights && (
        <section style={{ marginBottom: 24 }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'flex-end',
              justifyContent: 'space-between',
              gap: 12,
              marginBottom: 12,
            }}
          >
            <div style={{ minWidth: 0 }}>
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 900,
                  color: T.accent,
                  letterSpacing: 0,
                  textTransform: 'uppercase',
                  marginBottom: 3,
                }}
              >
                Public Archive Highlights
              </div>
              <p style={{ margin: 0, color: T.textMuted, fontSize: 13, lineHeight: 1.45 }}>
                Places and life moments, family memories, and trusted circles.
              </p>
            </div>
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 5,
                padding: '5px 9px',
                borderRadius: 999,
                background: 'rgba(27,107,92,0.08)',
                border: '1px solid rgba(27,107,92,0.16)',
                color: T.accent,
                fontSize: 11,
                fontWeight: 800,
                whiteSpace: 'nowrap',
              }}
            >
              <ShieldCheck size={12} /> Public-safe
            </span>
          </div>
          <div
            className="grid gap-3 sm:gap-4"
            style={{
              gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 260px), 1fr))',
              alignItems: 'stretch',
            }}
          >
            {showJourneyShowcase && (
              <SectionErrorBoundary section="journey-preview">
                <JourneyMapPreviewCard
                  moments={jMoments}
                  visible={journeyIsPublic}
                  onClick={() => onTabSwitch('journey')}
                  isOwner={isOwner}
                />
              </SectionErrorBoundary>
            )}
            {showHeritageShowcase && (
              <SectionErrorBoundary section="heritage-preview">
                <FamilyHeritagePreviewCard
                  items={fItems}
                  summary={data.familySummary}
                  visible={data.visibility.show_family_preview}
                  onClick={() => onTabSwitch('heritage')}
                  isOwner={isOwner}
                />
              </SectionErrorBoundary>
            )}
            {showCircleShowcase && (
              <SectionErrorBoundary section="circles-preview">
                <TrustedCirclesPreviewCard
                  circles={cItems}
                  ownerInitials={initials(data.profile.full_name || handle || 'Member')}
                  onClick={() => onTabSwitch('circles')}
                  isOwner={isOwner}
                  onRequestJoin={onRequestJoin}
                  requestStatus={circleRequestStatus}
                />
              </SectionErrorBoundary>
            )}
          </div>
        </section>
      )}

      {/* Living Profile intro */}
      <section
        style={{
          marginBottom: 24,
          display: 'grid',
          gridTemplateColumns: 'auto minmax(0, 1fr)',
          gap: 12,
          alignItems: 'start',
          padding: '15px 16px',
          borderRadius: 14,
          background:
            'linear-gradient(135deg, rgba(236,254,250,0.92), rgba(240,255,244,0.92) 56%, rgba(255,252,237,0.9))',
          border: '1px solid rgba(27,107,92,0.14)',
          boxShadow: '0 6px 24px rgba(27,107,92,0.06)',
        }}
      >
        <span
          style={{
            width: 34,
            height: 34,
            borderRadius: 11,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: T.accent,
            background: 'rgba(27,107,92,0.1)',
            border: '1px solid rgba(27,107,92,0.14)',
          }}
        >
          <BookOpen size={17} />
        </span>
        <div style={{ minWidth: 0 }}>
          <h3 style={{ margin: '0 0 5px', color: T.text, fontSize: 13.5, fontWeight: 850 }}>
            {isOwner ? 'Your living profile' : 'A living profile'}
          </h3>
          <p style={{ margin: 0, color: T.textMuted, fontSize: 12.5, lineHeight: 1.55 }}>
            {isOwner
              ? 'Your stories, milestones, achievements, journeys, family heritage, and circle memories in one place. A public space for the moments you choose to share, with proof or verification added only when it matters.'
              : 'Stories, milestones, achievements, journeys, family heritage, and circle memories. A public space for the moments they choose to share, with proof or verification added only when it matters.'}
          </p>
        </div>
      </section>

      {/* Life Chapters */}
      {(() => {
        const chapterPillars = Object.keys(PILLAR_TOKENS).filter((pillar) =>
          allStories.some((s) => {
            const tag = s.pillar_tag || '';
            return tag.toLowerCase().includes(pillar.toLowerCase());
          })
        );
        if (chapterPillars.length < 2) return null;
        return (
          <section style={{ marginBottom: 22 }}>
            <div style={{ marginBottom: 10 }}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  marginBottom: 4,
                }}
              >
                <BookOpen size={13} color={T.accent} />
                <span style={{ fontSize: 12, fontWeight: 800, color: T.text }}>Life Chapters</span>
              </div>
              <p style={{ margin: 0, fontSize: 11.5, color: T.textMuted, lineHeight: 1.45 }}>
                Explore this profile through the themes that shape{isOwner ? ' your' : ' their'}{' '}
                story.
              </p>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7 }}>
              {chapterPillars.map((pillar) => {
                const pt = pillarTokens(pillar);
                const count = allStories.filter((s) => {
                  const tag = s.pillar_tag || '';
                  return tag.toLowerCase().includes(pillar.toLowerCase());
                }).length;
                return (
                  <button
                    key={pillar}
                    type="button"
                    onClick={() => onTabSwitch('stories')}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 5,
                      padding: '6px 11px',
                      borderRadius: 9,
                      background: pt.bg,
                      border: `1px solid ${pt.text}22`,
                      color: pt.text,
                      fontSize: 11.5,
                      fontWeight: 700,
                      cursor: 'pointer',
                    }}
                  >
                    {pillar}
                    {count > 1 && (
                      <span
                        style={{
                          fontSize: 10,
                          fontWeight: 800,
                          padding: '1px 5px',
                          borderRadius: 5,
                          background: `${pt.text}18`,
                          color: pt.text,
                        }}
                      >
                        {count}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </section>
        );
      })()}

      {/* Stories */}
      {allStories.length > 0 && (
        <>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: 12,
            }}
          >
            <span
              style={{ fontSize: 13, fontWeight: 700, color: T.text, letterSpacing: '-0.01em' }}
            >
              Public Stories
            </span>
            {(data.stories || []).length > 3 && (
              <button
                type="button"
                onClick={() => onTabSwitch('stories')}
                style={{
                  fontSize: 12,
                  color: T.accent,
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 3,
                }}
              >
                View all <ChevronRight size={13} />
              </button>
            )}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {allStories.slice(0, 3).map((story) => (
              <StoryCard
                key={story.id}
                story={story}
                profileId={profileId}
                handle={handle}
                isFollowing={isFollowing}
              />
            ))}
          </div>
        </>
      )}

      {/* Pulse if no stories */}
      {allStories.length === 0 && (data.pulseHighlights || []).length > 0 && (
        <>
          <div style={{ fontSize: 13, fontWeight: 700, color: T.text, marginBottom: 12 }}>
            Recent Pulse
          </div>
          {(data.pulseHighlights || []).slice(0, 3).map((p) => (
            <PulseCard key={p.id} post={p} />
          ))}
        </>
      )}

      {/* Milestone highlights */}
      {(data.records || []).length > 0 && (
        <>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              margin: '20px 0 10px',
            }}
          >
            <span style={{ fontSize: 13, fontWeight: 700, color: T.text }}>
              Milestone Highlights
            </span>
            <button
              type="button"
              onClick={() => onTabSwitch('milestones')}
              style={{
                fontSize: 12,
                color: T.accent,
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                gap: 3,
              }}
            >
              All {(data.records || []).length} <ChevronRight size={13} />
            </button>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {(data.records || []).slice(0, 2).map((r) => {
              const pt = pillarTokens(r.pillar);
              return (
                <div
                  key={r.id}
                  style={{
                    background: T.cardBg,
                    border: `1px solid ${T.cardBorder}`,
                    borderRadius: 12,
                    padding: '12px 14px',
                    boxShadow: T.cardShadow,
                    display: 'flex',
                    gap: 10,
                    alignItems: 'center',
                  }}
                >
                  <div
                    style={{
                      width: 34,
                      height: 34,
                      borderRadius: 9,
                      background: pt.bg,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <Shield size={15} color={pt.text} />
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <p
                      style={{
                        margin: 0,
                        fontWeight: 700,
                        fontSize: 13.5,
                        color: T.text,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {r.title}
                    </p>
                    <p style={{ margin: '2px 0 0', fontSize: 11.5, color: T.textMuted }}>
                      {[r.pillar, r.institution, formatDate(r.achievement_date)]
                        .filter(Boolean)
                        .join(' · ')}
                    </p>
                  </div>
                  {r.pillar && (
                    <span
                      style={{
                        marginLeft: 'auto',
                        fontSize: 10,
                        fontWeight: 700,
                        padding: '2px 7px',
                        borderRadius: 5,
                        background: pt.bg,
                        color: pt.text,
                        flexShrink: 0,
                      }}
                    >
                      {r.pillar}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}

      <div className="grid gap-4 md:grid-cols-2" style={{ marginTop: 22 }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 800, color: T.text, marginBottom: 10 }}>
            Public Pillars Summary
          </div>
          <PillarsSummary data={data} />
        </div>
        <div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: 10,
            }}
          >
            <span style={{ fontSize: 13, fontWeight: 800, color: T.text }}>
              Pulse / Updates Preview
            </span>
            {(data.pulseHighlights || []).length > 2 && (
              <button
                type="button"
                onClick={() => onTabSwitch('pulse')}
                style={{
                  fontSize: 12,
                  color: T.accent,
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  fontWeight: 700,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 3,
                }}
              >
                More <ChevronRight size={13} />
              </button>
            )}
          </div>
          {(data.pulseHighlights || []).length > 0 ? (
            (data.pulseHighlights || []).slice(0, 2).map((p) => <PulseCard key={p.id} post={p} />)
          ) : (
            <div
              style={{
                background: T.cardBg,
                border: `1px solid ${T.cardBorder}`,
                borderRadius: 14,
                padding: 16,
                boxShadow: T.cardShadow,
              }}
            >
              <p style={{ margin: 0, color: T.textMuted, fontSize: 13 }}>No public updates yet.</p>
            </div>
          )}
        </div>
      </div>

      {/* Memory Capsule teaser */}
      <section
        style={{
          marginTop: 8,
          marginBottom: 22,
          padding: '15px 16px',
          borderRadius: 14,
          background: 'linear-gradient(135deg, rgba(245,243,255,0.95), rgba(236,254,255,0.88))',
          border: '1px solid rgba(99,102,241,0.16)',
          boxShadow: '0 4px 18px rgba(76,29,149,0.05)',
          display: 'flex',
          alignItems: 'center',
          gap: 12,
        }}
      >
        <div
          style={{
            width: 36,
            height: 36,
            borderRadius: 11,
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'rgba(99,102,241,0.10)',
            border: '1px solid rgba(99,102,241,0.18)',
          }}
        >
          <Lock size={16} color="#6366f1" />
        </div>
        <div style={{ minWidth: 0 }}>
          <p style={{ margin: '0 0 3px', fontWeight: 800, fontSize: 13, color: T.text }}>
            Memory Capsules
          </p>
          <p style={{ margin: 0, fontSize: 12, color: T.textMuted, lineHeight: 1.5 }}>
            Time-locked memories, future reveals, and preserved moments for trusted circles or
            meaningful dates.
          </p>
        </div>
      </section>

      {/* CTA — visitors only; owners have the owner bar above */}
      {!isOwner && (
        <div style={{ marginTop: 24 }}>
          <Link
            href={exploreHref}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 7,
              width: '100%',
              minHeight: 50,
              borderRadius: 12,
              background: T.accent,
              color: '#fff',
              fontWeight: 700,
              fontSize: 14,
              textDecoration: 'none',
              letterSpacing: '0.01em',
              boxShadow: '0 4px 16px rgba(27,107,92,0.28)',
              transition: 'opacity 0.15s',
            }}
          >
            <BookOpen size={15} /> Explore Public Stories
            <ChevronRight size={16} />
          </Link>
        </div>
      )}
    </div>
  );
}

// ── Journey Map preview card (with real data) ─────────────────────────────────

function StoryVisualFallback({
  story,
  compact = false,
}: {
  story: PublicStory;
  compact?: boolean;
}) {
  const pillar = (story.pillar_tag || 'History').toLowerCase();
  const themes: Record<
    string,
    { from: string; via: string; to: string; ink: string; motif: string }
  > = {
    faith: { from: '#fff7d6', via: '#e0f2fe', to: '#f5f3ff', ink: '#9a6b16', motif: 'Dawn' },
    history: { from: '#f6ead7', via: '#f8f1e7', to: '#e7d6bd', ink: '#854f0b', motif: 'Archive' },
    achievement: {
      from: '#ecfccb',
      via: '#ecfdf5',
      to: '#dbeafe',
      ink: '#3b6d11',
      motif: 'Milestone',
    },
    performance: { from: '#dbeafe', via: '#e0f2fe', to: '#ede9fe', ink: '#185fa5', motif: 'Craft' },
    journey: { from: '#cffafe', via: '#ecfeff', to: '#ede9fe', ink: '#0e7490', motif: 'Journey' },
    family: { from: '#dcfce7', via: '#f0fdf4', to: '#fef3c7', ink: '#166534', motif: 'Heritage' },
    news: { from: '#e2e8f0', via: '#f8fafc', to: '#dbeafe', ink: '#334155', motif: 'Editorial' },
    favorites: { from: '#ffedd5', via: '#fef3c7', to: '#fce7f3', ink: '#9a3412', motif: 'Culture' },
  };
  const theme = themes[pillar] || themes.history;

  return (
    <div
      className={`${compact ? '' : 'pp-featured-visual'} relative flex h-full overflow-hidden ${compact ? 'min-h-[148px] p-4' : 'min-h-[190px] p-5'}`}
      style={{
        background: `linear-gradient(145deg, ${theme.from} 0%, ${theme.via} 52%, ${theme.to} 100%)`,
        color: theme.ink,
      }}
      aria-label={`${theme.motif} visual for ${story.title || 'featured story'}`}
    >
      <div
        aria-hidden
        className="absolute -right-8 -top-10 h-36 w-36 rounded-full border border-white/60 bg-white/25"
      />
      <div
        aria-hidden
        className="absolute -bottom-10 left-8 h-24 w-44 -rotate-6 rounded-full border border-white/70 bg-white/20"
      />
      <svg
        aria-hidden
        viewBox="0 0 220 80"
        className="absolute bottom-5 left-4 right-4 h-20 opacity-30"
        style={{ width: 'calc(100% - 2rem)' }}
      >
        <path
          d="M4 66 C48 12, 92 78, 132 31 S196 18, 216 7"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeDasharray="5 6"
        />
        <circle cx="5" cy="66" r="4" fill="currentColor" />
        <circle cx="216" cy="7" r="4" fill="currentColor" />
      </svg>
      <div className="relative z-[1] self-start rounded-full border border-white/70 bg-white/55 px-3 py-1 text-[10px] font-black uppercase tracking-[0.14em] backdrop-blur-sm">
        {theme.motif}
      </div>
      <div className="relative z-[1] ml-auto flex h-10 w-10 items-center justify-center rounded-xl border border-white/70 bg-white/55 backdrop-blur-sm">
        <BookOpen size={18} />
      </div>
    </div>
  );
}

function StoryMediaPreview({ story, compact = false }: { story: PublicStory; compact?: boolean }) {
  const [mediaFailed, setMediaFailed] = useState(false);
  const hasVideo = isVideoMedia(story);
  const hasAudio = isAudioMedia(story);
  const hasVisualMedia = Boolean(story.media_url) && !hasAudio;

  useEffect(() => setMediaFailed(false), [story.media_url]);

  if (!hasVisualMedia || mediaFailed) {
    return (
      <div style={{ height: '100%', position: 'relative' }}>
        <StoryVisualFallback story={story} compact={compact} />
        {hasAudio && (
          <span
            style={{
              position: 'absolute',
              right: 10,
              bottom: 10,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 5,
              borderRadius: 999,
              padding: '5px 8px',
              background: 'rgba(255,255,255,0.82)',
              border: '1px solid rgba(255,255,255,0.9)',
              color: T.text,
              fontSize: 10,
              fontWeight: 800,
              backdropFilter: 'blur(8px)',
            }}
          >
            <Mic size={11} /> Oral story
          </span>
        )}
      </div>
    );
  }

  if (hasVideo) {
    return (
      <video
        src={story.media_url || undefined}
        controls={!compact}
        muted={compact}
        preload="metadata"
        playsInline
        onError={() => setMediaFailed(true)}
        aria-label={`Video preview for ${story.title || 'public story'}`}
        style={{
          width: '100%',
          height: '100%',
          minHeight: compact ? 148 : 190,
          objectFit: 'cover',
        }}
      />
    );
  }

  return (
    <img
      src={story.media_url || ''}
      alt={`Visual preview for ${story.title || 'public story'}`}
      loading="lazy"
      decoding="async"
      onError={() => setMediaFailed(true)}
      style={{
        width: '100%',
        height: '100%',
        minHeight: compact ? 148 : 190,
        objectFit: 'cover',
        display: 'block',
      }}
    />
  );
}

function FeaturedStoryCard({
  story,
  profileId,
  handle,
  isFollowing,
}: {
  story: PublicStory;
  profileId: string;
  handle: string;
  isFollowing: boolean;
}) {
  const pillar = pillarTokens(story.pillar_tag);
  const hasAudio = isAudioMedia(story);
  const shareUrl =
    typeof window !== 'undefined'
      ? `${window.location.origin}/community?story=${story.id}`
      : `/community?story=${story.id}`;

  return (
    <div
      style={{
        background: softRainbowBorderBackground('linear-gradient(#ffffff, #ffffff)'),
        border: '1.5px solid transparent',
        borderRadius: 16,
        overflow: 'hidden',
        boxShadow: T.cardShadow,
      }}
    >
      <div className="grid sm:grid-cols-[220px_minmax(0,1fr)]">
        <div
          className="pp-featured-visual"
          style={{ minHeight: 190, background: `linear-gradient(145deg, ${pillar.bg}, #fff7ed)` }}
        >
          <StoryMediaPreview story={story} />
        </div>
        <div style={{ padding: 18 }}>
          <span
            style={{
              fontSize: 10,
              fontWeight: 800,
              color: T.accent,
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
            }}
          >
            Featured story
          </span>
          <h3
            style={{
              margin: '8px 0 6px',
              color: T.text,
              fontSize: 22,
              fontWeight: 800,
              letterSpacing: '-0.02em',
              lineHeight: 1.18,
              fontFamily: 'DM Serif Display, serif',
            }}
          >
            {story.title || 'Untitled Story'}
          </h3>
          <p
            style={{
              margin: '0 0 10px',
              color: T.textMuted,
              fontSize: 12,
              display: 'flex',
              gap: 8,
              flexWrap: 'wrap',
            }}
          >
            {story.pillar_tag && <span>{story.pillar_tag}</span>}
            {story.milestone_date || story.created_at ? (
              <span>{shortDate(story.milestone_date || story.created_at)}</span>
            ) : null}
          </p>
          {story.body && (
            <p style={{ margin: '0 0 14px', color: T.textMuted, fontSize: 14, lineHeight: 1.65 }}>
              {story.body.slice(0, 320)}
            </p>
          )}
          {hasAudio && story.media_url && (
            <audio
              controls
              preload="metadata"
              src={story.media_url}
              style={{ width: '100%', marginBottom: 14 }}
            >
              Your browser does not support audio playback.
            </audio>
          )}
          <PublicContentActions
            postId={story.id}
            postSource="story_post"
            authorId={profileId}
            authorHandle={handle}
            isFollowing={isFollowing}
            variant="light"
            compact
            shareUrl={shareUrl}
            shareTitle={story.title || 'Story'}
            saveTitle={story.title || 'Story'}
            saveContent={(story.body || '').slice(0, 500)}
            savePillar={story.pillar_tag || 'History'}
          />
        </div>
      </div>
    </div>
  );
}

function PillarsSummary({ data }: { data: PublicProfilePayload }) {
  const counts = new Map<string, number>();
  [...data.stories.map((s) => s.pillar_tag), ...data.records.map((r) => r.pillar)]
    .filter(Boolean)
    .forEach((tag) => counts.set(tag as string, (counts.get(tag as string) || 0) + 1));
  const entries = Array.from(counts.entries()).slice(0, 6);

  if (entries.length === 0) {
    return (
      <div
        style={{
          background: T.cardBg,
          border: `1px solid ${T.cardBorder}`,
          borderRadius: 14,
          padding: 16,
          boxShadow: T.cardShadow,
        }}
      >
        <p style={{ margin: 0, color: T.textMuted, fontSize: 13 }}>
          Public pillars will summarize the themes that appear in shared stories.
        </p>
      </div>
    );
  }

  return (
    <div
      style={{
        background: T.cardBg,
        border: `1px solid ${T.cardBorder}`,
        borderRadius: 14,
        padding: 14,
        boxShadow: T.cardShadow,
        display: 'flex',
        flexDirection: 'column',
        gap: 9,
      }}
    >
      {entries.map(([tag, count]) => {
        const pt = pillarTokens(tag);
        return (
          <div key={tag} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div
              style={{
                width: 34,
                height: 34,
                borderRadius: 10,
                background: pt.bg,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: pt.text,
              }}
            >
              <Award size={15} />
            </div>
            <span style={{ flex: 1, color: T.text, fontSize: 13, fontWeight: 700 }}>{tag}</span>
            <span style={{ color: T.textMuted, fontSize: 12 }}>{count} public</span>
          </div>
        );
      })}
    </div>
  );
}

function JourneyMapPreviewCard({
  moments,
  visible,
  onClick,
  isOwner,
}: {
  moments: PublicJourneyMoment[];
  visible: boolean;
  onClick: () => void;
  isOwner: boolean;
}) {
  const [hovered, setHovered] = useState(false);

  const hasData = moments.length > 0;
  const hasCoords = moments.some((m) => m.latitude != null && m.longitude != null);

  const previewMoments = moments.slice(0, 5);
  const imageMoments = previewMoments.filter((moment) => Boolean(moment.thumbnail_url)).slice(0, 3);
  const years = moments
    .map((m) => (m.date ? new Date(m.date).getFullYear() : null))
    .filter((year): year is number => Number.isFinite(year));
  const yearsCovered =
    years.length > 0 ? Math.max(...years) - Math.min(...years) + 1 : moments.length > 0 ? 1 : 0;
  const locationCount = new Set(moments.map((m) => m.location_name).filter(Boolean)).size;
  const turningPointCount = moments.filter((m) =>
    `${m.source_type || ''} ${m.title || ''}`.toLowerCase().includes('turning')
  ).length;

  // Build simple dot map if coords available
  const mapDots = useMemo(() => {
    if (!hasCoords) return [];
    const withCoords = moments.filter((m) => m.latitude != null && m.longitude != null);
    if (withCoords.length === 0) return [];
    const lats = withCoords.map((m) => m.latitude!);
    const lngs = withCoords.map((m) => m.longitude!);
    const minLat = Math.min(...lats);
    const maxLat = Math.max(...lats);
    const minLng = Math.min(...lngs);
    const maxLng = Math.max(...lngs);
    const padLat = Math.max((maxLat - minLat) * 0.2, 5);
    const padLng = Math.max((maxLng - minLng) * 0.2, 5);
    const rangeL = maxLat - minLat + padLat * 2;
    const rangeG = maxLng - minLng + padLng * 2;
    return withCoords.map((m) => ({
      id: m.id,
      title: m.title,
      location: m.location_name,
      cx: ((m.longitude! - minLng + padLng) / rangeG) * 230 + 5,
      cy: 100 - ((m.latitude! - minLat + padLat) / rangeL) * 90 - 5,
    }));
  }, [moments, hasCoords]);

  // Top 3 unique locations for display
  const locationChips = useMemo(() => {
    const seen = new Set<string>();
    const out: string[] = [];
    for (const m of moments) {
      const loc = m.location_name || m.title;
      if (loc && !seen.has(loc)) {
        seen.add(loc);
        out.push(loc);
      }
      if (out.length >= 3) break;
    }
    return out;
  }, [moments]);

  if (!hasData && !isOwner && !visible) return null;

  // The card is interactive (acts as the "View Journey Map" CTA) whenever the
  // journey is public — even with no moments yet — or when there is public data.
  const clickable = hasData || visible;

  return (
    <button
      type="button"
      onClick={clickable ? onClick : undefined}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        borderRadius: 14,
        overflow: 'hidden',
        background: softRainbowBorderBackground('linear-gradient(#ffffff, #ffffff)'),
        border: '1.5px solid transparent',
        boxShadow: hovered && clickable ? T.cardHoverShadow : T.cardShadow,
        cursor: clickable ? 'pointer' : 'default',
        textAlign: 'left',
        padding: 0,
        transition: 'border-color 0.2s, box-shadow 0.2s, transform 0.15s',
        transform: hovered && clickable ? 'translateY(-2px)' : 'none',
        display: 'block',
        width: '100%',
      }}
    >
      {/* Visual area */}
      <div
        style={{
          position: 'relative',
          height: 120,
          overflow: 'hidden',
          background: 'linear-gradient(to bottom, #e8f4f0, #dff0f5)',
        }}
      >
        {hasData && hasCoords && mapDots.length > 1 ? (
          // Dot map
          <svg
            viewBox="0 0 240 120"
            style={{ width: '100%', height: '100%', display: 'block' }}
            preserveAspectRatio="xMidYMid meet"
          >
            <defs>
              <linearGradient id="jm-bg" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#e8f4f0" />
                <stop offset="100%" stopColor="#dff0f5" />
              </linearGradient>
            </defs>
            <rect width="240" height="120" fill="url(#jm-bg)" />
            {/* Grid lines */}
            {[30, 60, 90, 120, 150, 180, 210].map((x) => (
              <line
                key={x}
                x1={x}
                y1="0"
                x2={x}
                y2="120"
                stroke="rgba(27,107,92,0.07)"
                strokeWidth="1"
              />
            ))}
            {[20, 40, 60, 80, 100].map((y) => (
              <line
                key={y}
                x1="0"
                y1={y}
                x2="240"
                y2={y}
                stroke="rgba(27,107,92,0.07)"
                strokeWidth="1"
              />
            ))}
            {/* Path connecting dots */}
            {mapDots.length > 1 && (
              <polyline
                points={mapDots.map((d) => `${d.cx},${d.cy}`).join(' ')}
                fill="none"
                stroke="rgba(27,107,92,0.35)"
                strokeWidth="1.5"
                strokeDasharray="5,3"
              />
            )}
            {/* Dots */}
            {mapDots.map((d, i) => (
              <g key={d.id}>
                <circle cx={d.cx} cy={d.cy} r="8" fill="rgba(27,107,92,0.12)" />
                <circle cx={d.cx} cy={d.cy} r="4.5" fill={i === 0 ? '#1b6b5c' : '#34d399'} />
                {i === 0 && <circle cx={d.cx} cy={d.cy} r="4.5" fill="#1b6b5c" opacity="0.5" />}
              </g>
            ))}
            {/* Count badge */}
            <rect x="170" y="8" width="60" height="18" rx="9" fill="rgba(27,107,92,0.85)" />
            <text
              x="200"
              y="20"
              textAnchor="middle"
              fontSize="9"
              fill="#fff"
              fontWeight="700"
              fontFamily="DM Sans, sans-serif"
            >
              {moments.length} places
            </text>
          </svg>
        ) : hasData ? (
          // No coords — show location chip constellation
          <svg
            viewBox="0 0 240 120"
            style={{ width: '100%', height: '100%', display: 'block' }}
            preserveAspectRatio="xMidYMid meet"
          >
            <defs>
              <linearGradient id="jm-soft" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#e8f5f0" />
                <stop offset="100%" stopColor="#dff5ee" />
              </linearGradient>
            </defs>
            <rect width="240" height="120" fill="url(#jm-soft)" />
            {/* Decorative path */}
            <path
              d="M10,100 C50,88 90,72 130,68 C170,64 200,55 230,48"
              stroke="rgba(27,107,92,0.2)"
              strokeWidth="2"
              fill="none"
              strokeDasharray="6,4"
            />
            {/* Pins for top 3 locations */}
            {locationChips.slice(0, 3).map((loc, i) => {
              const cx = 40 + i * 75;
              const cy = 85 - i * 14;
              return (
                <g key={i}>
                  <circle cx={cx} cy={cy} r="6" fill="rgba(27,107,92,0.15)" />
                  <circle cx={cx} cy={cy} r="3.5" fill="#1b6b5c" />
                  <rect
                    x={cx - 28}
                    y={cy - 26}
                    width="56"
                    height="16"
                    rx="8"
                    fill="rgba(255,255,255,0.9)"
                  />
                  <text
                    x={cx}
                    y={cy - 15}
                    textAnchor="middle"
                    fontSize="7.5"
                    fill="#1b6b5c"
                    fontWeight="700"
                    fontFamily="DM Sans, sans-serif"
                  >
                    {loc.length > 10 ? loc.slice(0, 9) + '.' : loc}
                  </text>
                </g>
              );
            })}
            {/* Count */}
            <rect x="170" y="8" width="60" height="18" rx="9" fill="rgba(27,107,92,0.8)" />
            <text
              x="200"
              y="20"
              textAnchor="middle"
              fontSize="9"
              fill="#fff"
              fontWeight="700"
              fontFamily="DM Sans, sans-serif"
            >
              {moments.length} places
            </text>
          </svg>
        ) : !visible ? (
          // Private — locked illustration
          <svg
            viewBox="0 0 240 120"
            style={{ width: '100%', height: '100%', display: 'block' }}
            preserveAspectRatio="xMidYMid slice"
          >
            <defs>
              <linearGradient id="jm-sky" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#c8e6f5" />
                <stop offset="100%" stopColor="#dbedf5" />
              </linearGradient>
              <linearGradient id="jm-mount" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#4a7c59" stopOpacity="0.9" />
                <stop offset="100%" stopColor="#2d5c3f" />
              </linearGradient>
            </defs>
            <rect width="240" height="120" fill="url(#jm-sky)" />
            <ellipse cx="70" cy="22" rx="30" ry="9" fill="rgba(255,255,255,0.65)" />
            <ellipse cx="170" cy="28" rx="22" ry="7" fill="rgba(255,255,255,0.55)" />
            <path
              d="M0,80 L30,42 L60,62 L90,28 L125,58 L155,35 L185,55 L215,32 L240,52 L240,120 L0,120Z"
              fill="url(#jm-mount)"
            />
            <path d="M0,90 Q60,78 120,84 Q180,90 240,82 L240,120 L0,120Z" fill="#3d6332" />
            <rect width="240" height="120" fill="rgba(255,255,255,0.35)" />
            <circle cx="120" cy="60" r="18" fill="rgba(255,255,255,0.8)" />
            <rect x="113" y="56" width="14" height="11" rx="2" fill="#94a3b8" />
            <path
              d="M114,56 Q114,50 120,50 Q126,50 126,56"
              fill="none"
              stroke="#94a3b8"
              strokeWidth="2.5"
            />
          </svg>
        ) : (
          // Public but no moments yet — open empty map
          <svg
            viewBox="0 0 240 120"
            style={{ width: '100%', height: '100%', display: 'block' }}
            preserveAspectRatio="xMidYMid slice"
          >
            <defs>
              <linearGradient id="jm-sky2" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#d4edda" />
                <stop offset="100%" stopColor="#e8f5e9" />
              </linearGradient>
            </defs>
            <rect width="240" height="120" fill="url(#jm-sky2)" />
            <ellipse cx="60" cy="30" rx="28" ry="8" fill="rgba(255,255,255,0.6)" />
            <ellipse cx="175" cy="22" rx="20" ry="6" fill="rgba(255,255,255,0.5)" />
            <path
              d="M0,75 L25,55 L50,68 L85,40 L115,60 L145,44 L175,58 L210,38 L240,52 L240,120 L0,120Z"
              fill="#4a7c59"
              fillOpacity="0.55"
            />
            <path
              d="M0,88 Q60,80 120,85 Q180,90 240,82 L240,120 L0,120Z"
              fill="#3d6332"
              fillOpacity="0.7"
            />
            <circle
              cx="120"
              cy="58"
              r="14"
              fill="rgba(27,107,92,0.15)"
              stroke="rgba(27,107,92,0.4)"
              strokeWidth="1.5"
              strokeDasharray="3 3"
            />
            <path
              d="M116,58 L120,54 L124,58 M120,54 L120,64"
              stroke="#1b6b5c"
              strokeWidth="1.8"
              strokeLinecap="round"
              fill="none"
            />
          </svg>
        )}
        {imageMoments.length > 0 && (
          <div
            aria-label="Public journey moment thumbnails"
            style={{
              position: 'absolute',
              left: 10,
              bottom: 9,
              zIndex: 3,
              display: 'flex',
              alignItems: 'center',
            }}
          >
            {imageMoments.map((moment, index) => (
              // Public-profile API only returns thumbnails for explicitly public moments.
              // eslint-disable-next-line @next/next/no-img-element
              <img
                key={moment.id}
                src={moment.thumbnail_url!}
                alt=""
                title={moment.title}
                style={{
                  width: 34,
                  height: 34,
                  marginLeft: index === 0 ? 0 : -7,
                  borderRadius: 10,
                  objectFit: 'cover',
                  border: '2px solid rgba(255,255,255,0.94)',
                  boxShadow: '0 4px 12px rgba(15,23,42,0.2)',
                }}
              />
            ))}
          </div>
        )}
      </div>

      {/* Footer */}
      <div style={{ padding: '10px 13px 12px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, marginBottom: 5 }}>
          <div
            style={{
              fontSize: 10.5,
              fontWeight: 800,
              color: T.textMuted,
              textTransform: 'uppercase',
              letterSpacing: 0,
            }}
          >
            Journey Map
          </div>
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
              color: hasData ? T.accent : T.textMuted,
              background: hasData ? 'rgba(27,107,92,0.08)' : 'rgba(148,163,184,0.08)',
              border: `1px solid ${hasData ? 'rgba(27,107,92,0.16)' : 'rgba(148,163,184,0.16)'}`,
              borderRadius: 999,
              padding: '2px 7px',
              fontSize: 10,
              fontWeight: 800,
              whiteSpace: 'nowrap',
            }}
          >
            <ShieldCheck size={10} /> Public preview
          </span>
        </div>
        <p style={{ margin: '2px 0 5px', fontSize: 11, color: T.textMuted, lineHeight: 1.4 }}>
          Places and life moments
        </p>
        {hasData ? (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <p
                style={{
                  margin: 0,
                  fontSize: 12,
                  color: T.text,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4,
                  fontWeight: 600,
                }}
              >
                <MapPin size={11} color={T.accent} />
                {moments.length} public {moments.length === 1 ? 'moment' : 'moments'}
              </p>
              <span
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 3,
                  fontSize: 11.5,
                  color: T.accent,
                  fontWeight: 700,
                  whiteSpace: 'nowrap',
                }}
              >
                Explore Journey <ChevronRight size={12} />
              </span>
            </div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 9 }}>
              <CircleStatChip icon={<MapIcon size={11} />} label={`${moments.length} moments`} />
              {yearsCovered > 0 && (
                <CircleStatChip
                  icon={<Clock size={11} />}
                  label={`${yearsCovered} ${yearsCovered === 1 ? 'year' : 'years'}`}
                />
              )}
              {locationCount > 0 && (
                <CircleStatChip icon={<MapPin size={11} />} label={`${locationCount} places`} />
              )}
              {turningPointCount > 0 && (
                <CircleStatChip
                  icon={<Star size={11} />}
                  label={`${turningPointCount} turning points`}
                />
              )}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 5, marginTop: 9 }}>
              {previewMoments.slice(0, 3).map((m) => (
                <div
                  key={m.id}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '10px minmax(0,1fr)',
                    gap: 7,
                    alignItems: 'start',
                  }}
                >
                  <span
                    style={{
                      width: 7,
                      height: 7,
                      borderRadius: '50%',
                      marginTop: 5,
                      background: 'linear-gradient(135deg,#22d3ee,#a78bfa)',
                      boxShadow: '0 0 0 3px rgba(34,211,238,0.12)',
                    }}
                  />
                  <span style={{ fontSize: 11.5, color: T.textMuted, lineHeight: 1.35 }}>
                    <strong style={{ color: T.text }}>{m.title || 'Public milestone'}</strong>
                    {m.date ? ` - ${new Date(m.date).getFullYear()}` : ''}
                    {m.location_name ? ` - ${m.location_name}` : ''}
                  </span>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 10,
            }}
          >
            <p
              style={{
                margin: 0,
                fontSize: 12,
                color: T.textMuted,
                display: 'flex',
                alignItems: 'center',
                gap: 4,
              }}
            >
              {visible ? <MapPin size={11} color={T.accent} /> : <Lock size={11} />}{' '}
              {visible ? 'Journey Map is public, but no public moments yet.' : 'Journey Map hidden'}
            </p>
            {visible && (
              <span
                style={{
                  color: T.accent,
                  fontSize: 11.5,
                  fontWeight: 700,
                  whiteSpace: 'nowrap',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 3,
                }}
              >
                View Journey Map <ChevronRight size={12} />
              </span>
            )}
            {!visible && (
              <span
                style={{
                  background: 'rgba(255,255,255,0.06)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  color: '#7a9ab5',
                  borderRadius: 9,
                  padding: '4px 9px',
                  fontSize: 11,
                  fontWeight: 700,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 4,
                }}
              >
                <Lock size={10} /> Private
              </span>
            )}
          </div>
        )}
      </div>
    </button>
  );
}

function circleTypeLabel(type?: string | null): string {
  if (!type) return 'Circle';
  return type
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (letter) => letter.toUpperCase())
    .replace('Classmate', 'Classmates')
    .replace('Capsule', 'Time Capsule');
}

function circleAccent(circle: PublicCircle): { color: string; wash: string; glow: string } {
  if (circle.atelier_object) {
    return {
      color: circle.atelier_object.accent,
      wash: `${circle.atelier_object.accent}18`,
      glow: `${circle.atelier_object.accent}33`,
    };
  }
  const key = `${circle.public_theme || ''} ${circle.circle_type || ''}`.toLowerCase();
  if (key.includes('family') || key.includes('heritage')) {
    return { color: '#0f766e', wash: 'rgba(204,251,241,0.74)', glow: 'rgba(20,184,166,0.22)' };
  }
  if (key.includes('team') || key.includes('project')) {
    return { color: '#4f46e5', wash: 'rgba(224,231,255,0.76)', glow: 'rgba(99,102,241,0.22)' };
  }
  if (key.includes('witness')) {
    return { color: '#b45309', wash: 'rgba(254,243,199,0.8)', glow: 'rgba(245,158,11,0.24)' };
  }
  return { color: '#7c3aed', wash: 'rgba(245,243,255,0.78)', glow: 'rgba(167,139,250,0.24)' };
}

const SEAT_PALETTE = ['#00b4a0', '#e9a84c', '#a78bfa', '#34d399', '#f472b6', '#60a5fa'];

function MiniCircleTable({
  circle,
  size = 'normal',
  memberInitials,
}: {
  circle: PublicCircle;
  size?: 'normal' | 'compact';
  memberInitials?: string[];
}) {
  const accent = circleAccent(circle);
  const height = size === 'compact' ? 66 : 96;
  const viewW = 160;
  const viewH = height;
  const cx = viewW / 2;
  const cy = viewH / 2;
  const tableRx = viewW * 0.27;
  const tableRy = viewH * 0.3;
  const personRx = tableRx + viewW * 0.05;
  const personRy = tableRy + viewH * 0.07;
  const seatCount = Math.min(Math.max(circle.member_count || 2, 2), 6);
  const occupiedCount = Math.min(circle.member_count || 0, seatCount);
  const safeInitials = memberInitials?.filter(Boolean).slice(0, occupiedCount) || [];
  const fallbackInitial = initials(circle.name || 'Circle') || 'M';

  const seatPositions = Array.from({ length: seatCount }, (_, i) => {
    const angle = (i / seatCount) * 2 * Math.PI - Math.PI / 2;
    return {
      angle,
      x: cx + personRx * Math.cos(angle),
      y: cy + personRy * Math.sin(angle),
      color: SEAT_PALETTE[i % SEAT_PALETTE.length],
    };
  });

  return (
    <div
      style={{
        height,
        borderRadius: 12,
        position: 'relative',
        overflow: 'hidden',
        background:
          'linear-gradient(145deg, rgba(255,255,255,0.94), rgba(236,254,255,0.82) 45%, rgba(250,245,255,0.90))',
        border: `1px solid ${accent.glow}`,
        boxShadow: `inset 0 1px 0 rgba(255,255,255,0.85), 0 4px 14px rgba(15,23,42,0.10)`,
      }}
    >
      {circle.member_count > 0 && (
        <div
          style={{
            position: 'absolute',
            top: 6,
            right: 6,
            zIndex: 2,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 3,
            padding: size === 'compact' ? '1px 5px' : '2px 6px',
            borderRadius: 999,
            background: 'rgba(255,255,255,0.88)',
            border: `1px solid ${accent.glow}`,
            fontSize: size === 'compact' ? 9 : 10,
            fontWeight: 800,
            color: T.text,
            lineHeight: 1,
          }}
        >
          <Users size={size === 'compact' ? 9 : 10} color={accent.color} />
          {circle.member_count}
        </div>
      )}
      <svg
        viewBox={`0 0 ${viewW} ${viewH}`}
        preserveAspectRatio="xMidYMid meet"
        style={{ width: '100%', height: '100%' }}
        aria-hidden
      >
        <defs>
          <radialGradient id={`ctg-${circle.id}`} cx="50%" cy="34%" r="70%">
            <stop offset="0%" stopColor="rgba(255,255,255,0.92)" />
            <stop offset="58%" stopColor={accent.wash} />
            <stop offset="100%" stopColor={accent.wash} stopOpacity="0.55" />
          </radialGradient>
          <radialGradient id={`ctgs-${circle.id}`} cx="36%" cy="22%" r="54%">
            <stop offset="0%" stopColor="rgba(255,255,255,0.6)" />
            <stop offset="100%" stopColor="rgba(255,255,255,0)" />
          </radialGradient>
          <linearGradient id={`ctga-${circle.id}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={accent.color} stopOpacity="0.3" />
            <stop offset="100%" stopColor={accent.color} stopOpacity="0.12" />
          </linearGradient>
        </defs>
        {/* Soft floor shadow */}
        <ellipse
          cx={cx}
          cy={cy + tableRy * 0.5}
          rx={tableRx + 14}
          ry={tableRy * 0.62}
          fill="rgba(15,23,42,0.10)"
        />
        {/* Chairs around the rim (rounded backs + seats), occupied ones tinted warmer */}
        {seatPositions.map(({ angle, color }, i) => {
          const occupied = i < occupiedCount;
          const chx = cx + (tableRx + 13) * Math.cos(angle);
          const chy = cy + (tableRy + 11) * Math.sin(angle);
          const sx = cx + (tableRx + 5) * Math.cos(angle);
          const sy = cy + (tableRy + 4) * Math.sin(angle);
          const rot = (angle * 180) / Math.PI + 90;
          return (
            <g key={`chair-${i}`}>
              <rect
                x={chx - 6}
                y={chy - 3.4}
                width="12"
                height="7"
                rx="3.4"
                fill={occupied ? '#9b7a52' : '#cabfb0'}
                transform={`rotate(${rot} ${chx} ${chy})`}
              />
              <rect
                x={sx - 6.5}
                y={sy - 4.2}
                width="13"
                height="8.4"
                rx="3"
                fill={occupied ? '#b08c5d' : '#dad0c1'}
                stroke={occupied ? `${color}66` : 'rgba(0,0,0,0.06)'}
                strokeWidth="0.8"
                transform={`rotate(${rot} ${sx} ${sy})`}
              />
            </g>
          );
        })}
        {/* Table apron (thickness) + surface + muted-gold edge */}
        <ellipse cx={cx} cy={cy + 2.5} rx={tableRx} ry={tableRy} fill={`url(#ctga-${circle.id})`} />
        <ellipse
          cx={cx}
          cy={cy}
          rx={tableRx}
          ry={tableRy}
          fill={`url(#ctg-${circle.id})`}
          stroke={`${accent.color}80`}
          strokeWidth="1.4"
        />
        <ellipse
          cx={cx}
          cy={cy}
          rx={tableRx - 2.5}
          ry={tableRy - 2}
          fill="none"
          stroke={`${accent.color}2e`}
          strokeWidth="0.7"
        />
        <ellipse cx={cx} cy={cy} rx={tableRx} ry={tableRy} fill={`url(#ctgs-${circle.id})`} />
        {/* Center tabletop inlay (no decorative icon/glyph so the preview reads as furniture). */}
        <ellipse
          cx={cx}
          cy={cy}
          rx="9"
          ry="5"
          fill="rgba(255,255,255,0.32)"
          stroke={`${accent.color}42`}
          strokeWidth="0.9"
          strokeDasharray="2 2"
        />
        {/* Seated people for occupied seats — head + shoulders leaning toward the
            table so each reads as a person sitting down, not a floating dot. */}
        {seatPositions.map(({ x, y, color, angle }, i) => {
          if (i >= occupiedCount) return null;
          const dx = cx - x;
          const dy = cy - y;
          const len = Math.hypot(dx, dy) || 1;
          const shX = x + (dx / len) * 3;
          const shY = y + (dy / len) * 3;
          const rot = (angle * 180) / Math.PI + 90;
          return (
            <g key={`person-${i}`}>
              <ellipse cx={x} cy={y + 2} rx="6.5" ry="2" fill="rgba(0,0,0,0.12)" />
              <ellipse
                cx={shX}
                cy={shY}
                rx="5.4"
                ry="3.4"
                fill={color}
                opacity="0.9"
                transform={`rotate(${rot} ${shX} ${shY})`}
              />
              <circle cx={x} cy={y} r="4.6" fill="#ffffff" stroke={color} strokeWidth="1.6" />
              <text
                x={x}
                y={y + 1.8}
                textAnchor="middle"
                fontSize="4.3"
                fontWeight="900"
                fill={color}
                fontFamily="ui-sans-serif, system-ui"
              >
                {safeInitials[i] || fallbackInitial}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}

function CircleMediaVisual({
  circle,
  ownerInitials,
  size = 'compact',
}: {
  circle: PublicCircle;
  ownerInitials: string;
  size?: 'compact' | 'normal';
}) {
  const [mediaFailed, setMediaFailed] = useState(false);
  const mediaUrl = circle.public_media_url;
  const isVideo = Boolean(mediaUrl && /\.(mp4|mov|webm)($|\?)/i.test(mediaUrl));

  useEffect(() => setMediaFailed(false), [mediaUrl]);

  if (!mediaUrl || mediaFailed) {
    return <MiniCircleTable circle={circle} size={size} memberInitials={[ownerInitials]} />;
  }

  return (
    <div
      style={{
        height: size === 'compact' ? 76 : 132,
        borderRadius: 12,
        position: 'relative',
        overflow: 'hidden',
        background: circleAccent(circle).wash,
        border: `1px solid ${circleAccent(circle).glow}`,
      }}
    >
      {isVideo ? (
        <video
          src={mediaUrl}
          muted
          playsInline
          preload="metadata"
          onError={() => setMediaFailed(true)}
          aria-label={`Public memory preview for ${circle.name}`}
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
      ) : (
        <img
          src={mediaUrl}
          alt={`Public memory preview for ${circle.name}`}
          loading="lazy"
          decoding="async"
          onError={() => setMediaFailed(true)}
          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
        />
      )}
      <span
        style={{
          position: 'absolute',
          left: 6,
          bottom: 6,
          padding: '3px 6px',
          borderRadius: 999,
          background: 'rgba(15,23,42,0.72)',
          color: '#fff',
          fontSize: 8.5,
          fontWeight: 800,
          backdropFilter: 'blur(6px)',
        }}
      >
        Public memory
      </span>
    </div>
  );
}

function CircleStatChip({ icon, label }: { icon: React.ReactNode; label: string }) {
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        padding: '5px 8px',
        borderRadius: 999,
        background: 'rgba(255,255,255,0.68)',
        border: '1px solid rgba(148,163,184,0.18)',
        color: T.textMuted,
        fontSize: 11,
        fontWeight: 700,
      }}
    >
      {icon}
      {label}
    </span>
  );
}

function TrustedCirclesPreviewCard({
  circles,
  ownerInitials,
  onClick,
  isOwner,
  onRequestJoin,
  requestStatus,
}: {
  circles: PublicCircle[];
  ownerInitials: string;
  onClick: () => void;
  isOwner: boolean;
  onRequestJoin: (circle: PublicCircle) => void;
  requestStatus: Record<string, 'idle' | 'loading' | 'pending' | 'member' | 'error'>;
}) {
  const [hovered, setHovered] = useState(false);
  const hasData = circles.length > 0;
  if (!hasData && !isOwner) return null;

  const featured = circles.slice(0, 3);
  const joinable = featured.find((circle) => circle.allow_join_request);
  const joinStatus = joinable ? requestStatus[joinable.id] || 'idle' : 'idle';
  const joinLabel =
    joinStatus === 'loading'
      ? 'Sending...'
      : joinStatus === 'pending'
        ? 'Request Pending'
        : joinStatus === 'member'
          ? 'Open Circle'
          : joinStatus === 'error'
            ? 'Try Again'
            : 'Request to Join';

  return (
    <section
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        borderRadius: 16,
        overflow: 'hidden',
        background: softRainbowBorderBackground(
          'linear-gradient(145deg, rgba(255,255,255,0.98), rgba(240,253,250,0.96) 42%, rgba(245,243,255,0.98))'
        ),
        border: '1.5px solid transparent',
        boxShadow: hovered && hasData ? T.cardHoverShadow : T.cardShadow,
        textAlign: 'left',
        padding: 14,
        transition: 'border-color 0.2s, box-shadow 0.2s, transform 0.15s',
        transform: hovered && hasData ? 'translateY(-2px)' : 'none',
        width: '100%',
        position: 'relative',
      }}
    >
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background:
            'linear-gradient(120deg, rgba(34,211,238,0.1), rgba(167,139,250,0.08), rgba(251,191,36,0.1))',
          pointerEvents: 'none',
        }}
      />
      <div style={{ position: 'relative' }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 12,
            marginBottom: 12,
          }}
        >
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 5 }}>
              <ShieldCheck size={14} color={T.accent} />
              <span
                style={{
                  fontSize: 10.5,
                  fontWeight: 800,
                  color: T.textMuted,
                  textTransform: 'uppercase',
                  letterSpacing: 0,
                }}
              >
                Circles / Round Table
              </span>
            </div>
            <p
              style={{
                margin: 0,
                fontSize: 13.5,
                lineHeight: 1.45,
                color: T.text,
                fontWeight: 700,
              }}
            >
              Trusted circles, witness roles, and shared public memories.
            </p>
          </div>
          {hasData && (
            <span
              style={{
                padding: '5px 9px',
                borderRadius: 999,
                background: 'rgba(20,184,166,0.1)',
                border: '1px solid rgba(20,184,166,0.22)',
                color: T.accent,
                fontSize: 11,
                fontWeight: 800,
                whiteSpace: 'nowrap',
              }}
            >
              {circles.length} featured
            </span>
          )}
        </div>

        {hasData ? (
          <>
            {/* Aggregate stats row */}
            {(() => {
              const totalMembers = circles.reduce((s, c) => s + c.member_count, 0);
              const totalWitnesses = circles.reduce((s, c) => s + c.witness_count, 0);
              return (
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
                  <CircleStatChip icon={<Users size={11} />} label={`${circles.length} circles`} />
                  <CircleStatChip icon={<Shield size={11} />} label={`${totalMembers} seats`} />
                  <CircleStatChip
                    icon={<ShieldCheck size={11} />}
                    label={`${totalWitnesses} witnesses`}
                  />
                </div>
              );
            })()}
            <div style={{ display: 'grid', gap: 9 }}>
              {featured.map((circle) => {
                const accent = circleAccent(circle);
                return (
                  <div
                    key={circle.id}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'clamp(64px, 20%, 82px) minmax(0, 1fr)',
                      gap: 10,
                      alignItems: 'center',
                      padding: 8,
                      borderRadius: 12,
                      background: 'rgba(255,255,255,0.68)',
                      border: '1px solid rgba(148,163,184,0.16)',
                    }}
                  >
                    <CircleMediaVisual circle={circle} ownerInitials={ownerInitials} />
                    <div style={{ minWidth: 0 }}>
                      <div
                        style={{
                          display: 'flex',
                          gap: 6,
                          alignItems: 'center',
                          flexWrap: 'wrap',
                          marginBottom: 4,
                        }}
                      >
                        <span
                          style={{
                            color: T.text,
                            fontSize: 13,
                            fontWeight: 800,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            maxWidth: 180,
                          }}
                        >
                          {circle.name}
                        </span>
                        <span
                          style={{
                            color: accent.color,
                            background: accent.wash,
                            border: `1px solid ${accent.glow}`,
                            borderRadius: 999,
                            padding: '2px 7px',
                            fontSize: 10,
                            fontWeight: 800,
                          }}
                        >
                          {circleTypeLabel(circle.circle_type)}
                        </span>
                        {circle.atelier_object && (
                          <span
                            style={{
                              color: circle.atelier_object.accent,
                              background: `${circle.atelier_object.accent}12`,
                              border: `1px solid ${circle.atelier_object.accent}30`,
                              borderRadius: 999,
                              padding: '2px 7px',
                              fontSize: 10,
                              fontWeight: 800,
                            }}
                          >
                            {circle.atelier_object.name}
                          </span>
                        )}
                      </div>
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        <CircleStatChip
                          icon={<Users size={11} />}
                          label={`${circle.member_count} members`}
                        />
                        {circle.archive_count > 0 && (
                          <CircleStatChip
                            icon={<FileText size={11} />}
                            label={`${circle.archive_count} public items`}
                          />
                        )}
                        {circle.witness_count > 0 && (
                          <CircleStatChip
                            icon={<Shield size={11} />}
                            label={`${circle.witness_count} witnesses`}
                          />
                        )}
                      </div>
                      {circle.featured_public_item_title && (
                        <p
                          style={{
                            margin: '6px 0 0',
                            color: T.textMuted,
                            fontSize: 11.5,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          Latest public archive: {circle.featured_public_item_title}
                        </p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 13 }}>
              <button
                type="button"
                onClick={onClick}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  border: 0,
                  borderRadius: 10,
                  background: T.accent,
                  color: '#fff',
                  padding: '9px 12px',
                  fontSize: 12,
                  fontWeight: 800,
                  cursor: 'pointer',
                }}
              >
                View Circles <ChevronRight size={14} />
              </button>
              {joinable && (
                <button
                  type="button"
                  disabled={joinStatus === 'loading' || joinStatus === 'pending'}
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    onRequestJoin(joinable);
                  }}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    borderRadius: 10,
                    border: '1px solid rgba(27,107,92,0.22)',
                    background: 'rgba(255,255,255,0.72)',
                    color: T.accent,
                    padding: '9px 12px',
                    fontSize: 12,
                    fontWeight: 800,
                    cursor:
                      joinStatus === 'loading' || joinStatus === 'pending' ? 'default' : 'pointer',
                    opacity: joinStatus === 'loading' || joinStatus === 'pending' ? 0.72 : 1,
                  }}
                >
                  {joinStatus === 'loading' ? (
                    <Loader2 size={13} className="animate-spin" />
                  ) : (
                    <UserPlus size={13} />
                  )}
                  {joinLabel}
                </button>
              )}
            </div>
          </>
        ) : (
          <div
            style={{
              border: '1px dashed rgba(27,107,92,0.22)',
              background: 'rgba(255,255,255,0.62)',
              borderRadius: 12,
              padding: 13,
              display: 'flex',
              gap: 12,
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
            }}
          >
            <p
              style={{
                margin: 0,
                fontSize: 12.5,
                color: T.textMuted,
                display: 'flex',
                alignItems: 'center',
                gap: 6,
              }}
            >
              <Lock size={12} /> No public circle highlights yet.
            </p>
            <Link
              href="/individual-dashboard/public-identity"
              style={{
                color: T.accent,
                fontSize: 12,
                fontWeight: 800,
                textDecoration: 'none',
                whiteSpace: 'nowrap',
              }}
            >
              Manage Circle Visibility
            </Link>
          </div>
        )}
      </div>
    </section>
  );
}

// ── Family Heritage preview card (with real data) ─────────────────────────────

const FAMILY_PREVIEW_POSITIONS = [
  [100, 18],
  [52, 52],
  [152, 52],
  [26, 88],
  [108, 88],
  [178, 52],
] as const;

const FAMILY_PREVIEW_NODE_COLORS = [
  '#1b6b5c',
  '#185fa5',
  '#854f0b',
  '#534ab7',
  '#0f6e56',
  '#a32d2d',
] as const;

function FamilyHeritagePreviewCard({
  items,
  summary,
  visible,
  onClick,
  isOwner,
}: {
  items: PublicFamilyItem[];
  summary?: PublicFamilySummary;
  visible: boolean;
  onClick: () => void;
  isOwner: boolean;
}) {
  const [hovered, setHovered] = useState(false);

  const hasData = visible && items.length > 0;
  const previewItems = summary?.preview_items?.length ? summary.preview_items : items.slice(0, 3);
  const memberCount =
    summary?.public_family_members_count ?? items.filter((i) => i.kind === 'member').length;
  const recordCount =
    summary?.public_family_records_count ?? items.filter((i) => i.kind === 'record').length;
  const generationCount = summary?.public_generations_count ?? null;

  type NodeDef = {
    id: string;
    initials: string;
    label: string;
    x: number;
    y: number;
    masked?: boolean;
    color: string;
    imageUrl?: string | null;
  };

  const nodes: NodeDef[] = useMemo(() => {
    const previewSource =
      hasData && items.some((i) => i.kind === 'member')
        ? items.filter((i) => i.kind === 'member')
        : items;

    if (hasData) {
      return previewSource.slice(0, 5).map((item, i) => {
        const [x, y] = FAMILY_PREVIEW_POSITIONS[i] ?? [100 + i * 30, 20 + i * 20];
        const words = item.title.trim().split(/\s+/);
        const inits =
          words.length >= 2
            ? (words[0][0] + words[words.length - 1][0]).toUpperCase()
            : item.title.slice(0, 2).toUpperCase();
        const label = item.title.length > 9 ? item.title.slice(0, 8) + '.' : item.title;
        return {
          id: item.id,
          initials: inits,
          label,
          x,
          y,
          masked: false,
          color: FAMILY_PREVIEW_NODE_COLORS[i % FAMILY_PREVIEW_NODE_COLORS.length],
          imageUrl: item.thumbnail_url,
        };
      });
    }
    return [
      { id: 'setup-member', initials: '+', label: 'Member', x: 74, y: 44, color: '#1b6b5c' },
      { id: 'setup-record', initials: '+', label: 'Record', x: 126, y: 44, color: '#854f0b' },
      {
        id: 'setup-private',
        initials: '',
        label: '',
        x: 100,
        y: 82,
        masked: true,
        color: '#4a5d72',
      },
    ];
  }, [items, hasData]);

  const setupEdges = !hasData
    ? ([
        nodes[0] && nodes[2] ? [nodes[0], nodes[2]] : null,
        nodes[1] && nodes[2] ? [nodes[1], nodes[2]] : null,
      ].filter(Boolean) as [NodeDef, NodeDef][])
    : [];

  if (!hasData && !isOwner) return null;

  return (
    <button
      type="button"
      onClick={hasData ? onClick : undefined}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        borderRadius: 14,
        overflow: 'hidden',
        background: softRainbowBorderBackground(
          'linear-gradient(145deg, rgba(255,255,255,0.98), rgba(255,247,237,0.96) 42%, rgba(240,253,244,0.98))'
        ),
        border: '1.5px solid transparent',
        boxShadow: hovered && hasData ? T.cardHoverShadow : T.cardShadow,
        cursor: hasData ? 'pointer' : 'default',
        textAlign: 'left',
        padding: 0,
        transition: 'border-color 0.2s, box-shadow 0.2s, transform 0.15s',
        transform: hovered && hasData ? 'translateY(-2px)' : 'none',
        display: 'block',
        width: '100%',
      }}
    >
      {/* Mini tree */}
      <div style={{ height: 120, position: 'relative', overflow: 'hidden' }}>
        {/* Ambient background */}
        <div
          style={{
            position: 'absolute',
            top: -20,
            left: '50%',
            transform: 'translateX(-50%)',
            width: 100,
            height: 80,
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(20,184,166,0.16), transparent 70%)',
            pointerEvents: 'none',
          }}
        />
        <svg
          viewBox="0 0 200 108"
          style={{ width: '100%', height: '100%', display: 'block' }}
          preserveAspectRatio="xMidYMid meet"
        >
          {hasData && (
            <>
              <path
                d="M24 82 C58 48 96 42 124 58 C144 70 162 72 180 54"
                fill="none"
                stroke="rgba(20,184,166,0.22)"
                strokeWidth="1.4"
                strokeDasharray="5 6"
              />
              <rect
                x="34"
                y="84"
                width="132"
                height="14"
                rx="7"
                fill="rgba(255,255,255,0.68)"
                stroke="rgba(217,119,6,0.14)"
              />
              <text
                x="100"
                y="94"
                textAnchor="middle"
                fontSize="6.5"
                fill="rgba(92,107,122,0.85)"
                fontFamily="DM Sans, sans-serif"
                fontWeight="700"
              >
                PUBLIC FAMILY ARCHIVE
              </text>
            </>
          )}
          {setupEdges.map(([from, to], i) => (
            <line
              key={i}
              x1={from.x}
              y1={from.y + 9}
              x2={to.x}
              y2={to.y - 9}
              stroke="rgba(20,184,166,0.22)"
              strokeWidth="1.5"
              strokeDasharray="4,3"
            />
          ))}
          {nodes.map((n, nodeIndex) => (
            <g key={n.id}>
              {n.masked ? (
                <>
                  <rect
                    x={n.x - 28}
                    y={n.y - 10}
                    width="56"
                    height="20"
                    rx="5"
                    fill="rgba(255,255,255,0.9)"
                    stroke="rgba(20,184,166,0.2)"
                    strokeWidth="1"
                  />
                  <text
                    x={n.x}
                    y={n.y + 4}
                    textAnchor="middle"
                    fontSize="6"
                    fill={T.textMuted}
                    fontFamily="DM Sans, sans-serif"
                    letterSpacing="0.5"
                  >
                    PRIVATE
                  </text>
                </>
              ) : (
                <>
                  <circle
                    cx={n.x}
                    cy={n.y}
                    r="13"
                    fill={`${n.color}22`}
                    stroke={n.color}
                    strokeWidth="1.5"
                  />
                  {!n.imageUrl && n.initials === '+' && (
                    <text
                      x={n.x}
                      y={n.y + 4}
                      textAnchor="middle"
                      fontSize="10"
                      fill={n.color}
                      fontWeight="800"
                      fontFamily="DM Sans, sans-serif"
                    >
                      +
                    </text>
                  )}
                  {n.initials !== '+' && (
                    <g aria-hidden>
                      <circle cx={n.x} cy={n.y - 3.5} r="3.6" fill={n.color} opacity="0.82" />
                      <path
                        d={`M ${n.x - 7} ${n.y + 7} C ${n.x - 6} ${n.y + 1}, ${n.x + 6} ${n.y + 1}, ${n.x + 7} ${n.y + 7} Z`}
                        fill={n.color}
                        opacity="0.68"
                      />
                    </g>
                  )}
                  {n.imageUrl && (
                    <>
                      <defs>
                        <clipPath id={`family-preview-avatar-${nodeIndex}`}>
                          <circle cx={n.x} cy={n.y} r="11.5" />
                        </clipPath>
                      </defs>
                      <image
                        href={n.imageUrl}
                        x={n.x - 11.5}
                        y={n.y - 11.5}
                        width="23"
                        height="23"
                        preserveAspectRatio="xMidYMid slice"
                        clipPath={`url(#family-preview-avatar-${nodeIndex})`}
                      />
                      <circle
                        cx={n.x}
                        cy={n.y}
                        r="11.5"
                        fill="none"
                        stroke="rgba(255,255,255,0.9)"
                        strokeWidth="1"
                      />
                    </>
                  )}
                  <text
                    x={n.x}
                    y={n.y + 21}
                    textAnchor="middle"
                    fontSize="6.5"
                    fill="rgba(92,107,122,0.85)"
                    fontFamily="DM Sans, sans-serif"
                  >
                    {n.label}
                  </text>
                </>
              )}
            </g>
          ))}
        </svg>
        {!visible && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              background: 'rgba(255,255,255,0.62)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Lock size={18} color={T.textMuted} />
          </div>
        )}
      </div>

      {/* Footer */}
      <div style={{ padding: '10px 13px 12px' }}>
        <div
          style={{
            fontSize: 10.5,
            fontWeight: 700,
            color: T.textMuted,
            textTransform: 'uppercase',
            letterSpacing: '0.07em',
            marginBottom: 4,
          }}
        >
          Family Heritage
        </div>
        <p style={{ margin: '0 0 5px', fontSize: 11, color: T.textMuted, lineHeight: 1.4 }}>
          Family memories and relationships
        </p>
        {hasData ? (
          <div>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 10,
              }}
            >
              <p
                style={{
                  margin: 0,
                  fontSize: 12,
                  color: T.text,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4,
                  fontWeight: 600,
                }}
              >
                <GitFork size={11} color={T.accent} />
                {memberCount} public {memberCount === 1 ? 'member' : 'members'}
                {recordCount > 0
                  ? ` - ${recordCount} ${recordCount === 1 ? 'record' : 'records'}`
                  : ''}
                {generationCount ? ` - ${generationCount} generations` : ''}
              </p>
              <span
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 3,
                  fontSize: 11.5,
                  color: T.accent,
                  fontWeight: 700,
                  whiteSpace: 'nowrap',
                }}
              >
                View Heritage Preview <ChevronRight size={12} />
              </span>
            </div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 9 }}>
              <CircleStatChip icon={<Users size={11} />} label={`${memberCount} members`} />
              <CircleStatChip icon={<FileText size={11} />} label={`${recordCount} records`} />
              {generationCount ? (
                <CircleStatChip
                  icon={<GitFork size={11} />}
                  label={`${generationCount} generations`}
                />
              ) : null}
              {items.length > previewItems.length && (
                <CircleStatChip icon={<BookOpen size={11} />} label="more public highlights" />
              )}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 9 }}>
              {previewItems.slice(0, 3).map((item) => (
                <div
                  key={item.id}
                  style={{ display: 'flex', alignItems: 'center', gap: 7, minWidth: 0 }}
                >
                  <span
                    style={{
                      width: 20,
                      height: 20,
                      borderRadius: 7,
                      background:
                        item.kind === 'member' ? 'rgba(20,184,166,0.12)' : 'rgba(167,139,250,0.13)',
                      color: item.kind === 'member' ? T.accent : '#6d5bd0',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    {item.kind === 'member' ? <Users size={11} /> : <FileText size={11} />}
                  </span>
                  <span
                    style={{
                      fontSize: 11.5,
                      color: T.textMuted,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    <strong style={{ color: T.text }}>{item.title}</strong>
                    {item.relationship_label ? ` - ${item.relationship_label}` : ''}
                    {item.subtitle ? ` - ${item.subtitle}` : ''}
                  </span>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 10,
            }}
          >
            <p
              style={{
                margin: 0,
                fontSize: 12,
                color: '#9fb3c8',
                display: 'flex',
                alignItems: 'center',
                gap: 4,
              }}
            >
              <Lock size={11} />{' '}
              {visible
                ? isOwner
                  ? 'No public family highlights yet'
                  : 'Family Heritage is not shared publicly yet'
                : 'Family Heritage hidden'}
            </p>
            {isOwner && visible && (
              <Link
                href="/individual-dashboard/public-identity"
                style={{
                  color: T.accent,
                  fontSize: 11.5,
                  fontWeight: 700,
                  textDecoration: 'none',
                  whiteSpace: 'nowrap',
                }}
              >
                Select public family highlights
              </Link>
            )}
            {!visible && (
              <span
                style={{
                  background: 'rgba(255,255,255,0.06)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  color: '#7a9ab5',
                  borderRadius: 9,
                  padding: '4px 9px',
                  fontSize: 11,
                  fontWeight: 700,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 4,
                }}
              >
                <Lock size={10} /> Private
              </span>
            )}
          </div>
        )}
      </div>
    </button>
  );
}

// ── Story card ────────────────────────────────────────────────────────────────

function StoryCard({
  story,
  profileId,
  handle,
  isFollowing,
}: {
  story: PublicStory;
  profileId: string;
  handle: string;
  isFollowing: boolean;
}) {
  const [hovered, setHovered] = useState(false);
  const [comments, setComments] = useState<PublicComment[]>([]);
  const [showComments, setShowComments] = useState(false);

  useEffect(() => {
    if (!showComments) return;
    fetch(`/api/community/comment?post_id=${encodeURIComponent(story.id)}`)
      .then((r) => (r.ok ? r.json() : { comments: [] }))
      .then((json) => setComments(json.comments || []))
      .catch(() => setComments([]));
  }, [story.id, showComments]);

  const pillar = pillarTokens(story.pillar_tag);
  const bodyPreview = (story.body || '').slice(0, 220);
  const shareUrl =
    typeof window !== 'undefined'
      ? `${window.location.origin}/community?story=${story.id}`
      : `/community?story=${story.id}`;
  const storyDate = story.milestone_date || story.created_at;

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: softRainbowBorderBackground('linear-gradient(#ffffff, #ffffff)'),
        border: '1.5px solid transparent',
        borderRadius: 14,
        overflow: 'hidden',
        boxShadow: hovered ? T.cardHoverShadow : T.cardShadow,
        transition: 'border-color 0.2s, box-shadow 0.2s',
      }}
    >
      <div className="pp-story-card-grid">
        {/* Stable media zone: public upload first, premium editorial fallback second. */}
        <div className="pp-story-visual">
          <div
            style={{
              width: '100%',
              height: '100%',
              transition: 'transform 180ms cubic-bezier(0.23, 1, 0.32, 1)',
              transform: hovered ? 'scale(1.025)' : 'scale(1)',
            }}
          >
            <StoryMediaPreview story={story} compact />
          </div>
        </div>

        {/* Text */}
        <div style={{ flex: 1, padding: '12px 14px', minWidth: 0 }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 7,
              marginBottom: 5,
              flexWrap: 'wrap',
            }}
          >
            <p
              style={{
                margin: 0,
                fontWeight: 700,
                fontSize: 14,
                color: T.text,
                flex: 1,
                minWidth: 0,
              }}
            >
              {story.title || 'Untitled Story'}
            </p>
            {story.pillar_tag && (
              <span
                style={{
                  fontSize: 9.5,
                  fontWeight: 700,
                  padding: '2px 7px',
                  borderRadius: 5,
                  background: pillar.bg,
                  color: pillar.text,
                  flexShrink: 0,
                  whiteSpace: 'nowrap',
                }}
              >
                {story.pillar_tag}
              </span>
            )}
          </div>
          {storyDate && (
            <p
              style={{
                margin: '0 0 5px',
                fontSize: 11,
                color: T.textMuted,
                display: 'flex',
                alignItems: 'center',
                gap: 4,
              }}
            >
              <Calendar size={9} /> {shortDate(storyDate)}
            </p>
          )}
          {bodyPreview && (
            <p
              style={{
                margin: '0 0 10px',
                fontSize: 12.5,
                color: T.textMuted,
                lineHeight: 1.55,
                overflow: 'hidden',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical' as React.CSSProperties['WebkitBoxOrient'],
              }}
            >
              {bodyPreview}
            </p>
          )}
          <PublicContentActions
            postId={story.id}
            postSource="story_post"
            authorId={profileId}
            authorHandle={handle}
            isFollowing={isFollowing}
            variant="light"
            shareUrl={shareUrl}
            shareTitle={story.title || 'Story'}
            saveTitle={story.title || 'Story'}
            saveContent={(story.body || '').slice(0, 500)}
            savePillar={story.pillar_tag || 'History'}
            onCommentPosted={(c) => {
              setComments((prev) => [...prev, c]);
              setShowComments(true);
            }}
          />
        </div>
      </div>

      {showComments && comments.length > 0 && (
        <div
          style={{
            padding: '0 14px 14px',
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
            borderTop: `1px solid ${T.cardBorder}`,
            paddingTop: 12,
          }}
        >
          {comments.map((c) => (
            <div
              key={c.id}
              style={{
                padding: '8px 10px',
                borderRadius: 9,
                background: '#f8fafc',
                border: `1px solid ${T.cardBorder}`,
              }}
            >
              <p style={{ margin: '0 0 3px', fontSize: 10.5, fontWeight: 700, color: T.textMuted }}>
                {c.author?.full_name || c.author?.public_handle || 'Reader'}
              </p>
              <p style={{ margin: 0, fontSize: 13, color: T.text, lineHeight: 1.5 }}>{c.body}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Pulse card ────────────────────────────────────────────────────────────────

function PulseCard({ post }: { post: PublicPulsePost }) {
  const pillar = pillarTokens(post.pillar_tag);
  return (
    <Link href={`/pulse?post=${post.id}`} style={{ textDecoration: 'none' }}>
      <div
        style={{
          background: T.cardBg,
          border: `1px solid ${T.cardBorder}`,
          borderRadius: 14,
          padding: '14px',
          marginBottom: 10,
          boxShadow: T.cardShadow,
          transition: 'box-shadow 0.2s',
        }}
      >
        {post.pillar_tag && (
          <span
            style={{
              fontSize: 9.5,
              fontWeight: 700,
              padding: '2px 7px',
              borderRadius: 5,
              background: pillar.bg,
              color: pillar.text,
              marginBottom: 8,
              display: 'inline-block',
            }}
          >
            {post.pillar_tag}
          </span>
        )}
        <p style={{ margin: 0, fontSize: 13.5, color: T.text, lineHeight: 1.6 }}>
          {(post.body || '').slice(0, 220)}
        </p>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
          {post.witness_count > 0 && (
            <span style={{ fontSize: 11.5, color: T.textMuted }}>
              {post.witness_count} witnesses
            </span>
          )}
          <span style={{ fontSize: 11.5, color: T.textMuted }}>{formatDate(post.created_at)}</span>
        </div>
      </div>
    </Link>
  );
}

// ── Circles public tab ────────────────────────────────────────────────────────

function LegacyCirclesPublicTab({
  circles,
  planTier,
  isOwner,
  onRequestJoin,
  requestStatus,
}: {
  circles: PublicCircle[];
  planTier: ProfilePlanTier;
  isOwner: boolean;
  onRequestJoin: (circle: PublicCircle) => void;
  requestStatus: Record<string, 'idle' | 'loading' | 'pending' | 'member' | 'error'>;
}) {
  void planTier;
  void onRequestJoin;

  if (circles.length === 0) {
    if (!isOwner) return null;

    return (
      <div
        style={{
          padding: '34px 24px',
          textAlign: 'center',
          border: '1px dashed rgba(27,107,92,0.22)',
          borderRadius: 16,
          background:
            'linear-gradient(145deg, rgba(255,255,255,0.86), rgba(240,253,250,0.72), rgba(245,243,255,0.76))',
          boxShadow: T.cardShadow,
        }}
      >
        <Users size={28} color={T.accent} style={{ margin: '0 auto 10px' }} />
        <h3 style={{ margin: '0 0 6px', fontSize: 16, color: T.text }}>
          No public circles featured yet
        </h3>
        <p style={{ color: T.textMuted, fontSize: 13, margin: '0 0 16px' }}>
          Choose a public or profile-visible circle to feature on this profile.
        </p>
        <Link
          href="/individual-dashboard/public-identity"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 7,
            color: '#fff',
            background: T.accent,
            borderRadius: 10,
            padding: '10px 14px',
            fontSize: 13,
            fontWeight: 800,
            textDecoration: 'none',
          }}
        >
          Feature a Circle <ChevronRight size={14} />
        </Link>
      </div>
    );
  }

  return (
    <div style={{ paddingTop: 20 }}>
      {/* Section header */}
      <div
        style={{
          borderRadius: 18,
          padding: 18,
          marginBottom: 16,
          background:
            'linear-gradient(145deg, rgba(255,255,255,0.9), rgba(236,254,255,0.7) 42%, rgba(250,245,255,0.82))',
          border: '1px solid rgba(255,255,255,0.9)',
          boxShadow: T.cardShadow,
          display: 'flex',
          gap: 16,
          justifyContent: 'space-between',
          flexWrap: 'wrap',
        }}
      >
        <div style={{ maxWidth: 520 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 7 }}>
            <div
              style={{
                width: 30,
                height: 30,
                borderRadius: 10,
                background: T.accentLight,
                border: `1px solid ${T.accentBorder}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <ShieldCheck size={14} color={T.accent} />
            </div>
            <h3 style={{ margin: 0, fontSize: 17, fontWeight: 850, color: T.text }}>
              Trusted Circles
            </h3>
          </div>
          <p style={{ margin: 0, fontSize: 13.5, color: T.textMuted, lineHeight: 1.55 }}>
            Public-safe windows into the circles where this archive is preserved, witnessed, and
            shared.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <CircleStatChip icon={<Users size={12} />} label={`${circles.length} featured`} />
          <CircleStatChip
            icon={<FileText size={12} />}
            label={`${circles.reduce((sum, circle) => sum + circle.archive_count, 0)} public archives`}
          />
          <CircleStatChip
            icon={<Shield size={12} />}
            label={`${circles.reduce((sum, circle) => sum + circle.witness_count, 0)} witnesses`}
          />
        </div>
      </div>

      {/* Circle cards */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
          gap: 12,
        }}
      >
        {circles.map((circle) => {
          const privacyColor = circle.privacy === 'Public Proof' ? T.accent : '#b45309';
          return (
            <div
              key={circle.id}
              style={{
                background: '#fff',
                border: `1px solid ${T.cardBorder}`,
                borderRadius: 14,
                padding: '14px 16px',
                display: 'flex',
                flexDirection: 'column',
                gap: 10,
              }}
            >
              {/* Circle icon + name */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div
                  style={{
                    width: 38,
                    height: 38,
                    borderRadius: 10,
                    background: T.accentLight,
                    border: `1px solid ${T.accentBorder}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 15,
                    fontWeight: 800,
                    color: T.accent,
                    flexShrink: 0,
                  }}
                >
                  {circle.name[0]?.toUpperCase() || 'C'}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: 13.5,
                      fontWeight: 700,
                      color: T.text,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {circle.name}
                  </div>
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 2 }}>
                    <span
                      style={{
                        fontSize: 9.5,
                        fontWeight: 700,
                        padding: '1px 7px',
                        borderRadius: 5,
                        background: `${privacyColor}14`,
                        color: privacyColor,
                        border: `1px solid ${privacyColor}28`,
                      }}
                    >
                      {circle.privacy}
                    </span>
                    <span style={{ fontSize: 10.5, color: T.textMuted }}>
                      {circle.member_count} {circle.member_count === 1 ? 'member' : 'members'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Description */}
              {circle.description && (
                <p
                  style={{
                    margin: 0,
                    fontSize: 12,
                    color: T.textMuted,
                    lineHeight: 1.55,
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                  }}
                >
                  {circle.description}
                </p>
              )}

              {/* Trust indicators */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, paddingTop: 2 }}>
                <Shield size={11} color={T.accent} />
                <span style={{ fontSize: 11, color: T.accent, fontWeight: 600 }}>
                  Trusted Circle
                </span>
                {circle.is_discoverable && (
                  <>
                    <span style={{ color: T.textMuted }}>·</span>
                    <Globe size={11} color={T.textMuted} />
                    <span style={{ fontSize: 11, color: T.textMuted }}>Discoverable</span>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Journey tab ───────────────────────────────────────────────────────────────

function CirclesPublicTab({
  circles,
  planTier,
  isOwner,
  onRequestJoin,
  requestStatus,
}: {
  circles: PublicCircle[];
  planTier: ProfilePlanTier;
  isOwner: boolean;
  onRequestJoin: (circle: PublicCircle) => void;
  requestStatus: Record<string, 'idle' | 'loading' | 'pending' | 'member' | 'error'>;
}) {
  void planTier;
  void LegacyCirclesPublicTab;

  if (circles.length === 0) {
    if (!isOwner) return null;

    return (
      <div
        style={{
          padding: '34px 24px',
          textAlign: 'center',
          border: '1px dashed rgba(27,107,92,0.22)',
          borderRadius: 16,
          background:
            'linear-gradient(145deg, rgba(255,255,255,0.86), rgba(240,253,250,0.72), rgba(245,243,255,0.76))',
          boxShadow: T.cardShadow,
        }}
      >
        <Users size={28} color={T.accent} style={{ margin: '0 auto 10px' }} />
        <h3 style={{ margin: '0 0 6px', fontSize: 16, color: T.text }}>
          No public circles featured yet
        </h3>
        <p style={{ color: T.textMuted, fontSize: 13, margin: '0 0 16px' }}>
          Choose a public or profile-visible circle to feature on this profile.
        </p>
        <Link
          href="/individual-dashboard/public-identity"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 7,
            color: '#fff',
            background: T.accent,
            borderRadius: 10,
            padding: '10px 14px',
            fontSize: 13,
            fontWeight: 800,
            textDecoration: 'none',
          }}
        >
          Feature a Circle <ChevronRight size={14} />
        </Link>
      </div>
    );
  }

  const archiveTotal = circles.reduce((sum, circle) => sum + circle.archive_count, 0);
  const witnessTotal = circles.reduce((sum, circle) => sum + circle.witness_count, 0);

  return (
    <div style={{ paddingTop: 20 }}>
      <div
        style={{
          borderRadius: 18,
          padding: 18,
          marginBottom: 16,
          background:
            'linear-gradient(145deg, rgba(255,255,255,0.9), rgba(236,254,255,0.7) 42%, rgba(250,245,255,0.82))',
          border: '1px solid rgba(255,255,255,0.9)',
          boxShadow: T.cardShadow,
          display: 'flex',
          gap: 16,
          justifyContent: 'space-between',
          flexWrap: 'wrap',
        }}
      >
        <div style={{ maxWidth: 520 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 7 }}>
            <div
              style={{
                width: 30,
                height: 30,
                borderRadius: 10,
                background: T.accentLight,
                border: `1px solid ${T.accentBorder}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <ShieldCheck size={14} color={T.accent} />
            </div>
            <h3 style={{ margin: 0, fontSize: 17, fontWeight: 850, color: T.text }}>
              Trusted Circles
            </h3>
          </div>
          <p style={{ margin: 0, fontSize: 13.5, color: T.textMuted, lineHeight: 1.55 }}>
            Public-safe windows into the circles where this archive is preserved, witnessed, and
            shared.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <CircleStatChip icon={<Users size={12} />} label={`${circles.length} featured`} />
          <CircleStatChip icon={<FileText size={12} />} label={`${archiveTotal} public archives`} />
          <CircleStatChip icon={<Shield size={12} />} label={`${witnessTotal} witnesses`} />
        </div>
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
          gap: 14,
        }}
      >
        {circles.map((circle) => {
          const accent = circleAccent(circle);
          return (
            <div
              key={circle.id}
              style={{
                background: softRainbowBorderBackground(
                  'linear-gradient(145deg, rgba(255,255,255,0.98), rgba(248,250,252,0.96))'
                ),
                border: '1.5px solid transparent',
                borderRadius: 16,
                padding: 14,
                boxShadow: '0 12px 30px rgba(15,23,42,0.06)',
                display: 'flex',
                flexDirection: 'column',
                gap: 12,
              }}
            >
              <CircleMediaVisual
                circle={circle}
                ownerInitials={initials(circle.name || 'Circle')}
                size="normal"
              />
              <div>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    gap: 10,
                  }}
                >
                  <div style={{ minWidth: 0 }}>
                    <h4
                      style={{
                        margin: 0,
                        fontSize: 15,
                        fontWeight: 850,
                        color: T.text,
                        lineHeight: 1.25,
                      }}
                    >
                      {circle.name}
                    </h4>
                    <div
                      style={{
                        display: 'flex',
                        gap: 6,
                        alignItems: 'center',
                        marginTop: 7,
                        flexWrap: 'wrap',
                      }}
                    >
                      <span
                        style={{
                          fontSize: 10.5,
                          fontWeight: 800,
                          padding: '3px 8px',
                          borderRadius: 999,
                          background: accent.wash,
                          color: accent.color,
                          border: `1px solid ${accent.glow}`,
                        }}
                      >
                        {circleTypeLabel(circle.circle_type)}
                      </span>
                      {circle.atelier_object && (
                        <span
                          style={{
                            fontSize: 10.5,
                            fontWeight: 800,
                            padding: '3px 8px',
                            borderRadius: 999,
                            background: `${circle.atelier_object.accent}12`,
                            color: circle.atelier_object.accent,
                            border: `1px solid ${circle.atelier_object.accent}30`,
                          }}
                        >
                          {circle.atelier_object.name}
                        </span>
                      )}
                      <span
                        style={{
                          fontSize: 10.5,
                          fontWeight: 800,
                          padding: '3px 8px',
                          borderRadius: 999,
                          background: 'rgba(20,184,166,0.1)',
                          color: T.accent,
                          border: '1px solid rgba(20,184,166,0.2)',
                        }}
                      >
                        {circle.visibility_label}
                      </span>
                    </div>
                  </div>
                  <span
                    style={{
                      width: 34,
                      height: 34,
                      borderRadius: 12,
                      background: accent.wash,
                      border: `1px solid ${accent.glow}`,
                      color: accent.color,
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 14,
                      fontWeight: 900,
                      flexShrink: 0,
                    }}
                  >
                    {circle.public_icon || circle.name[0]?.toUpperCase() || 'C'}
                  </span>
                </div>
                {circle.description && (
                  <p
                    style={{
                      margin: '10px 0 0',
                      fontSize: 12.5,
                      color: T.textMuted,
                      lineHeight: 1.55,
                      display: '-webkit-box',
                      WebkitLineClamp: 3,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                    }}
                  >
                    {circle.description}
                  </p>
                )}
              </div>
              <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap' }}>
                <CircleStatChip
                  icon={<Users size={11} />}
                  label={`${circle.member_count} members`}
                />
                <CircleStatChip
                  icon={<FileText size={11} />}
                  label={`${circle.archive_count} public items`}
                />
                <CircleStatChip
                  icon={<Shield size={11} />}
                  label={`${circle.witness_count} witnesses`}
                />
              </div>
              {circle.featured_public_item_title && (
                <div
                  style={{
                    borderRadius: 12,
                    border: '1px solid rgba(148,163,184,0.16)',
                    background: 'rgba(248,250,252,0.76)',
                    padding: '9px 10px',
                    color: T.textMuted,
                    fontSize: 12,
                    lineHeight: 1.45,
                  }}
                >
                  <strong style={{ color: T.text }}>Latest public archive:</strong>{' '}
                  {circle.featured_public_item_title}
                </div>
              )}
              <div
                style={{
                  display: 'flex',
                  gap: 9,
                  flexWrap: 'wrap',
                  marginTop: 'auto',
                  paddingTop: 2,
                }}
              >
                {circle.allow_join_request && (
                  <button
                    type="button"
                    disabled={
                      requestStatus[circle.id] === 'loading' ||
                      requestStatus[circle.id] === 'pending'
                    }
                    onClick={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                      onRequestJoin(circle);
                    }}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6,
                      border: 0,
                      borderRadius: 10,
                      background: T.accent,
                      color: '#fff',
                      padding: '9px 12px',
                      fontSize: 12,
                      fontWeight: 800,
                      cursor:
                        requestStatus[circle.id] === 'loading' ||
                        requestStatus[circle.id] === 'pending'
                          ? 'default'
                          : 'pointer',
                      opacity:
                        requestStatus[circle.id] === 'loading' ||
                        requestStatus[circle.id] === 'pending'
                          ? 0.72
                          : 1,
                    }}
                  >
                    {requestStatus[circle.id] === 'loading' ? (
                      <Loader2 size={13} className="animate-spin" />
                    ) : (
                      <UserPlus size={13} />
                    )}
                    {requestStatus[circle.id] === 'pending'
                      ? 'Request Pending'
                      : requestStatus[circle.id] === 'member'
                        ? 'Open Circle'
                        : requestStatus[circle.id] === 'error'
                          ? 'Try Again'
                          : requestStatus[circle.id] === 'loading'
                            ? 'Sending...'
                            : 'Request to Join'}
                  </button>
                )}
                <Link
                  href="/circles"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    borderRadius: 10,
                    border: '1px solid rgba(27,107,92,0.2)',
                    background: 'rgba(255,255,255,0.72)',
                    color: T.accent,
                    padding: '9px 12px',
                    fontSize: 12,
                    fontWeight: 800,
                    textDecoration: 'none',
                  }}
                >
                  Create a Private Circle <ChevronRight size={13} />
                </Link>
              </div>
            </div>
          );
        })}
      </div>
      {!isOwner && (
        <div style={{ marginTop: 16, display: 'flex', justifyContent: 'center' }}>
          <Link
            href="/register"
            style={{
              color: T.textMuted,
              fontSize: 12,
              fontWeight: 800,
              textDecoration: 'none',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
            }}
          >
            Create Your Ertzyx Profile <ChevronRight size={13} />
          </Link>
        </div>
      )}
    </div>
  );
}

function JourneyTab({
  moments,
  visible,
  isOwner,
  planTier,
}: {
  moments: PublicJourneyMoment[];
  visible: boolean;
  isOwner: boolean;
  planTier: ProfilePlanTier;
}) {
  if (planTier === 'starter') {
    return (
      <LockedModuleCard
        icon={<MapIcon size={22} />}
        title="Journey Map"
        description="Map your life story across places and time — milestones, travels, and chapters on an interactive journey path."
        requiredPlan="verified"
        isOwner={isOwner}
        ownerPlanTier={planTier}
        variant="journey"
      />
    );
  }
  if (!visible && !isOwner) {
    return (
      <LockedSection
        icon={<MapIcon size={26} />}
        title="Journey Map is private"
        body="This user has not made their journey map public."
        ctaLabel="Create your Journey Map"
        ctaHref="/register"
      />
    );
  }
  if (moments.length === 0) {
    return (
      <div
        style={{
          padding: '32px 24px',
          textAlign: 'center',
          borderRadius: 14,
          border: `1px dashed ${T.cardBorder}`,
          background: '#f9fafb',
        }}
      >
        <MapPin size={28} color={T.textMuted} style={{ marginBottom: 12 }} />
        <p style={{ color: T.textMuted, fontSize: 14, margin: 0 }}>
          {isOwner
            ? 'Add journey moments to share your story. Enable preview in privacy settings.'
            : 'No public journey moments yet.'}
        </p>
      </div>
    );
  }
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 16 }}>
        <MapPin size={14} color={T.accent} />
        <span style={{ fontSize: 13, fontWeight: 700, color: T.text }}>
          {moments.length} public {moments.length === 1 ? 'place' : 'places'} on the map
        </span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {moments.map((m) => {
          const visualKey = `${m.source_type || ''} ${m.title || ''}`.toLowerCase();
          const visual =
            visualKey.includes('career') || visualKey.includes('achievement')
              ? {
                  background: 'linear-gradient(145deg,#dcfce7,#dbeafe)',
                  color: '#166534',
                  icon: <Award size={20} />,
                }
              : visualKey.includes('education') || visualKey.includes('school')
                ? {
                    background: 'linear-gradient(145deg,#dbeafe,#ede9fe)',
                    color: '#1d4ed8',
                    icon: <BookOpen size={20} />,
                  }
                : visualKey.includes('family') || visualKey.includes('history')
                  ? {
                      background: 'linear-gradient(145deg,#fef3c7,#ffedd5)',
                      color: '#92400e',
                      icon: <GitFork size={20} />,
                    }
                  : {
                      background: 'linear-gradient(145deg,#cffafe,#ede9fe)',
                      color: '#0e7490',
                      icon: <MapPin size={20} />,
                    };
          return (
            <div
              key={m.id}
              style={{
                background: T.cardBg,
                border: `1px solid ${T.cardBorder}`,
                borderRadius: 14,
                padding: '14px 16px',
                boxShadow: T.cardShadow,
                display: 'flex',
                gap: 12,
                alignItems: 'flex-start',
              }}
            >
              {m.thumbnail_url ? (
                // Journey thumbnails originate from explicitly public uploaded media.
                // eslint-disable-next-line @next/next/no-img-element
                <img
                  src={m.thumbnail_url}
                  alt=""
                  style={{
                    width: 56,
                    height: 56,
                    borderRadius: 9,
                    objectFit: 'cover',
                    flexShrink: 0,
                  }}
                />
              ) : (
                <div
                  style={{
                    width: 56,
                    height: 56,
                    borderRadius: 9,
                    background: visual.background,
                    color: visual.color,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    border: '1px solid rgba(255,255,255,0.76)',
                    boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.76)',
                  }}
                >
                  {visual.icon}
                </div>
              )}
              <div style={{ minWidth: 0, flex: 1 }}>
                <p style={{ margin: '0 0 4px', fontWeight: 700, fontSize: 14, color: T.text }}>
                  {m.title}
                </p>
                <div
                  style={{
                    display: 'flex',
                    gap: 10,
                    flexWrap: 'wrap',
                    marginBottom: m.summary ? 6 : 0,
                  }}
                >
                  {m.location_name && (
                    <span
                      style={{
                        fontSize: 12,
                        color: T.textMuted,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 3,
                      }}
                    >
                      <MapPin size={10} />
                      {m.location_name}
                    </span>
                  )}
                  {m.date && (
                    <span
                      style={{
                        fontSize: 12,
                        color: T.textMuted,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 3,
                      }}
                    >
                      <Calendar size={10} />
                      {formatDate(m.date)}
                    </span>
                  )}
                </div>
                {m.summary && (
                  <p style={{ margin: 0, fontSize: 13, color: T.textMuted, lineHeight: 1.55 }}>
                    {m.summary}
                  </p>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Family Heritage tab ───────────────────────────────────────────────────────

function FamilyTab({
  items,
  visible,
  isOwner,
  planTier,
}: {
  items: PublicFamilyItem[];
  visible: boolean;
  isOwner: boolean;
  planTier: ProfilePlanTier;
}) {
  if (planTier === 'starter' || planTier === 'verified') {
    return (
      <LockedModuleCard
        icon={<GitFork size={22} />}
        title="Family Heritage"
        description="A curated public view of family members, heritage records, and ancestral milestones — preserved across generations."
        requiredPlan="legacy"
        isOwner={isOwner}
        ownerPlanTier={planTier}
        variant="heritage"
      />
    );
  }
  if (!visible) {
    return (
      <LockedSection
        icon={<GitFork size={26} />}
        title="Family Heritage is private"
        body="The owner has not enabled Family Ledger highlights on their public profile."
        ctaLabel={isOwner ? 'Manage in Public Identity' : 'Start your Family Ledger'}
        ctaHref={isOwner ? '/individual-dashboard/public-identity' : '/register'}
      />
    );
  }
  if (items.length === 0) {
    return (
      <div
        style={{
          padding: '32px 24px',
          textAlign: 'center',
          borderRadius: 14,
          border: `1px dashed ${T.cardBorder}`,
          background: '#f9fafb',
        }}
      >
        <GitFork size={28} color={T.textMuted} style={{ marginBottom: 12 }} />
        <p style={{ color: T.textMuted, fontSize: 14, margin: 0 }}>
          {isOwner
            ? 'Mark family members or records as Public in Family Ledger, then enable Family Heritage here.'
            : 'No public family highlights yet.'}
        </p>
      </div>
    );
  }

  const members = items.filter((i) => i.kind === 'member');
  const records = items.filter((i) => i.kind === 'record');
  const heritage = items.filter((i) => i.kind === 'heritage');

  const grouped: Record<string, typeof items> = {};
  const ungrouped: typeof items = [];
  for (const item of heritage) {
    if (item.collection_title) {
      if (!grouped[item.collection_title]) grouped[item.collection_title] = [];
      grouped[item.collection_title].push(item);
    } else {
      ungrouped.push(item);
    }
  }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 16 }}>
        <GitFork size={14} color={T.accent} />
        <span style={{ fontSize: 13, fontWeight: 700, color: T.text }}>
          {items.length} public {items.length === 1 ? 'highlight' : 'highlights'}
        </span>
      </div>

      {members.length > 0 && (
        <div style={{ marginBottom: 18 }}>
          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              color: T.textMuted,
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
              marginBottom: 8,
              display: 'flex',
              alignItems: 'center',
              gap: 5,
            }}
          >
            <Users size={11} /> Family members
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {members.map((f) => (
              <FamilyItemCard key={f.id} item={f} />
            ))}
          </div>
        </div>
      )}

      {records.length > 0 && (
        <div style={{ marginBottom: 18 }}>
          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              color: T.textMuted,
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
              marginBottom: 8,
              display: 'flex',
              alignItems: 'center',
              gap: 5,
            }}
          >
            <FileText size={11} /> Family records
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {records.map((f) => (
              <FamilyItemCard key={f.id} item={f} />
            ))}
          </div>
        </div>
      )}

      {Object.entries(grouped).map(([colTitle, colItems]) => (
        <div key={colTitle} style={{ marginBottom: 18 }}>
          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              color: T.textMuted,
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
              marginBottom: 8,
              display: 'flex',
              alignItems: 'center',
              gap: 5,
            }}
          >
            <GitFork size={11} /> {colTitle}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {colItems.map((f) => (
              <FamilyItemCard key={f.id} item={f} />
            ))}
          </div>
        </div>
      ))}

      {ungrouped.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {ungrouped.map((f) => (
            <FamilyItemCard key={f.id} item={f} />
          ))}
        </div>
      )}
    </div>
  );
}

function FamilyItemCard({ item }: { item: PublicFamilyItem }) {
  const [hovered, setHovered] = useState(false);
  const icon =
    item.kind === 'member' ? (
      <Users size={15} color={T.accent} />
    ) : item.kind === 'record' ? (
      <FileText size={15} color={T.accent} />
    ) : (
      <GitFork size={15} color={T.accent} />
    );
  const badge =
    item.relationship_label ||
    (item.kind === 'member' ? 'Family member' : item.kind === 'record' ? 'Record' : 'Heritage');

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: T.cardBg,
        border: `1px solid ${hovered ? T.accentBorder : T.cardBorder}`,
        borderRadius: 12,
        padding: '13px 16px',
        boxShadow: hovered ? T.cardHoverShadow : T.cardShadow,
        transition: 'all 0.2s',
        display: 'flex',
        gap: 12,
        alignItems: 'flex-start',
      }}
    >
      <div
        style={{
          width: 36,
          height: 36,
          borderRadius: 9,
          background: 'rgba(27,107,92,0.1)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        {icon}
      </div>
      <div style={{ minWidth: 0 }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            flexWrap: 'wrap',
            marginBottom: 3,
          }}
        >
          <p style={{ margin: 0, fontWeight: 700, fontSize: 14, color: T.text }}>{item.title}</p>
          <span
            style={{
              fontSize: 10,
              fontWeight: 700,
              color: T.accent,
              background: 'rgba(0,180,160,0.1)',
              borderRadius: 6,
              padding: '2px 6px',
            }}
          >
            {badge}
          </span>
          {item.is_living && (
            <span style={{ fontSize: 10, color: T.textMuted }}>Living · safe fields only</span>
          )}
        </div>
        {item.subtitle && (
          <p style={{ margin: '0 0 3px', fontSize: 12, color: T.textMuted }}>{item.subtitle}</p>
        )}
        {item.description && (
          <p style={{ margin: 0, fontSize: 12.5, color: T.textMuted, lineHeight: 1.5 }}>
            {item.description.slice(0, 140)}
          </p>
        )}
        <p
          style={{
            margin: '4px 0 0',
            fontSize: 11,
            color: T.textMuted,
            display: 'flex',
            alignItems: 'center',
            gap: 3,
          }}
        >
          <Calendar size={9} /> {formatDate(item.created_at)}
        </p>
      </div>
    </div>
  );
}

// ── Stories Vault tab ─────────────────────────────────────────────────────────

function PublicEmptyState({
  icon,
  title,
  description,
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
}) {
  return (
    <div
      style={{
        textAlign: 'center',
        padding: '38px 24px',
        borderRadius: 16,
        border: `1px solid ${T.cardBorder}`,
        background: T.cardBg,
        boxShadow: T.cardShadow,
      }}
    >
      <span
        style={{
          width: 42,
          height: 42,
          margin: '0 auto 12px',
          borderRadius: 12,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: T.accent,
          background: T.accentLight,
          border: `1px solid ${T.accentBorder}`,
        }}
      >
        {icon}
      </span>
      <p style={{ margin: 0, color: T.text, fontSize: 14, fontWeight: 700 }}>{title}</p>
      <p
        style={{
          maxWidth: 440,
          margin: '7px auto 0',
          color: T.textMuted,
          fontSize: 13,
          lineHeight: 1.6,
        }}
      >
        {description}
      </p>
    </div>
  );
}

function StoriesTab({
  stories,
  featuredStory,
  handle,
  profileId,
  isFollowing,
  visible,
}: {
  stories: PublicStory[];
  featuredStory: PublicStory | null;
  handle: string;
  profileId: string;
  isFollowing: boolean;
  visible: boolean;
}) {
  if (!visible)
    return <p style={{ color: T.textMuted, fontSize: 14 }}>Stories are hidden on this profile.</p>;
  const all: PublicStory[] = [];
  if (featuredStory) all.push(featuredStory);
  stories.forEach((s) => {
    if (!all.some((x) => x.id === s.id)) all.push(s);
  });
  if (all.length === 0)
    return (
      <PublicEmptyState
        icon={<BookOpen size={20} />}
        title="This section is ready for a first story."
        description="Public memories, milestones, and stories chosen by this person will appear here."
      />
    );
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {all.map((s) => (
        <StoryCard
          key={s.id}
          story={s}
          profileId={profileId}
          handle={handle}
          isFollowing={isFollowing}
        />
      ))}
    </div>
  );
}

// ── Records tab ───────────────────────────────────────────────────────────────

function MilestonesTab({ data }: { data: PublicProfilePayload }) {
  const moments = (data.journeyMoments || []).map((m) => ({
    id: m.id,
    title: m.title,
    date: m.date,
    detail: [m.location_name, m.summary].filter(Boolean).join(' - '),
    icon: <MapPin size={16} />,
  }));
  const records = (data.records || []).slice(0, 8).map((r) => ({
    id: r.id,
    title: r.title,
    date: r.achievement_date,
    detail: [r.pillar, r.institution, r.description].filter(Boolean).join(' - '),
    icon: <Award size={16} />,
  }));
  const items = [...moments, ...records].sort(
    (a, b) => new Date(b.date || 0).getTime() - new Date(a.date || 0).getTime()
  );

  if (items.length === 0) {
    return (
      <PublicEmptyState
        icon={<Award size={20} />}
        title="No public milestones have been shared yet."
        description="Journey moments and achievements will form a meaningful timeline here when their owner chooses to publish them."
      />
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {items.map((item) => (
        <div
          key={item.id}
          style={{
            background: T.cardBg,
            border: `1px solid ${T.cardBorder}`,
            borderRadius: 14,
            padding: '14px 16px',
            boxShadow: T.cardShadow,
            display: 'flex',
            gap: 12,
            alignItems: 'flex-start',
          }}
        >
          <div
            style={{
              width: 38,
              height: 38,
              borderRadius: 12,
              background: T.accentLight,
              color: T.accent,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            {item.icon}
          </div>
          <div style={{ minWidth: 0 }}>
            <p style={{ margin: '0 0 4px', color: T.text, fontWeight: 800, fontSize: 14 }}>
              {item.title}
            </p>
            {item.date && (
              <p style={{ margin: '0 0 5px', color: T.textMuted, fontSize: 12 }}>
                {shortDate(item.date)}
              </p>
            )}
            {item.detail && (
              <p style={{ margin: 0, color: T.textMuted, fontSize: 13, lineHeight: 1.55 }}>
                {item.detail.slice(0, 180)}
              </p>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

function PillarsTab({ data }: { data: PublicProfilePayload }) {
  const counts = new Map<string, { stories: number; milestones: number }>();
  (data.stories || []).forEach((s) => {
    if (!s.pillar_tag) return;
    const next = counts.get(s.pillar_tag) || { stories: 0, milestones: 0 };
    counts.set(s.pillar_tag, { ...next, stories: next.stories + 1 });
  });
  (data.records || []).forEach((r) => {
    if (!r.pillar) return;
    const next = counts.get(r.pillar) || { stories: 0, milestones: 0 };
    counts.set(r.pillar, { ...next, milestones: next.milestones + 1 });
  });
  const entries = Array.from(counts.entries());

  if (entries.length === 0) {
    return (
      <p style={{ color: T.textMuted, fontSize: 14 }}>
        Public pillar themes will appear here as stories are shared.
      </p>
    );
  }

  return (
    <div className="grid gap-4 md:grid-cols-2">
      {entries.map(([pillar, count]) => {
        const pt = pillarTokens(pillar);
        return (
          <div
            key={pillar}
            style={{
              background: T.cardBg,
              border: `1px solid ${T.cardBorder}`,
              borderRadius: 14,
              padding: 16,
              boxShadow: T.cardShadow,
            }}
          >
            <div
              style={{
                width: 42,
                height: 42,
                borderRadius: 13,
                background: pt.bg,
                color: pt.text,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: 12,
              }}
            >
              <Award size={18} />
            </div>
            <p style={{ margin: '0 0 6px', color: T.text, fontSize: 15, fontWeight: 800 }}>
              {pillar}
            </p>
            <p style={{ margin: 0, color: T.textMuted, fontSize: 13, lineHeight: 1.55 }}>
              {count.stories} public {count.stories === 1 ? 'story' : 'stories'} and{' '}
              {count.milestones} public {count.milestones === 1 ? 'milestone' : 'milestones'}.
            </p>
          </div>
        );
      })}
    </div>
  );
}

function PulseTab({ posts }: { posts: PublicPulsePost[] }) {
  if (posts.length === 0)
    return <p style={{ color: T.textMuted, fontSize: 14 }}>No public Pulse posts yet.</p>;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {posts.map((p) => (
        <PulseCard key={p.id} post={p} />
      ))}
    </div>
  );
}

// ── Locked section ────────────────────────────────────────────────────────────

// ── Life Cards public tab ─────────────────────────────────────────────────────

function LifeCardsPublicTab({ profileId }: { profileId: string }) {
  const [cards, setCards] = React.useState<any[]>([]);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    fetch(`/api/public/life-cards?profileId=${encodeURIComponent(profileId)}`)
      .then((r) => (r.ok ? r.json() : { cards: [] }))
      .then((d) => setCards(Array.isArray(d.cards) ? d.cards : []))
      .catch(() => setCards([]))
      .finally(() => setLoading(false));
  }, [profileId]);

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 32 }}>
        <Loader2 size={22} style={{ color: T.accent }} className="animate-spin" />
      </div>
    );
  }

  if (cards.length === 0) {
    return (
      <PublicEmptyState
        icon={<BookOpen size={20} />}
        title="Life Cards are ready for a first public memory."
        description="Visual cards for memories, milestones, and achievements will appear here when this person chooses to share them."
      />
    );
  }

  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {cards.map((card: any) => (
        <div
          key={card.id}
          style={{
            background: T.cardBg,
            border: `1px solid ${T.cardBorder}`,
            borderRadius: 14,
            padding: 16,
            boxShadow: T.cardShadow,
          }}
        >
          <p
            style={{
              margin: '0 0 4px',
              fontSize: 11,
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.1em',
              color: T.accent,
            }}
          >
            {card.category || 'Memory'}
          </p>
          <p
            style={{
              margin: '0 0 6px',
              fontSize: 15,
              fontWeight: 800,
              color: T.text,
              lineHeight: 1.3,
            }}
          >
            {card.title}
          </p>
          {card.year && (
            <p style={{ margin: '0 0 8px', fontSize: 12, color: T.textMuted }}>{card.year}</p>
          )}
          {card.summary && (
            <p style={{ margin: 0, fontSize: 13, color: T.textMuted, lineHeight: 1.55 }}>
              {card.summary}
            </p>
          )}
        </div>
      ))}
    </div>
  );
}

// ── About tab ─────────────────────────────────────────────────────────────────

function AboutTab({
  profile,
  stats,
  memberSince,
}: {
  profile: PublicProfilePayload['profile'];
  stats: PublicProfileStats;
  memberSince: string;
}) {
  return (
    <div style={{ maxWidth: 580, display: 'flex', flexDirection: 'column', gap: 20 }}>
      {profile.bio && (
        <div
          style={{
            background: T.cardBg,
            border: `1px solid ${T.cardBorder}`,
            borderRadius: 14,
            padding: 20,
            boxShadow: T.cardShadow,
          }}
        >
          <p
            style={{
              margin: '0 0 6px',
              fontSize: 11,
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.1em',
              color: T.accent,
            }}
          >
            Bio
          </p>
          <p style={{ margin: 0, fontSize: 14, color: T.text, lineHeight: 1.65 }}>{profile.bio}</p>
        </div>
      )}
      <div
        style={{
          background: T.cardBg,
          border: `1px solid ${T.cardBorder}`,
          borderRadius: 14,
          padding: 20,
          boxShadow: T.cardShadow,
        }}
      >
        <p
          style={{
            margin: '0 0 14px',
            fontSize: 11,
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.1em',
            color: T.accent,
          }}
        >
          Details
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {profile.location && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <MapPin size={14} style={{ color: T.textMuted, flexShrink: 0 }} />
              <span style={{ fontSize: 14, color: T.text }}>{profile.location}</span>
            </div>
          )}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Calendar size={14} style={{ color: T.textMuted, flexShrink: 0 }} />
            <span style={{ fontSize: 14, color: T.text }}>
              Member since {formatDate(memberSince)}
            </span>
          </div>
        </div>
      </div>
      <div
        style={{
          background: T.cardBg,
          border: `1px solid ${T.cardBorder}`,
          borderRadius: 14,
          padding: 20,
          boxShadow: T.cardShadow,
        }}
      >
        <p
          style={{
            margin: '0 0 14px',
            fontSize: 11,
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.1em',
            color: T.accent,
          }}
        >
          Public Activity
        </p>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          {(
            [
              { label: 'Followers', value: stats.followers },
              { label: 'Following', value: stats.following },
              { label: 'Stories', value: stats.publicStories },
              { label: 'Verified', value: stats.verifiedRecords },
              { label: 'Records', value: stats.publicRecords },
            ] as const
          ).map((s) => (
            <div
              key={s.label}
              style={{
                textAlign: 'center',
                padding: '10px 8px',
                borderRadius: 10,
                background: 'rgba(27,107,92,0.05)',
                border: `1px solid ${T.cardBorder}`,
              }}
            >
              <p style={{ margin: '0 0 2px', fontSize: 20, fontWeight: 800, color: T.text }}>
                {s.value ?? 0}
              </p>
              <p style={{ margin: 0, fontSize: 11, color: T.textMuted, fontWeight: 600 }}>
                {s.label}
              </p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Photos tab ────────────────────────────────────────────────────────────────

function PhotosTab({ stories, visible }: { stories: PublicStory[]; visible: boolean }) {
  const [lightbox, setLightbox] = React.useState<PublicStory | null>(null);

  if (!visible) {
    return (
      <div style={{ textAlign: 'center', padding: '48px 24px', color: T.textMuted }}>
        <Lock size={30} style={{ margin: '0 auto 12px', opacity: 0.35 }} />
        <p style={{ fontSize: 14, fontWeight: 600, color: T.text }}>Photos are private</p>
        <p style={{ fontSize: 13, marginTop: 4, lineHeight: 1.55 }}>
          This profile has not made photo memories public.
        </p>
      </div>
    );
  }

  if (stories.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '48px 24px', color: T.textMuted }}>
        <ImageIcon size={32} style={{ margin: '0 auto 12px', opacity: 0.35 }} />
        <p style={{ fontSize: 14, fontWeight: 600, color: T.text }}>No public photos yet.</p>
        <p style={{ fontSize: 13, marginTop: 4, lineHeight: 1.55 }}>
          This profile has not published photo memories.
        </p>
      </div>
    );
  }

  return (
    <>
      <div className="grid gap-3 grid-cols-2 sm:grid-cols-3 lg:grid-cols-4">
        {stories.map((s) => {
          const pt = pillarTokens(s.pillar_tag);
          return (
            <button
              key={s.id}
              type="button"
              onClick={() => setLightbox(s)}
              style={{
                background: 'none',
                border: 'none',
                padding: 0,
                cursor: 'pointer',
                textAlign: 'left',
                borderRadius: 14,
                overflow: 'hidden',
                boxShadow: T.cardShadow,
              }}
            >
              <div
                style={{
                  position: 'relative',
                  paddingTop: '75%',
                  background: pt.bg,
                  borderRadius: 14,
                  overflow: 'hidden',
                }}
              >
                <img
                  src={s.media_url!}
                  alt={s.title || ''}
                  style={{
                    position: 'absolute',
                    inset: 0,
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    display: 'block',
                  }}
                />
                {s.pillar_tag && (
                  <span
                    style={{
                      position: 'absolute',
                      top: 8,
                      left: 8,
                      fontSize: 10,
                      fontWeight: 700,
                      padding: '2px 7px',
                      borderRadius: 6,
                      background: pt.bg,
                      color: pt.text,
                    }}
                  >
                    {s.pillar_tag}
                  </span>
                )}
              </div>
              {(s.title || s.milestone_date) && (
                <div style={{ padding: '8px 2px 4px', background: T.cardBg }}>
                  {s.title && (
                    <p
                      style={{
                        margin: 0,
                        fontSize: 12,
                        fontWeight: 700,
                        color: T.text,
                        lineHeight: 1.3,
                      }}
                    >
                      {s.title}
                    </p>
                  )}
                  {s.milestone_date && (
                    <p style={{ margin: '2px 0 0', fontSize: 11, color: T.textMuted }}>
                      {shortDate(s.milestone_date)}
                    </p>
                  )}
                </div>
              )}
            </button>
          );
        })}
      </div>

      {lightbox && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 9000,
            background: 'rgba(0,0,0,0.85)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 16,
          }}
          onClick={() => setLightbox(null)}
        >
          <div
            style={{
              position: 'relative',
              maxWidth: 900,
              width: '100%',
              borderRadius: 18,
              overflow: 'hidden',
              background: '#0d1117',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={lightbox.media_url!}
              alt={lightbox.title || ''}
              style={{
                width: '100%',
                maxHeight: '70vh',
                objectFit: 'contain',
                display: 'block',
                background: '#0d1117',
              }}
            />
            <div style={{ padding: '14px 18px', borderTop: '1px solid rgba(255,255,255,0.07)' }}>
              {lightbox.title && (
                <p style={{ margin: '0 0 4px', fontSize: 14, fontWeight: 700, color: '#f1f5f9' }}>
                  {lightbox.title}
                </p>
              )}
              <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                {lightbox.pillar_tag && (
                  <span style={{ fontSize: 11, fontWeight: 700, color: T.accent }}>
                    {lightbox.pillar_tag}
                  </span>
                )}
                {lightbox.milestone_date && (
                  <span style={{ fontSize: 11, color: '#6b8eaa' }}>
                    {shortDate(lightbox.milestone_date)}
                  </span>
                )}
              </div>
              {lightbox.body && (
                <p style={{ margin: '8px 0 0', fontSize: 13, color: '#a8bfd4', lineHeight: 1.6 }}>
                  {lightbox.body.slice(0, 300)}
                </p>
              )}
            </div>
            <button
              type="button"
              onClick={() => setLightbox(null)}
              style={{
                position: 'absolute',
                top: 10,
                right: 10,
                width: 32,
                height: 32,
                borderRadius: 8,
                background: 'rgba(0,0,0,0.55)',
                border: 'none',
                color: '#fff',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <XIcon size={16} />
            </button>
          </div>
        </div>
      )}
    </>
  );
}

// ── Videos tab ────────────────────────────────────────────────────────────────

function VideosTab({ stories, visible }: { stories: PublicStory[]; visible: boolean }) {
  const [player, setPlayer] = React.useState<PublicStory | null>(null);

  if (!visible) {
    return (
      <div style={{ textAlign: 'center', padding: '48px 24px', color: T.textMuted }}>
        <Lock size={30} style={{ margin: '0 auto 12px', opacity: 0.35 }} />
        <p style={{ fontSize: 14, fontWeight: 600, color: T.text }}>Videos are private</p>
        <p style={{ fontSize: 13, marginTop: 4, lineHeight: 1.55 }}>
          This profile has not made video memories public.
        </p>
      </div>
    );
  }

  if (stories.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '48px 24px', color: T.textMuted }}>
        <Video size={32} style={{ margin: '0 auto 12px', opacity: 0.35 }} />
        <p style={{ fontSize: 14, fontWeight: 600, color: T.text }}>No public videos yet.</p>
        <p style={{ fontSize: 13, marginTop: 4, lineHeight: 1.55 }}>
          Published video memories will appear here.
        </p>
      </div>
    );
  }

  return (
    <>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {stories.map((s) => {
          const pt = pillarTokens(s.pillar_tag);
          return (
            <button
              key={s.id}
              type="button"
              onClick={() => setPlayer(s)}
              style={{
                background: 'none',
                border: 'none',
                padding: 0,
                cursor: 'pointer',
                textAlign: 'left',
                borderRadius: 14,
                overflow: 'hidden',
                boxShadow: T.cardShadow,
              }}
            >
              <div
                style={{
                  position: 'relative',
                  paddingTop: '56.25%',
                  background: pt.bg,
                  borderRadius: 14,
                  overflow: 'hidden',
                }}
              >
                {s.media_url ? (
                  <video
                    src={s.media_url}
                    style={{
                      position: 'absolute',
                      inset: 0,
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                    }}
                    muted
                    preload="metadata"
                  />
                ) : (
                  <div
                    style={{
                      position: 'absolute',
                      inset: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Video size={32} style={{ opacity: 0.3, color: pt.icon }} />
                  </div>
                )}
                <div
                  style={{
                    position: 'absolute',
                    inset: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <div
                    style={{
                      width: 44,
                      height: 44,
                      borderRadius: '50%',
                      background: 'rgba(0,0,0,0.58)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backdropFilter: 'blur(4px)',
                    }}
                  >
                    <Play size={18} fill="#fff" color="#fff" style={{ marginLeft: 2 }} />
                  </div>
                </div>
                {s.pillar_tag && (
                  <span
                    style={{
                      position: 'absolute',
                      bottom: 8,
                      left: 8,
                      fontSize: 10,
                      fontWeight: 700,
                      padding: '2px 7px',
                      borderRadius: 6,
                      background: pt.bg,
                      color: pt.text,
                    }}
                  >
                    {s.pillar_tag}
                  </span>
                )}
              </div>
              <div style={{ padding: '10px 2px 4px', background: T.cardBg }}>
                {s.title && (
                  <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: T.text }}>
                    {s.title}
                  </p>
                )}
                {s.milestone_date && (
                  <p style={{ margin: '2px 0 0', fontSize: 11, color: T.textMuted }}>
                    {shortDate(s.milestone_date)}
                  </p>
                )}
              </div>
            </button>
          );
        })}
      </div>

      {player && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 9000,
            background: 'rgba(0,0,0,0.88)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 16,
          }}
          onClick={() => setPlayer(null)}
        >
          <div
            style={{
              position: 'relative',
              maxWidth: 900,
              width: '100%',
              borderRadius: 18,
              overflow: 'hidden',
              background: '#000',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {player.media_url ? (
              <video
                src={player.media_url}
                controls
                autoPlay
                style={{ width: '100%', maxHeight: '75vh', display: 'block', background: '#000' }}
              />
            ) : (
              <div style={{ padding: 48, textAlign: 'center', color: '#6b8eaa' }}>
                <Video size={40} style={{ opacity: 0.4, margin: '0 auto 12px' }} />
                <p style={{ fontSize: 14 }}>Video playback not available.</p>
              </div>
            )}
            <div style={{ padding: '12px 18px', borderTop: '1px solid rgba(255,255,255,0.07)' }}>
              {player.title && (
                <p style={{ margin: '0 0 4px', fontSize: 14, fontWeight: 700, color: '#f1f5f9' }}>
                  {player.title}
                </p>
              )}
              <div style={{ display: 'flex', gap: 10 }}>
                {player.pillar_tag && (
                  <span style={{ fontSize: 11, fontWeight: 700, color: T.accent }}>
                    {player.pillar_tag}
                  </span>
                )}
                {player.milestone_date && (
                  <span style={{ fontSize: 11, color: '#6b8eaa' }}>
                    {shortDate(player.milestone_date)}
                  </span>
                )}
              </div>
            </div>
            <button
              type="button"
              onClick={() => setPlayer(null)}
              style={{
                position: 'absolute',
                top: 10,
                right: 10,
                width: 32,
                height: 32,
                borderRadius: 8,
                background: 'rgba(0,0,0,0.55)',
                border: 'none',
                color: '#fff',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <XIcon size={16} />
            </button>
          </div>
        </div>
      )}
    </>
  );
}

// ── Oral Stories tab ──────────────────────────────────────────────────────────

function OralStoriesTab({ stories, visible }: { stories: PublicStory[]; visible: boolean }) {
  const [playing, setPlaying] = React.useState<string | null>(null);
  const audioRef = React.useRef<HTMLAudioElement | null>(null);

  const togglePlay = (id: string, url: string) => {
    if (playing === id) {
      audioRef.current?.pause();
      setPlaying(null);
    } else {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.src = url;
        audioRef.current.play().catch(() => {});
      }
      setPlaying(id);
    }
  };

  if (!visible) {
    return (
      <div style={{ textAlign: 'center', padding: '48px 24px', color: T.textMuted }}>
        <Lock size={30} style={{ margin: '0 auto 12px', opacity: 0.35 }} />
        <p style={{ fontSize: 14, fontWeight: 600, color: T.text }}>Oral stories are private</p>
        <p style={{ fontSize: 13, marginTop: 4, lineHeight: 1.55 }}>
          This profile has not made audio memories public.
        </p>
      </div>
    );
  }

  if (stories.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '48px 24px', color: T.textMuted }}>
        <Mic size={32} style={{ margin: '0 auto 12px', opacity: 0.35 }} />
        <p style={{ fontSize: 14, fontWeight: 600, color: T.text }}>No public oral stories yet.</p>
        <p style={{ fontSize: 13, marginTop: 4, lineHeight: 1.55 }}>
          Voice memories and spoken reflections will appear here when published.
        </p>
      </div>
    );
  }

  return (
    <>
      <audio ref={audioRef} onEnded={() => setPlaying(null)} style={{ display: 'none' }} />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {stories.map((s) => {
          const pt = pillarTokens(s.pillar_tag);
          const isPlaying = playing === s.id;
          const hasAudio = !!s.media_url;
          return (
            <div
              key={s.id}
              style={{
                background: T.cardBg,
                border: `1px solid ${T.cardBorder}`,
                borderRadius: 14,
                padding: '14px 16px',
                boxShadow: T.cardShadow,
                display: 'flex',
                gap: 14,
                alignItems: 'center',
              }}
            >
              <button
                type="button"
                onClick={() => hasAudio && s.media_url && togglePlay(s.id, s.media_url)}
                disabled={!hasAudio}
                style={{
                  width: 44,
                  height: 44,
                  borderRadius: '50%',
                  flexShrink: 0,
                  border: 'none',
                  background: hasAudio
                    ? isPlaying
                      ? T.accent
                      : T.accentLight
                    : 'rgba(0,0,0,0.04)',
                  color: hasAudio ? (isPlaying ? '#fff' : T.accent) : T.textMuted,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: hasAudio ? 'pointer' : 'not-allowed',
                  transition: 'all 0.15s',
                }}
              >
                {isPlaying ? (
                  <span
                    style={{
                      width: 14,
                      height: 14,
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr',
                      gap: 2,
                    }}
                  >
                    <span style={{ background: 'currentColor', borderRadius: 1 }} />
                    <span style={{ background: 'currentColor', borderRadius: 1 }} />
                  </span>
                ) : (
                  <Play size={16} fill="currentColor" style={{ marginLeft: 2 }} />
                )}
              </button>

              <div style={{ flex: 1, minWidth: 0 }}>
                {s.pillar_tag && (
                  <span
                    style={{
                      fontSize: 10,
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      letterSpacing: '0.08em',
                      color: pt.text,
                      background: pt.bg,
                      padding: '2px 6px',
                      borderRadius: 5,
                      display: 'inline-block',
                      marginBottom: 5,
                    }}
                  >
                    {s.pillar_tag}
                  </span>
                )}
                <p style={{ margin: '0 0 3px', fontSize: 14, fontWeight: 700, color: T.text }}>
                  {s.title || 'Oral memory'}
                </p>
                {s.milestone_date && (
                  <p style={{ margin: '0 0 6px', fontSize: 12, color: T.textMuted }}>
                    {shortDate(s.milestone_date)}
                  </p>
                )}

                <div style={{ display: 'flex', gap: 3, alignItems: 'flex-end', height: 24 }}>
                  {Array.from({ length: 28 }).map((_, i) => (
                    <div
                      key={i}
                      style={{
                        width: 2,
                        borderRadius: 2,
                        background: isPlaying ? T.accent : T.cardBorder,
                        height: `${25 + Math.sin(i * 1.3) * 55 + Math.cos(i * 0.7) * 20}%`,
                        opacity: hasAudio ? (isPlaying ? 1 : 0.6) : 0.25,
                        transition: 'background 0.2s',
                      }}
                    />
                  ))}
                </div>

                {!hasAudio && (
                  <p
                    style={{
                      margin: '6px 0 0',
                      fontSize: 11,
                      color: T.textMuted,
                      fontStyle: 'italic',
                    }}
                  >
                    Audio playback not available for this memory.
                  </p>
                )}
                {s.body && (
                  <p
                    style={{
                      margin: '8px 0 0',
                      fontSize: 13,
                      color: T.textMuted,
                      lineHeight: 1.55,
                    }}
                  >
                    {s.body.slice(0, 160)}
                    {s.body.length > 160 ? '...' : ''}
                  </p>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
}

// ── Verified tab ──────────────────────────────────────────────────────────────

function VerifiedTab({
  proofCards,
  visible,
  publicVaultHref,
}: {
  proofCards: PublicProofCard[];
  visible: boolean;
  publicVaultHref: string;
}) {
  if (!visible) {
    return (
      <div style={{ textAlign: 'center', padding: '48px 24px', color: T.textMuted }}>
        <Lock size={30} style={{ margin: '0 auto 12px', opacity: 0.35 }} />
        <p style={{ fontSize: 14, fontWeight: 600, color: T.text }}>Verified records are private</p>
        <p style={{ fontSize: 13, marginTop: 4, lineHeight: 1.55 }}>
          This profile has not made their verified credentials public.
        </p>
      </div>
    );
  }

  if (proofCards.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '48px 24px', color: T.textMuted }}>
        <ShieldCheck size={32} style={{ margin: '0 auto 12px', opacity: 0.35 }} />
        <p style={{ fontSize: 14, fontWeight: 600, color: T.text }}>
          No public verified records yet.
        </p>
        <p style={{ fontSize: 13, marginTop: 4, lineHeight: 1.55 }}>
          Institutional verifications and proof credentials will appear here.
        </p>
        <Link
          href={publicVaultHref}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 5,
            marginTop: 16,
            padding: '8px 16px',
            borderRadius: 10,
            background: T.accentLight,
            color: T.accent,
            textDecoration: 'none',
            fontSize: 13,
            fontWeight: 700,
          }}
        >
          <Shield size={13} /> View Public Vault
        </Link>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" style={{ marginBottom: 20 }}>
        {proofCards.map((card) => (
          <div
            key={card.id}
            style={{
              background: T.cardBg,
              border: `1px solid ${T.cardBorder}`,
              borderRadius: 14,
              padding: 16,
              boxShadow: T.cardShadow,
              display: 'flex',
              flexDirection: 'column',
              gap: 10,
            }}
          >
            <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
              {card.thumbnail_url ? (
                <img
                  src={card.thumbnail_url}
                  alt=""
                  style={{
                    width: 44,
                    height: 44,
                    borderRadius: 10,
                    objectFit: 'cover',
                    flexShrink: 0,
                    border: `1px solid ${T.cardBorder}`,
                  }}
                />
              ) : (
                <div
                  style={{
                    width: 44,
                    height: 44,
                    borderRadius: 10,
                    background: T.accentLight,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    color: T.accent,
                  }}
                >
                  <ShieldCheck size={20} />
                </div>
              )}
              <div style={{ flex: 1, minWidth: 0 }}>
                <p
                  style={{
                    margin: '0 0 2px',
                    fontSize: 14,
                    fontWeight: 800,
                    color: T.text,
                    lineHeight: 1.3,
                  }}
                >
                  {card.title}
                </p>
                <p style={{ margin: 0, fontSize: 12, color: T.textMuted }}>{card.issuer_name}</p>
              </div>
            </div>

            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              <span
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  padding: '3px 8px',
                  borderRadius: 6,
                  background: 'rgba(34,197,94,0.1)',
                  color: '#16a34a',
                  border: '1px solid rgba(34,197,94,0.2)',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 3,
                  textTransform: 'uppercase',
                  letterSpacing: '0.06em',
                }}
              >
                <Check size={9} strokeWidth={3} /> Verified
              </span>
              {card.trust_level && (
                <span
                  style={{
                    fontSize: 10,
                    fontWeight: 600,
                    padding: '3px 8px',
                    borderRadius: 6,
                    background: T.accentLight,
                    color: T.accent,
                  }}
                >
                  {card.trust_level}
                </span>
              )}
            </div>

            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                paddingTop: 8,
                borderTop: `1px solid ${T.cardBorder}`,
              }}
            >
              {card.issue_date && (
                <span
                  style={{
                    fontSize: 11,
                    color: T.textMuted,
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 4,
                  }}
                >
                  <Calendar size={11} /> {shortDate(card.issue_date)}
                </span>
              )}
              {card.verification_ref && (
                <Link
                  href={`/credential/${card.verification_ref}`}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 4,
                    fontSize: 11,
                    fontWeight: 700,
                    color: T.accent,
                    textDecoration: 'none',
                  }}
                >
                  View <ExternalLink size={11} />
                </Link>
              )}
            </div>
          </div>
        ))}
      </div>

      <div style={{ textAlign: 'center' }}>
        <Link
          href={publicVaultHref}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            padding: '9px 16px',
            borderRadius: 10,
            border: `1px solid ${T.accentBorder}`,
            color: T.accent,
            textDecoration: 'none',
            fontSize: 13,
            fontWeight: 700,
          }}
        >
          <Shield size={14} /> View full Public Vault
        </Link>
      </div>
    </div>
  );
}

// ── Locked module card (plan gate, not visibility gate) ───────────────────────

function meetsRequiredPlan(tier: ProfilePlanTier, required: 'verified' | 'legacy'): boolean {
  if (required === 'verified') return tier === 'verified' || tier === 'legacy';
  return tier === 'legacy';
}

function LockedModuleCard({
  icon,
  title,
  description,
  requiredPlan,
  isOwner,
  ownerPlanTier,
  variant = 'default',
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
  requiredPlan: 'verified' | 'legacy';
  isOwner: boolean;
  ownerPlanTier?: ProfilePlanTier;
  variant?: 'journey' | 'heritage' | 'default';
}) {
  // Plan-gated messaging on a public profile is management UI for its owner, never marketing to visitors.
  if (!isOwner) return null;

  const alreadyHasPlan = !!ownerPlanTier && meetsRequiredPlan(ownerPlanTier, requiredPlan);
  const planLabel = requiredPlan === 'legacy' ? 'Legacy' : 'Verified';
  const planColor = requiredPlan === 'legacy' ? '#92400e' : '#0f766e';
  const planBg = requiredPlan === 'legacy' ? 'rgba(252,211,77,0.12)' : 'rgba(13,148,136,0.08)';
  const planBorder = requiredPlan === 'legacy' ? 'rgba(252,211,77,0.35)' : 'rgba(13,148,136,0.22)';

  const ctaHref = alreadyHasPlan
    ? '/individual-dashboard/public-identity'
    : '/individual-dashboard/billing';
  const ctaLabel = alreadyHasPlan ? 'Enable in Settings' : `Upgrade to ${planLabel}`;

  // ── Journey Map artifact ──────────────────────────────────────────────────────
  if (variant === 'journey') {
    return (
      <div
        style={{
          borderRadius: 14,
          overflow: 'hidden',
          border: '1px solid rgba(20,184,166,0.22)',
          boxShadow: T.cardShadow,
          background: T.cardBg,
        }}
      >
        <div
          style={{
            position: 'relative',
            height: 150,
            background: 'linear-gradient(145deg,#ecfeff,#f5f3ff 55%,#fff7ed)',
            overflow: 'hidden',
          }}
        >
          <svg
            viewBox="0 0 400 150"
            preserveAspectRatio="xMidYMid slice"
            style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}
            aria-hidden
          >
            <defs>
              <pattern id="jLockGrid" width="20" height="20" patternUnits="userSpaceOnUse">
                <path d="M 20 0 L 0 0 0 20" fill="none" stroke="#14b8a6" strokeWidth="0.4" />
              </pattern>
            </defs>
            <rect width="400" height="150" fill="url(#jLockGrid)" opacity="0.07" />
            <line
              x1="133"
              y1="0"
              x2="133"
              y2="150"
              stroke="#22d3ee"
              strokeWidth="0.5"
              strokeDasharray="3,5"
              opacity="0.28"
            />
            <line
              x1="266"
              y1="0"
              x2="266"
              y2="150"
              stroke="#22d3ee"
              strokeWidth="0.5"
              strokeDasharray="3,5"
              opacity="0.28"
            />
            <line
              x1="0"
              y1="75"
              x2="400"
              y2="75"
              stroke="#22d3ee"
              strokeWidth="0.5"
              strokeDasharray="3,5"
              opacity="0.28"
            />
            <polyline
              points="30,118 80,70 140,95 200,38 270,72 360,28"
              fill="none"
              stroke="#14b8a6"
              strokeWidth="1.5"
              opacity="0.45"
            />
            {(
              [
                [30, 118],
                [80, 70],
                [140, 95],
                [200, 38],
                [270, 72],
                [360, 28],
              ] as [number, number][]
            ).map(([x, y], i) => (
              <g key={i}>
                <circle cx={x} cy={y} r="5" fill="#14b8a6" opacity="0.16" />
                <circle cx={x} cy={y} r="2.5" fill="#14b8a6" opacity="0.7" />
              </g>
            ))}
          </svg>
          <div
            style={{
              position: 'absolute',
              top: 10,
              right: 12,
              fontFamily: 'ui-monospace, monospace',
              fontSize: 9,
              color: '#0e7490',
              opacity: 0.65,
              letterSpacing: '0.1em',
            }}
          >
            38.9N · 77.0W
          </div>
          <svg
            viewBox="0 0 32 32"
            style={{ position: 'absolute', top: 8, left: 12, width: 28, height: 28, opacity: 0.22 }}
            aria-hidden
          >
            <circle cx="16" cy="16" r="14" fill="none" stroke="#0e7490" strokeWidth="0.8" />
            <polygon points="16,3 17.8,13.5 16,11.5 14.2,13.5" fill="#0e7490" />
            <polygon points="16,29 17.8,18.5 16,20.5 14.2,18.5" fill="#64748b" />
            <polygon points="3,16 13.5,14.2 11.5,16 13.5,17.8" fill="#64748b" />
            <polygon points="29,16 18.5,14.2 20.5,16 18.5,17.8" fill="#64748b" />
          </svg>
          <div
            style={{
              position: 'absolute',
              inset: 0,
              background:
                'radial-gradient(circle at 50% 50%, rgba(255,255,255,0.08), rgba(255,255,255,0.62))',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <div
              style={{
                width: 52,
                height: 52,
                borderRadius: '50%',
                border: '1.5px solid rgba(20,184,166,0.32)',
                background: 'rgba(255,255,255,0.92)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 0 24px rgba(20,184,166,0.16)',
              }}
            >
              <Lock size={18} color={T.accent} />
            </div>
          </div>
        </div>
        <div style={{ padding: '14px 16px 16px', background: 'rgba(255,255,255,0.86)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
            <span
              style={{
                fontSize: 9,
                fontFamily: 'ui-monospace, monospace',
                color: '#0e7490',
                letterSpacing: '0.14em',
                opacity: 0.75,
                textTransform: 'uppercase',
              }}
            >
              ERV / JOURNEY MAP
            </span>
            <span
              style={{
                marginLeft: 'auto',
                fontSize: 9,
                fontWeight: 800,
                color: planColor,
                background: planBg,
                border: `1px solid ${planBorder}`,
                borderRadius: 999,
                padding: '2px 8px',
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
                flexShrink: 0,
              }}
            >
              {planLabel}
            </span>
          </div>
          <p
            style={{
              margin: '0 0 6px',
              fontWeight: 700,
              fontSize: 15,
              color: T.text,
              fontFamily: 'DM Serif Display, serif',
            }}
          >
            {title}
          </p>
          <p style={{ margin: '0 0 14px', fontSize: 12, color: T.textMuted, lineHeight: 1.55 }}>
            {description}
          </p>
          <Link
            href={ctaHref}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 5,
              padding: '8px 14px',
              borderRadius: 9,
              background: T.accentLight,
              border: `1px solid ${T.accentBorder}`,
              color: T.accent,
              fontWeight: 700,
              fontSize: 12,
              textDecoration: 'none',
            }}
          >
            {ctaLabel} <ChevronRight size={12} />
          </Link>
        </div>
      </div>
    );
  }

  // ── Family Heritage artifact ──────────────────────────────────────────────────
  if (variant === 'heritage') {
    return (
      <div
        style={{
          borderRadius: 14,
          overflow: 'hidden',
          border: '1px solid rgba(52,211,153,0.2)',
          boxShadow: T.cardShadow,
          background: T.cardBg,
        }}
      >
        <div
          style={{
            position: 'relative',
            height: 150,
            background: 'linear-gradient(145deg,#fff7ed,#f0fdf4 58%,#eef6ff)',
            overflow: 'hidden',
          }}
        >
          <svg
            viewBox="0 0 400 150"
            preserveAspectRatio="xMidYMid slice"
            style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}
            aria-hidden
          >
            {([90, 62, 40, 22] as number[]).map((r, i) => (
              <circle
                key={i}
                cx="200"
                cy="150"
                r={r * 2}
                fill="none"
                stroke="#34d399"
                strokeWidth="0.5"
                opacity={0.03 + i * 0.013}
              />
            ))}
            <line
              x1="200"
              y1="128"
              x2="120"
              y2="78"
              stroke="#34d399"
              strokeWidth="1"
              opacity="0.38"
            />
            <line
              x1="200"
              y1="128"
              x2="280"
              y2="78"
              stroke="#34d399"
              strokeWidth="1"
              opacity="0.38"
            />
            <line
              x1="120"
              y1="78"
              x2="75"
              y2="32"
              stroke="#34d399"
              strokeWidth="0.8"
              opacity="0.3"
            />
            <line
              x1="120"
              y1="78"
              x2="162"
              y2="32"
              stroke="#34d399"
              strokeWidth="0.8"
              opacity="0.3"
            />
            <line
              x1="280"
              y1="78"
              x2="238"
              y2="32"
              stroke="#34d399"
              strokeWidth="0.8"
              opacity="0.3"
            />
            <line
              x1="280"
              y1="78"
              x2="325"
              y2="32"
              stroke="#34d399"
              strokeWidth="0.8"
              opacity="0.3"
            />
            <circle
              cx="200"
              cy="128"
              r="6"
              fill="#ffffff"
              stroke="#34d399"
              strokeWidth="1.3"
              opacity="0.85"
            />
            <circle
              cx="120"
              cy="78"
              r="5"
              fill="#ffffff"
              stroke="#34d399"
              strokeWidth="1.1"
              opacity="0.75"
            />
            <circle
              cx="280"
              cy="78"
              r="5"
              fill="#ffffff"
              stroke="#34d399"
              strokeWidth="1.1"
              opacity="0.75"
            />
            <circle
              cx="75"
              cy="32"
              r="4"
              fill="#ffffff"
              stroke="#34d399"
              strokeWidth="0.9"
              opacity="0.7"
            />
            <circle
              cx="162"
              cy="32"
              r="4"
              fill="#ffffff"
              stroke="#34d399"
              strokeWidth="0.9"
              opacity="0.7"
            />
            <circle
              cx="238"
              cy="32"
              r="4"
              fill="#ffffff"
              stroke="#34d399"
              strokeWidth="0.9"
              opacity="0.7"
            />
            <circle
              cx="325"
              cy="32"
              r="4"
              fill="#ffffff"
              stroke="#34d399"
              strokeWidth="0.9"
              opacity="0.7"
            />
          </svg>
          <div
            style={{
              position: 'absolute',
              top: 10,
              right: 12,
              fontFamily: 'ui-monospace, monospace',
              fontSize: 9,
              color: T.accent,
              opacity: 0.65,
              letterSpacing: '0.1em',
            }}
          >
            GEN. III — SEALED
          </div>
          <svg
            viewBox="0 0 28 28"
            style={{ position: 'absolute', top: 8, left: 12, width: 26, height: 26, opacity: 0.28 }}
            aria-hidden
          >
            <polygon
              points="14,1 25,7.5 25,20.5 14,27 3,20.5 3,7.5"
              fill="none"
              stroke="#34d399"
              strokeWidth="1"
            />
            <text
              x="14"
              y="17"
              textAnchor="middle"
              fontSize="6.5"
              fill="#34d399"
              fontFamily="ui-monospace, monospace"
              fontWeight="bold"
            >
              ERV
            </text>
          </svg>
          <div
            style={{
              position: 'absolute',
              inset: 0,
              background:
                'radial-gradient(circle at 50% 55%, rgba(255,255,255,0.05), rgba(255,255,255,0.62))',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <div
              style={{
                width: 52,
                height: 52,
                borderRadius: '50%',
                border: '1.5px solid rgba(52,211,153,0.34)',
                background: 'rgba(255,255,255,0.92)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 0 22px rgba(52,211,153,0.14)',
              }}
            >
              <Lock size={18} color={T.accent} />
            </div>
          </div>
        </div>
        <div style={{ padding: '14px 16px 16px', background: 'rgba(255,255,255,0.86)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
            <span
              style={{
                fontSize: 9,
                fontFamily: 'ui-monospace, monospace',
                color: T.accent,
                letterSpacing: '0.14em',
                opacity: 0.75,
                textTransform: 'uppercase',
              }}
            >
              ERV / FAMILY HERITAGE
            </span>
            <span
              style={{
                marginLeft: 'auto',
                fontSize: 9,
                fontWeight: 800,
                color: '#92400e',
                background: 'rgba(252,211,77,0.12)',
                border: '1px solid rgba(252,211,77,0.35)',
                borderRadius: 999,
                padding: '2px 8px',
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
                flexShrink: 0,
              }}
            >
              Legacy
            </span>
          </div>
          <p
            style={{
              margin: '0 0 6px',
              fontWeight: 700,
              fontSize: 15,
              color: T.text,
              fontFamily: 'DM Serif Display, serif',
            }}
          >
            {title}
          </p>
          <p style={{ margin: '0 0 14px', fontSize: 12, color: T.textMuted, lineHeight: 1.55 }}>
            {description}
          </p>
          <Link
            href={ctaHref}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 5,
              padding: '8px 14px',
              borderRadius: 9,
              background: T.accentLight,
              border: `1px solid ${T.accentBorder}`,
              color: T.accent,
              fontWeight: 700,
              fontSize: 12,
              textDecoration: 'none',
            }}
          >
            {ctaLabel} <ChevronRight size={12} />
          </Link>
        </div>
      </div>
    );
  }

  // ── Default locked card ───────────────────────────────────────────────────────
  return (
    <div
      style={{
        borderRadius: 14,
        border: `1px solid ${planBorder}`,
        background: `linear-gradient(135deg, ${planBg}, rgba(255,255,255,0.6))`,
        padding: '24px 20px',
        textAlign: 'center',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <div
        aria-hidden
        style={{
          position: 'absolute',
          top: -40,
          right: -40,
          width: 120,
          height: 120,
          borderRadius: '50%',
          background: planBg,
          opacity: 0.6,
        }}
      />
      <div
        style={{
          width: 52,
          height: 52,
          borderRadius: 14,
          background: planBg,
          border: `1px solid ${planBorder}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto 14px',
          color: planColor,
          position: 'relative',
        }}
      >
        {icon}
        <div
          style={{
            position: 'absolute',
            bottom: -4,
            right: -4,
            width: 18,
            height: 18,
            borderRadius: 9,
            background: '#fff',
            border: `1px solid ${planBorder}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Lock size={9} color={planColor} />
        </div>
      </div>
      <div
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 4,
          padding: '3px 9px',
          borderRadius: 999,
          background: planBg,
          border: `1px solid ${planBorder}`,
          fontSize: 9.5,
          fontWeight: 800,
          color: planColor,
          letterSpacing: '0.07em',
          textTransform: 'uppercase',
          marginBottom: 10,
        }}
      >
        {requiredPlan === 'legacy' ? <Star size={8} /> : <Zap size={8} />} {planLabel} feature
      </div>
      <p
        style={{
          margin: '0 0 6px',
          fontWeight: 700,
          fontSize: 15,
          color: T.text,
          fontFamily: 'DM Serif Display, serif',
        }}
      >
        {title}
      </p>
      <p
        style={{
          margin: '0 0 18px',
          fontSize: 12.5,
          color: T.textMuted,
          lineHeight: 1.6,
          maxWidth: 300,
          marginLeft: 'auto',
          marginRight: 'auto',
        }}
      >
        {description}
      </p>
      <Link
        href={ctaHref}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 5,
          padding: '9px 18px',
          borderRadius: 10,
          background: isOwner && alreadyHasPlan ? '#4a5d72' : planColor,
          color: '#fff',
          fontWeight: 700,
          fontSize: 12.5,
          textDecoration: 'none',
          boxShadow: `0 2px 10px ${planBg}`,
        }}
      >
        {ctaLabel}
        <ChevronRight size={13} />
      </Link>
    </div>
  );
}

function LockedSection({
  icon,
  title,
  body,
  ctaLabel,
  ctaHref,
}: {
  icon: React.ReactNode;
  title: string;
  body: string;
  ctaLabel: string;
  ctaHref: string;
}) {
  return (
    <div
      style={{
        padding: '36px 24px',
        borderRadius: 14,
        border: `1px dashed ${T.cardBorder}`,
        background: '#f9fafb',
        textAlign: 'center',
      }}
    >
      <div
        style={{
          width: 54,
          height: 54,
          borderRadius: 14,
          background: T.accentLight,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto 14px',
          color: T.accent,
        }}
      >
        {icon}
      </div>
      <p
        style={{
          margin: '0 0 8px',
          fontWeight: 700,
          fontSize: 15.5,
          color: T.text,
          fontFamily: 'DM Serif Display, serif',
        }}
      >
        {title}
      </p>
      <p
        style={{
          margin: '0 0 22px',
          fontSize: 13,
          color: T.textMuted,
          lineHeight: 1.6,
          maxWidth: 320,
          marginLeft: 'auto',
          marginRight: 'auto',
        }}
      >
        {body}
      </p>
      <Link
        href={ctaHref}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 5,
          padding: '10px 20px',
          borderRadius: 10,
          background: T.accent,
          color: '#fff',
          fontWeight: 700,
          fontSize: 13,
          textDecoration: 'none',
          boxShadow: '0 2px 10px rgba(27,107,92,0.25)',
        }}
      >
        {ctaLabel}
        <ChevronRight size={14} />
      </Link>
    </div>
  );
}
